import { useConvex } from "convex/react";
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import * as THREE from "three";
import { Camera, Trash2 } from "lucide-react";
import ComponentRenderer, { getIsSkinComponentResizing } from "@/components/skin/ComponentRenderer";
import { Button } from "@/components/ui/button";
import { BacklinkPlacementModal } from "@/components/editor/BacklinkPlacementModal";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useCSSVariableWatcher } from "@/hooks/useCSSVariableWatcher";
import { useIndexedDBCache } from "@/hooks/useIndexedDBCache";
import { type Hotspot, type Panorama, useEditorStore } from "@/store/editorStore";
import { type Connection, repositionHotspotsForNorthChange } from "@/utils/autoHotspotGenerator";
import {
  applyDisplayCoordsToCamera,
  cameraToDisplayCoords,
  focusCameraOnPosition,
  normalizeAngle,
  positionToYawPitch,
  shortestRotation,
} from "@/utils/coordinateSystem";
import { api } from "../../../convex/_generated/api";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { StorageService } from "@/services/storageService";
import { promptAndSaveFile } from "@/utils/fileDownload";
import { createHotspot as createLocalHotspot, updateHotspot as updateLocalHotspot, removeHotspot as removeLocalHotspot } from "@/data/adapters/local/hotspots";
import { LOCAL_USER_ID } from "@/data/adapters/local/users";
import { updatePanorama as updateLocalPanorama } from "@/data/adapters/local/panoramas";
import { HotspotManager } from "./threejs/HotspotManager";
import { HotspotContextualPopover } from "./HotspotContextualPopover";

/**
 * Parse image pixel dimensions from the first bytes of a blob — no full decode needed.
 * Supports JPEG (SOF0/1/2), PNG, and lossy WebP.
 */
function parseDimensionsFromHeader(data: Uint8Array): { w: number; h: number } | null {
  if (data.length < 4) return null;
  // JPEG: FF D8 …
  if (data[0] === 0xFF && data[1] === 0xD8) {
    let i = 2;
    while (i + 3 < data.length) {
      if (data[i] !== 0xFF) break;
      const marker = data[i + 1];
      const segLen = (data[i + 2] << 8) | data[i + 3];
      if ((marker === 0xC0 || marker === 0xC1 || marker === 0xC2) && i + 8 < data.length) {
        return { h: (data[i + 5] << 8) | data[i + 6], w: (data[i + 7] << 8) | data[i + 8] };
      }
      i += 2 + segLen;
    }
    return null;
  }
  // PNG: 89 50 4E 47
  if (data[0] === 0x89 && data[1] === 0x50 && data[2] === 0x4E && data[3] === 0x47) {
    if (data.length < 24) return null;
    return {
      w: ((data[16] << 24) | (data[17] << 16) | (data[18] << 8) | data[19]) >>> 0,
      h: ((data[20] << 24) | (data[21] << 16) | (data[22] << 8) | data[23]) >>> 0,
    };
  }
  // WebP: RIFF….WEBPVP8 (simple lossy)
  if (data[0] === 0x52 && data[1] === 0x49 && data[8] === 0x57 && data[9] === 0x45 && data.length >= 30) {
    if (data[12] === 0x56 && data[13] === 0x50 && data[14] === 0x38 && data[15] === 0x20) {
      return {
        w: ((data[26] | (data[27] << 8)) & 0x3FFF) + 1,
        h: ((data[28] | (data[29] << 8)) & 0x3FFF) + 1,
      };
    }
  }
  return null;
}


type EditorState = ReturnType<typeof useEditorStore.getState>;
type HotspotContentRecord = Record<string, unknown>;
type PanoramaDataRecord = Record<string, unknown>;
type PanoramaView = { yaw: number; pitch: number; fov: number };
type HotspotWorldPosition = { x: number; y: number; z: number };
type SlideToLandingSource =
  | "explicit-view"
  | "initial-view"
  | "backlink-hotspot"
  | "linked-hotspot"
  | "any-hotspot"
  | "source-hotspot"
  | "none";
type SlideToDebugStats = {
  status: "idle" | "active" | "invalid" | "complete";
  viewMode: "automatic" | "manual";
  sourcePanoramaId: string | null;
  targetPanoramaId: string | null;
  sourcePanoramaName?: string | null;
  targetPanoramaName?: string | null;
  sourceHotspotId?: string | null;
  sourceHotspotName?: string | null;
  anchorHotspotId?: string | null;
  anchorHotspotName?: string | null;
  reason?: string | null;
  resolvedFrom?: SlideToLandingSource | null;
  landingView?: { yaw: number; pitch: number; fov: number } | null;
  landingYawDelta?: number | null;
  progress?: number | null;
  k?: number | null;
  startDistance?: number | null;
  currentDistance?: number | null;
  startFov?: number | null;
  currentFov?: number | null;
  dollyTargetScale?: number | null;
  direction?: { x: number; y: number; z: number } | null;
  anchorPosition?: { x: number; y: number; z: number } | null;
  startCamera?: { x: number; y: number; z: number } | null;
  currentCamera?: { x: number; y: number; z: number } | null;
  sourceExitYaw?: number | null;
  targetEntryYaw?: number | null;
  targetBaseYaw?: number | null;
  relativeYawFromExit?: number | null;
  resolvedYaw?: number | null;
  resolvedPitch?: number | null;
};
type BacklinkPlacementContext = {
  sourcePanoramaId: string;
  sourcePanoramaName: string | null;
  targetPanoramaId: string;
  targetPanoramaName: string | null;
  sourceHotspotId: string;
};
type PanoramaTransitionType =
  | "default"
  | "same-view"
  | "specific-view"
  | "crossfade"
  | "fade"
  | "fade-same-view"
  | "slide-to"
  | "morph";
type PanoramaNavigationOptions = {
  transitionType?: PanoramaTransitionType;
  view?: PanoramaView;
  hotspotPosition?: HotspotWorldPosition;
  hotspotId?: string;
  sourcePanoramaId?: string;
  fadeColor?: string;
  fadeDurationMs?: number;
  forceLandingView?: boolean;
  transitionViewMode?: "automatic" | "manual";
  transitionDirectionView?: PanoramaView;
};
type PanoramaNavigationRequestDetail = {
  panoramaId: string;
  transitionType?: PanoramaTransitionType;
  linkedPanoramaView?: PanoramaView;
  hotspotPosition?: HotspotWorldPosition;
  hotspotId?: string;
  sourcePanoramaId?: string;
  fadeColor?: string;
  fadeDurationMs?: number;
  forceLandingView?: boolean;
  transitionViewMode?: "automatic" | "manual";
  transitionDirectionView?: PanoramaView;
};
type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";
type ScreenPoint = { x: number; y: number };
type HotspotTextResizeMode = "fixed" | "scale-with-box";
const HOTSPOT_LOCAL_STYLE_CONTENT_KEY = "localStyleContent";
type HtmlResizeContentSnapshot = {
  width: string;
  height: string;
  fontSize?: string;
};

type ResizeDragState = {
  hotspotId: string;
  handle: ResizeHandle;
  isHtml: boolean;
  startPointer: ScreenPoint;
  startScale: { x: number; y: number; z: number };
  startHalfWidth: number;
  startHalfHeight: number;
  baseWidth: number;
  baseHeight: number;
  startContentWidthPx: number;
  startContentHeightPx: number;
  startFontSizePx: number | null;
  textResizeMode: HotspotTextResizeMode;
  startContentSnapshot: HotspotContentRecord;
  xPixelsPerWorld: number;
  yPixelsPerWorld: number;
  xScreenUnit: ScreenPoint;
  yScreenUnit: ScreenPoint;
  aspectRatio: number;
  lastScale: { x: number; y: number; z: number };
  lastHtmlContent: HtmlResizeContentSnapshot | null;
  lastHtmlDispatchAtMs: number;
};

type RotateDragState = {
  hotspotId: string;
  axis: "x" | "y" | "z" | "free";
  startPointer: ScreenPoint;
  startRotation: { x: number; y: number; z: number };
};

type PlacementDragState = {
  startPointer: ScreenPoint;
  currentPointer: ScreenPoint;
};

type HotspotFrame = {
  centerWorld: THREE.Vector3;
  xAxisWorld: THREE.Vector3;
  yAxisWorld: THREE.Vector3;
  halfWidthWorld: number;
  halfHeightWorld: number;
  centerScreen: ScreenPoint;
  cornersScreen: [ScreenPoint, ScreenPoint, ScreenPoint, ScreenPoint];
  xScreenVector: ScreenPoint;
  yScreenVector: ScreenPoint;
};

const RESIZE_HANDLES: ResizeHandle[] = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];

const RESIZE_CURSOR_BY_HANDLE: Record<ResizeHandle, string> = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  ne: "nesw-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
  sw: "nesw-resize",
};

const RESIZE_DIRECTION_BY_HANDLE: Record<ResizeHandle, { x: -1 | 0 | 1; y: -1 | 0 | 1 }> = {
  n: { x: 0, y: 1 },
  s: { x: 0, y: -1 },
  e: { x: 1, y: 0 },
  w: { x: -1, y: 0 },
  ne: { x: 1, y: 1 },
  nw: { x: -1, y: 1 },
  se: { x: 1, y: -1 },
  sw: { x: -1, y: -1 },
};

// Mapping of handles to rotation axes for 3D mode
const ROTATE_AXIS_BY_HANDLE: Record<ResizeHandle, "x" | "y" | "z"> = {
  n: "x",  // Top edge -> rotate around X
  s: "x",  // Bottom edge -> rotate around X
  e: "z",  // Right edge -> rotate around Z
  w: "z",  // Left edge -> rotate around Z
  ne: "y", // Top-right corner -> rotate around Y
  nw: "y", // Top-left corner -> rotate around Y
  se: "y", // Bottom-right corner -> rotate around Y
  sw: "y", // Bottom-left corner -> rotate around Y
};

// Rotate cursor - curved arrow for rotation (like Figma, Illustrator, PowerPoint)
const ROTATE_CURSOR = `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cg fill='%2360a5fa'%3E%3Cpath d='M16 1a15 15 0 0 1 13.86 22.5l-2.6-1.5A12 12 0 0 0 16 4a12 12 0 0 0-11.26 7.5L2.14 9.5A15 15 0 0 1 16 1z'/%3E%3Cpath d='M29 23.5l-4.5 2.6-1.5-5.2 2.6 1.5c1.5-2.5 2.4-5.2 2.4-8.4 0-3-0.8-6-2.4-8.4l2.6-1.5c1.8 2.8 2.8 6 2.8 9.9 0 3.8-1 7-2.8 9.9z'/%3E%3Cpath d='M7 25.5L2.5 28l1.5 5.2-2.6-1.5C-0.6 29 -1.6 26 -1.6 22c0-3 0.8-6 2.4-8.4l-2.6-1.5C-1.8 14.9-2.8 18-2.8 22c0 3.8 1 7 2.8 9.9z'/%3E%3C/g%3E%3C/svg%3E") 16 16, auto`;

const getHotspotOrderValue = (hotspot: Hotspot): number =>
  typeof hotspot.order === "number" ? hotspot.order : 0;

const compareHotspotsBySidebarOrder = (a: Hotspot, b: Hotspot): number => {
  return getHotspotOrderValue(a) - getHotspotOrderValue(b);
};

interface Viewer360Props {
  hotspotPreviewColor?: string;
  forceEnableCamera?: boolean;
  onPanoramaChange?: (panorama: Panorama) => void;
}

// Helper to normalize angles to (-180, 180]
const _normalizeDeg = (value: number): number => {
  while (value > 180) value -= 360;
  while (value <= -180) value += 360;
  return value;
};

const isHotspotWorldPosition = (value: unknown): value is HotspotWorldPosition => {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    "x" in value &&
    "y" in value &&
    "z" in value
  );
};

const parseHotspotWorldPosition = (value: unknown): HotspotWorldPosition | null => {
  if (!value) return null;

  if (typeof value === "string") {
    try {
      return parseHotspotWorldPosition(JSON.parse(value));
    } catch {
      return null;
    }
  }

  if (!isHotspotWorldPosition(value)) {
    return null;
  }

  const x = Number(value.x);
  const y = Number(value.y);
  const z = Number(value.z);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null;
  }

  return { x, y, z };
};

const hotspotLinksToPanorama = (hotspot: Hotspot, panoramaId: string): boolean => {
  const content = (hotspot.content as HotspotContentRecord) || {};
  if (content.linkedPanoramaId === panoramaId) return true;
  const actions = (content.actions as Array<Record<string, unknown>>) || [];
  return actions.some(
    (action) =>
      action?.type === "navigate" && action.linkedPanoramaId === panoramaId,
  );
};

const hotspotHasLinkedPanorama = (hotspot: Hotspot): boolean => {
  const content = (hotspot.content as HotspotContentRecord) || {};
  if (typeof content.linkedPanoramaId === "string" && content.linkedPanoramaId.length > 0) {
    return true;
  }
  const actions = (content.actions as Array<Record<string, unknown>>) || [];
  return actions.some(
    (action) =>
      action?.type === "navigate" &&
      typeof action.linkedPanoramaId === "string" &&
      action.linkedPanoramaId.length > 0,
  );
};

const yawPitchToVector = (yaw: number, pitch: number): THREE.Vector3 => {
  const yawRad = THREE.MathUtils.degToRad(yaw);
  const pitchRad = THREE.MathUtils.degToRad(pitch);
  return new THREE.Vector3(
    Math.cos(pitchRad) * Math.sin(yawRad),
    Math.sin(pitchRad),
    Math.cos(pitchRad) * Math.cos(yawRad),
  );
};

const easeOutCubic = (value: number): number => 1 - (1 - value) ** 3;

const easeInOutCubic = (value: number): number =>
  value < 0.5 ? 4 * value * value * value : 1 - (-2 * value + 2) ** 3 / 2;

const PANORAMA_CROSSFADE_TOTAL_DURATION_MS = 1000;
const PANORAMA_CROSSFADE_OUTGOING_START_OPACITY = 0.2;
const SLIDE_TO_DOLLY_TARGET_SCALE = 0.6;

const toVec3 = (
  value: THREE.Vector3 | null | undefined,
): { x: number; y: number; z: number } | null =>
  value ? { x: value.x, y: value.y, z: value.z } : null;

const warnSlideToDev = (stage: string, payload: Record<string, unknown>) => {
  if (!import.meta.env.DEV) return;
  console.warn(`[slide-to] ${stage}`, payload);
};

const parseStrictPx = (value: unknown): number | null => {
  if (typeof value !== "string") return null;
  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)px$/i);
  if (!match) return null;
  const parsed = Number.parseFloat(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPxString = (value: number): string => `${Math.max(1, Math.round(value))}px`;

const resolveTextResizeMode = (value: unknown): HotspotTextResizeMode =>
  value === "scale-with-box" ? "scale-with-box" : "fixed";

const normalizePanoramaTransitionType = (
  rawTransitionType: unknown,
  hasLinkedPanoramaView: boolean,
): PanoramaTransitionType => {
  if (
    rawTransitionType === "same-view" ||
    rawTransitionType === "specific-view" ||
    rawTransitionType === "crossfade" ||
    rawTransitionType === "fade" ||
    rawTransitionType === "fade-same-view" ||
    rawTransitionType === "slide-to" ||
    rawTransitionType === "morph"
  ) {
    return rawTransitionType;
  }

  if (
    rawTransitionType === "morph-1" ||
    rawTransitionType === "morph-2" ||
    rawTransitionType === "morph-3" ||
    rawTransitionType === "morph-screen-space" ||
    rawTransitionType === "morph-camera-driven"
  ) {
    return "morph";
  }

  return hasLinkedPanoramaView ? "specific-view" : "default";
};

// Helper function to execute hotspot actions
const executeHotspotActions = (
  hotspot: Hotspot,
  currentState: EditorState,
  navigateToPanorama: (panorama: Panorama, options?: PanoramaNavigationOptions) => void,
) => {
  const content = (hotspot.content as HotspotContentRecord) || {};

  // Read actions array, with backward compat for old flat structure
  const actions = (content.actions as Array<Record<string, unknown>>) || [];
  const activeSourcePanoramaId =
    currentState.selectedPanorama?.id ||
    (hotspot as { panorama_id?: string; panoramaId?: string }).panorama_id ||
    (hotspot as { panorama_id?: string; panoramaId?: string }).panoramaId;

  if (actions.length > 0) {
    // New multi-action format
    actions.forEach((action) => {
      const type = action.type as string;

      if (type === "navigate" && action.linkedPanoramaId) {
        const linkedPanorama = currentState.panoramas?.find((p: Panorama) => p.id === action.linkedPanoramaId);
        if (linkedPanorama) {
          const linkedPanoramaView = action.linkedPanoramaView as PanoramaView | undefined;
          const transitionViewMode = action.linkTransitionViewMode as "automatic" | "manual" | undefined;
          const transitionDirectionView = action.linkTransitionDirectionView as PanoramaView | undefined;
          const normalizedTransitionType = normalizePanoramaTransitionType(
            action.linkTransitionType,
            Boolean(linkedPanoramaView),
          );
          const fadeColor = action.linkTransitionFadeColor as string | undefined;
          const fadeDurationSec = action.linkTransitionFadeDurationSec as number | undefined;
          const fadeDurationMs =
            typeof fadeDurationSec === "number" && Number.isFinite(fadeDurationSec)
              ? Math.max(0, fadeDurationSec) * 1000
              : undefined;

          if (normalizedTransitionType === "slide-to") {
            warnSlideToDev("viewer.executeHotspotActions", {
              mode: currentState.isPreviewMode ? "preview" : "edit",
              hotspotId: hotspot.id,
              sourcePanoramaId: activeSourcePanoramaId,
              targetPanoramaId: linkedPanorama.id,
              transitionViewMode,
              transitionDirectionView,
              hotspotPosition: parseHotspotWorldPosition(hotspot.position) || undefined,
            });
          }

          window.dispatchEvent(
            new CustomEvent<PanoramaNavigationRequestDetail>("360crafter:navigate-panorama", {
              detail: {
                panoramaId: linkedPanorama.id,
                transitionType: normalizedTransitionType,
                linkedPanoramaView:
                  normalizedTransitionType === "same-view" ||
                  normalizedTransitionType === "fade-same-view"
                    ? currentState.currentCameraView || undefined
                    : normalizedTransitionType === "specific-view" || normalizedTransitionType === "morph"
                      ? linkedPanoramaView
                      : undefined,
                hotspotPosition: parseHotspotWorldPosition(hotspot.position) || undefined,
                hotspotId: hotspot.id,
                sourcePanoramaId: activeSourcePanoramaId,
                fadeColor,
                fadeDurationMs,
                transitionViewMode,
                transitionDirectionView,
              },
            }),
          );
        }
      } else if (type === "notification") {
        const title = (action.notificationTitle as string) || "Notification";
        const message = (action.notificationMessage as string) || "";
        const autoHideDuration = action.notificationAutoHideDuration as number | undefined;
        import("@/hooks/use-toast").then(({ toast }) => {
          toast({
            title,
            description: message,
            duration: autoHideDuration !== undefined ? autoHideDuration * 1000 : 1000000000,
            variant: autoHideDuration === undefined ? "alert" : "default",
          });
        });
      } else if (type === "download" && action.downloadFileUrl) {
        void promptAndSaveFile(action.downloadFileUrl as string, action.downloadFileName as string | undefined);
      } else if (type === "showHideSkin") {
        const targetIds = (action.skinVisibilityTargetComponentIds as string[]) || [];
        const visAction = action.skinVisibilityAction as string;
        if (visAction && targetIds.length > 0) {
          if (visAction === "show") currentState.showSkinComponents(targetIds);
          else if (visAction === "hide") currentState.hideSkinComponents(targetIds);
          else if (visAction === "toggle") currentState.toggleSkinComponents(targetIds);
        }
      } else if (type === "showHideHotspots") {
        let targetIds = (action.hotspotVisibilityTargetIds as string[]) || [];
        const config = action.hotspotVisibilityItemsConfig as Record<string, unknown>;
        if (config?.mode === "dynamic" && targetIds.length === 0) {
          targetIds = currentState.hotspots.map((h) => h.id);
        }
        const visAction = action.hotspotVisibilityAction as string;
        if (visAction && targetIds.length > 0) {
          if (visAction === "show") currentState.showHotspots(targetIds);
          else if (visAction === "hide") currentState.hideHotspots(targetIds);
          else if (visAction === "toggle") currentState.toggleHotspots(targetIds);
        }
      }
    });
  } else {
    // Backward compat: old flat structure
    let actionsTriggered = 0;

    // Handle panorama linking if hotspot has one
    const linkedPanoramaId = content.linkedPanoramaId;
    if (linkedPanoramaId && currentState.panoramas) {
      const linkedPanorama = currentState.panoramas.find((p: Panorama) => p.id === linkedPanoramaId);
      if (linkedPanorama) {
        const linkedPanoramaView = content.linkedPanoramaView as PanoramaView | undefined;
        const transitionViewMode = content.linkTransitionViewMode as "automatic" | "manual" | undefined;
        const transitionDirectionView = content.linkTransitionDirectionView as PanoramaView | undefined;
        const normalizedTransitionType = normalizePanoramaTransitionType(
          content.linkTransitionType,
          Boolean(linkedPanoramaView),
        );

        if (normalizedTransitionType === "slide-to") {
          warnSlideToDev("viewer.executeHotspotActions.legacy", {
            mode: currentState.isPreviewMode ? "preview" : "edit",
            hotspotId: hotspot.id,
            sourcePanoramaId: activeSourcePanoramaId,
            targetPanoramaId: linkedPanorama.id,
            transitionViewMode,
            transitionDirectionView,
            hotspotPosition: parseHotspotWorldPosition(hotspot.position) || undefined,
          });
        }

        window.dispatchEvent(
          new CustomEvent<PanoramaNavigationRequestDetail>("360crafter:navigate-panorama", {
            detail: {
              panoramaId: linkedPanorama.id,
              transitionType: normalizedTransitionType,
              linkedPanoramaView:
                normalizedTransitionType === "same-view"
                  ? currentState.currentCameraView || undefined
                  : normalizedTransitionType === "specific-view" || normalizedTransitionType === "morph"
                    ? linkedPanoramaView
                    : undefined,
              hotspotPosition: parseHotspotWorldPosition(hotspot.position) || undefined,
              hotspotId: hotspot.id,
              sourcePanoramaId: activeSourcePanoramaId,
              transitionViewMode,
              transitionDirectionView,
            },
          }),
        );
        actionsTriggered++;
      }
    }

    // Show notification if hotspot has one
    const notificationMessage = content.notificationMessage;
    const notificationTitle = content.notificationTitle;
    if (notificationMessage || notificationTitle) {
      const title = notificationTitle || "Notification";
      const message = notificationMessage || "";
      const autoHideDuration = content.notificationAutoHideDuration;
      const isAlert = autoHideDuration === undefined;

      // Import toast dynamically to avoid circular dependencies
      import("@/hooks/use-toast").then(({ toast }) => {
        toast({
          title: title,
          description: message,
          duration: autoHideDuration !== undefined ? autoHideDuration * 1000 : 1000000000,
          variant: isAlert ? "alert" : "default",
        });
      });
      actionsTriggered++;
    }

    // Trigger download if hotspot has one
    const downloadFileUrl = content.downloadFileUrl;
    if (downloadFileUrl) {
      void promptAndSaveFile(downloadFileUrl, content.downloadFileName as string | undefined);
      actionsTriggered++;
    }

    // Handle skin component visibility actions
    const skinVisibilityTargetIds = content.skinVisibilityTargetComponentIds;
    const skinVisibilityAction = content.skinVisibilityAction;
    if (skinVisibilityAction && skinVisibilityTargetIds && skinVisibilityTargetIds.length > 0) {
      if (skinVisibilityAction === "show") {
        currentState.showSkinComponents(skinVisibilityTargetIds);
      } else if (skinVisibilityAction === "hide") {
        currentState.hideSkinComponents(skinVisibilityTargetIds);
      } else if (skinVisibilityAction === "toggle") {
        currentState.toggleSkinComponents(skinVisibilityTargetIds);
      }
      actionsTriggered++;
    }

    // Handle hotspot visibility actions
    const hotspotVisibilityTargetIds = content.hotspotVisibilityTargetIds;
    const hotspotVisibilityAction = content.hotspotVisibilityAction;
    const hotspotVisibilityConfig = content.hotspotVisibilityItemsConfig;

    if (hotspotVisibilityAction) {
      // Determine which hotspots to apply the action to
      let targetIds = hotspotVisibilityTargetIds || [];

      // In dynamic mode with filters/sort, apply action to all hotspots
      // (In a future update, we'd apply filters, but for now we use all available)
      if (hotspotVisibilityConfig?.mode === "dynamic" && targetIds.length === 0) {
        targetIds = currentState.hotspots.map((h) => h.id);
      }

      if (targetIds.length > 0) {
        if (hotspotVisibilityAction === "show") {
          currentState.showHotspots(targetIds);
        } else if (hotspotVisibilityAction === "hide") {
          currentState.hideHotspots(targetIds);
        } else if (hotspotVisibilityAction === "toggle") {
          currentState.toggleHotspots(targetIds);
        }
        actionsTriggered++;
      }
    }

    // Handle confetti actions
    const confettiAction = content.confettiAction;
    if (confettiAction) {
      // Import confetti service dynamically
      import("@/utils/confettiService").then(({ confettiService }) => {
        confettiService.triggerConfetti(confettiAction);
      });
      actionsTriggered++;
    }

    if (actionsTriggered === 0) {
      console.log("No actions configured for hotspot:", hotspot.id);
    }
  }

  // Handle confetti (independent, always additive)
  const confettiAction = content.confettiAction;
  if (confettiAction) {
    // Import confetti service dynamically
    import("@/utils/confettiService").then(({ confettiService }) => {
      confettiService.triggerConfetti(confettiAction);
    });
  }
};

const Viewer360 = ({
  hotspotPreviewColor: _hotspotPreviewColor = "#2196f3",
  forceEnableCamera: _forceEnableCamera = false,
  onPanoramaChange,
}: Viewer360Props) => {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const convex = useConvex();
  const {
    getPanorama,
    getThumbnail,
    createBlobURL,
    isInitialized: isCacheInitialized,
  } = useIndexedDBCache();

  // Ref so the texture-loading effect can read the latest isCacheInitialized
  // without being re-triggered every time it flips (false→true on init).
  const isCacheInitializedRef = useRef(isCacheInitialized);
  isCacheInitializedRef.current = isCacheInitialized;

  // Check for debug URL parameter
  const urlParams = new URLSearchParams(location.search);
  const showDebug = urlParams.get("debug") === "true";

  // Get all state from the store
  const {
    selectedPanorama: panorama,
    hotspots,
    selectedHotspot,
    panoramas,
    placementMode,
    placementType,
    setSelectedPanorama,
    setPlacementMode,
    isPreviewMode,
    updateHotspotFromViewer,
    skinComponents,
    selectedSkinComponent,
    setSelectedSkinComponent,
    setSkinComponents,
    currentRoute,
    activeCMSDatasetId,
    activeCMSRowIndex,
    project,
    setCurrentCameraView,
    pendingPanoramaView,
    setPendingPanoramaView,
    currentCameraView,
    defaultViewSaveEvent,
    focusHotspotRequestId,
    requestFocusHotspot,
    hotspotRenderStyle,
    isDraggingToAddHotspot,
    chromaKeyPickHotspotId,
    setChromaKeyPickHotspotId,
    pendingHotspotPlacementId,
    setPendingHotspotPlacementId,
  } = useEditorStore();
  const projectId = project?.id;
  const projectCraftspaceId = project?.craftspace_id || "default";

  const toastRef = useRef(toast);
  const userRef = useRef(user);
  const isPreviewModeRef = useRef(isPreviewMode);
  const currentRouteRef = useRef(currentRoute);
  const showContextualPopoverRef = useRef<((x: number, y: number) => void) | null>(null);
  const pickHotspotAtPointRef = useRef<((clientX: number, clientY: number) => string | null) | null>(null);
  const contextMenuOpenRef = useRef(false);
  const chromaSampleCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const setCurrentCameraViewRef = useRef(setCurrentCameraView);
  const setPendingPanoramaViewRef = useRef(setPendingPanoramaView);
  const pendingPanoramaViewRef = useRef<{
    panoramaId: string;
    view: PanoramaView;
  } | null>(null);
  const navigationLandingViewRef = useRef<{
    panoramaId: string;
    view: PanoramaView;
  } | null>(null);
  const selectedHotspotRef = useRef<Hotspot | null>(selectedHotspot);
  const placementModeRef = useRef(placementMode);

  const getPlacementUser = useCallback(() => {
    return userRef.current ?? (isElectron() ? { id: LOCAL_USER_ID } : null);
  }, []);

  const getHotspotFacingMode = useCallback(
    (hotspot: Hotspot | null) => {
      const content = (hotspot?.content as Record<string, unknown>) || {};
      const style = content.renderStyle;
      return style === "3d" || style === "billboard" ? style : hotspotRenderStyle;
    },
    [hotspotRenderStyle],
  );
  const lastPanoramaLoadRef = useRef<{ id: string; url: string } | null>(null);
  const morphSwitchTimeoutRef = useRef<number | null>(null);
  const morphStageTimeoutRef = useRef<number | null>(null);
  const morphResetTimeoutRef = useRef<number | null>(null);
  const panoramaTransitionAnimationFrameRef = useRef<number | null>(null);
  const clickSoundRef = useRef<HTMLAudioElement | null>(null);
  const defaultViewOutlineTimerRef = useRef<number | null>(null);
  const invalidSlideToOutlineTimerRef = useRef<number | null>(null);
  const pendingCameraDrivenArrivalRef = useRef<{ panoramaId: string; incomingFov: number } | null>(
    null,
  );
  const pendingCrossfadeArrivalRef = useRef<{
    panoramaId: string;
    incomingFov: number;
    restoreFov: number;
    fadeDurationMs?: number;
    fadeColor?: string;
    isFade?: boolean;
    forceLandingView?: boolean;
    isAutomaticSlideTo?: boolean;
    slideToResolvedYaw?: number;
    slideToResolvedPitch?: number;
  } | null>(null);
  const crossfadeFovZoomRef = useRef<{
    startFov: number;
    zoomedFov: number;
    endFov: number;
    hotspotDirection?: THREE.Vector3;
    anchorPosition?: THREE.Vector3;
  } | null>(null);
  const crossfadeAnimationFrameRef = useRef<number | null>(null);
  const crossfadeStartTimeoutRef = useRef<number | null>(null);
  const crossfadeRetainedTextureRef = useRef<THREE.Texture | null>(null);

  useEffect(() => {
    if (!chromaKeyPickHotspotId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setChromaKeyPickHotspotId(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [chromaKeyPickHotspotId, setChromaKeyPickHotspotId]);

  useEffect(() => {
    if (!hotspotManagerRef.current) return;
    hotspotManagerRef.current.setVideoPlaybackActive(currentRoute === "editor");
  }, [currentRoute]);
  const crossfadeDebugCounterRef = useRef(0);
  const crossfadeDebugContextRef = useRef<{
    id: number;
    startedAt: number;
    sourcePanoramaId: string | null;
    targetPanoramaId: string | null;
    cameraLockLogged: boolean;
  } | null>(null);
  const isPanoramaTransitioningRef = useRef(false);
  const getTextureCacheKey = (panorama: Panorama): string => {
    const url = panorama.private_file_url || panorama.file_url || "no-url";
    return `panorama:${panorama.id}:${url}`;
  };
  const [isMorphTransitionActive, setIsMorphTransitionActive] = useState(false);
  const [showDefaultViewOutline, setShowDefaultViewOutline] = useState(false);
  const [showInvalidSlideToOutline, setShowInvalidSlideToOutline] = useState(false);
  const placementPreviewColor =
    placementType === "html"
      ? "rgba(255, 255, 255, 0.9)"
      : placementType === "video"
        ? "rgba(0, 0, 0, 0.55)"
        : (_hotspotPreviewColor || "#2196f3");

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    isPreviewModeRef.current = isPreviewMode;
  }, [isPreviewMode]);

  useEffect(() => {
    if (!defaultViewSaveEvent || !panorama) return;
    if (defaultViewSaveEvent.panoramaId !== panorama.id) return;

    setShowDefaultViewOutline(true);
    if (defaultViewOutlineTimerRef.current) {
      window.clearTimeout(defaultViewOutlineTimerRef.current);
    }
    defaultViewOutlineTimerRef.current = window.setTimeout(() => {
      setShowDefaultViewOutline(false);
      defaultViewOutlineTimerRef.current = null;
    }, 520);
  }, [defaultViewSaveEvent, panorama?.id]);

  const triggerInvalidSlideToOutline = useCallback(() => {
    setShowInvalidSlideToOutline(true);
    if (invalidSlideToOutlineTimerRef.current) {
      window.clearTimeout(invalidSlideToOutlineTimerRef.current);
    }
    invalidSlideToOutlineTimerRef.current = window.setTimeout(() => {
      setShowInvalidSlideToOutline(false);
      invalidSlideToOutlineTimerRef.current = null;
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (defaultViewOutlineTimerRef.current) {
        window.clearTimeout(defaultViewOutlineTimerRef.current);
      }
      if (invalidSlideToOutlineTimerRef.current) {
        window.clearTimeout(invalidSlideToOutlineTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setCurrentCameraViewRef.current = setCurrentCameraView;
  }, [setCurrentCameraView]);

  useEffect(() => {
    setPendingPanoramaViewRef.current = setPendingPanoramaView;
  }, [setPendingPanoramaView]);

  useEffect(() => {
    pendingPanoramaViewRef.current = pendingPanoramaView;
  }, [pendingPanoramaView]);

  useEffect(() => {
    selectedHotspotRef.current = selectedHotspot;
  }, [selectedHotspot]);

  useEffect(() => {
    placementModeRef.current = placementMode;
  }, [placementMode]);

  useEffect(() => {
    if (!placementMode) {
      isPlacementDraggingRef.current = false;
      placementDragRef.current = null;
      setPlacementRect(null);
    }
  }, [placementMode]);

  useEffect(() => {
    currentRouteRef.current = currentRoute;
  }, [currentRoute]);

  const logCrossfadeDebug = (event: string, payload?: Record<string, unknown>) => {
    const context = crossfadeDebugContextRef.current;
    const elapsed = context ? `${(performance.now() - context.startedAt).toFixed(1)}ms` : "n/a";
    const label = context ? `#${context.id}` : "no-id";
    if (payload) {
      console.log(`[PanoramaCrossfadeDebug ${label} +${elapsed}] ${event}`, payload);
      return;
    }
    console.log(`[PanoramaCrossfadeDebug ${label} +${elapsed}] ${event}`);
  };

  const clearMorphTransitionTimeouts = useCallback(() => {
    if (morphSwitchTimeoutRef.current !== null) {
      window.clearTimeout(morphSwitchTimeoutRef.current);
      morphSwitchTimeoutRef.current = null;
    }
    if (morphStageTimeoutRef.current !== null) {
      window.clearTimeout(morphStageTimeoutRef.current);
      morphStageTimeoutRef.current = null;
    }
    if (morphResetTimeoutRef.current !== null) {
      window.clearTimeout(morphResetTimeoutRef.current);
      morphResetTimeoutRef.current = null;
    }
  }, []);

  const clearPanoramaTransitionAnimation = useCallback(() => {
    if (panoramaTransitionAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(panoramaTransitionAnimationFrameRef.current);
      panoramaTransitionAnimationFrameRef.current = null;
    }
  }, []);

  const clearPanoramaTransitionVisuals = useCallback(() => {
    const renderer = rendererRef.current;
    if (!renderer) return;

    const element = renderer.domElement;
    element.style.transition = "";
    element.style.willChange = "";
    element.style.transformOrigin = "";
    element.style.transform = "";
    element.style.filter = "";
    element.style.opacity = "";
  }, []);

  const clearCrossfadeTransition = useCallback(() => {
    logCrossfadeDebug("clearCrossfadeTransition");
    if (crossfadeAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(crossfadeAnimationFrameRef.current);
      crossfadeAnimationFrameRef.current = null;
    }
    if (crossfadeStartTimeoutRef.current !== null) {
      window.clearTimeout(crossfadeStartTimeoutRef.current);
      crossfadeStartTimeoutRef.current = null;
    }
    const crossfadeSphere = crossfadeSphereRef.current;
    if (crossfadeSphere?.material instanceof THREE.MeshBasicMaterial) {
      crossfadeSphere.material.opacity = 0;
      crossfadeSphere.material.map = null;
      crossfadeSphere.material.needsUpdate = true;
    }
    const fadeOverlaySphere = fadeOverlaySphereRef.current;
    if (fadeOverlaySphere?.material instanceof THREE.MeshBasicMaterial) {
      fadeOverlaySphere.material.opacity = 0;
      fadeOverlaySphere.material.needsUpdate = true;
    }
    if (crossfadeRetainedTextureRef.current) {
      const activeTexture =
        sphereRef.current?.material instanceof THREE.MeshBasicMaterial
          ? sphereRef.current.material.map
          : null;
      if (crossfadeRetainedTextureRef.current !== activeTexture) {
        crossfadeRetainedTextureRef.current.dispose();
      }
      crossfadeRetainedTextureRef.current = null;
    }
  }, []);

  const startCrossfadeFadeOut = useCallback((
    durationMs: number = 1000,
    incomingMaterial?: THREE.MeshBasicMaterial,
    fovZoom?: {
      startFov: number;
      zoomedFov: number;
      endFov: number;
      hotspotDirection?: THREE.Vector3;
      anchorPosition?: THREE.Vector3;
      recenterAtEnd?: boolean;
    },
    fadeColor?: string,
  ) => {
    logCrossfadeDebug("startCrossfadeFadeOut", {
      durationMs,
      hasIncomingMaterial: Boolean(incomingMaterial),
      hasFovZoom: Boolean(fovZoom),
      fadeColor: fadeColor || "transparent",
    });
    const crossfadeSphere = crossfadeSphereRef.current;
    if (!(crossfadeSphere?.material instanceof THREE.MeshBasicMaterial)) return;
    if (!crossfadeSphere.material.map || crossfadeSphere.material.opacity <= 0) return;

    if (crossfadeAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(crossfadeAnimationFrameRef.current);
      crossfadeAnimationFrameRef.current = null;
    }
    if (crossfadeStartTimeoutRef.current !== null) {
      window.clearTimeout(crossfadeStartTimeoutRef.current);
      crossfadeStartTimeoutRef.current = null;
    }

    const camera = cameraRef.current;
    const fadeOverlaySphere = fadeOverlaySphereRef.current;
    const fadeOverlayMaterial =
      fadeOverlaySphere?.material instanceof THREE.MeshBasicMaterial
        ? fadeOverlaySphere.material
        : null;
    const resolveCssColor = (value: string): string | null => {
      if (typeof document === "undefined") return null;
      const temp = document.createElement("div");
      temp.style.color = value;
      temp.style.visibility = "hidden";
      temp.style.position = "absolute";
      temp.style.top = "-9999px";
      document.body.appendChild(temp);
      const computed = window.getComputedStyle(temp).color;
      document.body.removeChild(temp);
      if (!computed) return null;
      const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!match) return null;
      return computed;
    };

    const normalizeFadeColor = (value: string | undefined): string | null => {
      if (!value) return null;
      const trimmed = value.trim();
      if (!trimmed || trimmed.toLowerCase() === "transparent") return null;
      if (trimmed.startsWith("var(") || trimmed.startsWith("--") || trimmed.toLowerCase().includes("color-mix(")) {
        const resolved = resolveCssColor(trimmed.startsWith("--") ? `var(${trimmed})` : trimmed);
        if (resolved) return resolved;
      }
      const variableLike = /^[a-zA-Z0-9_-]+$/.test(trimmed)
        ? resolveCssColor(`var(--${trimmed})`)
        : null;
      if (variableLike) return variableLike;
      if (/^#[0-9a-fA-F]{4}$/.test(trimmed)) return `#${trimmed.slice(1, 4)}`;
      if (/^#[0-9a-fA-F]{8}$/.test(trimmed)) return `#${trimmed.slice(1, 7)}`;
      if (/^#[0-9a-fA-F]{3}$/.test(trimmed) || /^#[0-9a-fA-F]{6}$/.test(trimmed)) {
        return trimmed;
      }
      if (/^[0-9a-fA-F]{4}$/.test(trimmed)) return `#${trimmed.slice(0, 3)}`;
      if (/^[0-9a-fA-F]{8}$/.test(trimmed)) return `#${trimmed.slice(0, 6)}`;
      if (/^[0-9a-fA-F]{3}$/.test(trimmed) || /^[0-9a-fA-F]{6}$/.test(trimmed)) {
        return `#${trimmed}`;
      }
      const resolved = resolveCssColor(trimmed);
      return resolved ?? trimmed;
    };
    const normalizedFadeColor = normalizeFadeColor(fadeColor);
    const isSlideToTransition = Boolean(fovZoom);
    const useFadeOverlay =
      !isSlideToTransition && Boolean(fadeOverlayMaterial) && Boolean(normalizedFadeColor);
    if (useFadeOverlay && fadeOverlayMaterial && normalizedFadeColor) {
      try {
        fadeOverlayMaterial.color.set(normalizedFadeColor);
      } catch {
        fadeOverlayMaterial.color.set("#000000");
      }
      fadeOverlayMaterial.opacity = 0;
      fadeOverlayMaterial.needsUpdate = true;
    } else if (fadeOverlayMaterial) {
      fadeOverlayMaterial.opacity = 0;
      fadeOverlayMaterial.needsUpdate = true;
    }
    const startOpacity = incomingMaterial
      ? crossfadeSphere.material.opacity
      : Math.min(crossfadeSphere.material.opacity, PANORAMA_CROSSFADE_OUTGOING_START_OPACITY);
    crossfadeSphere.material.opacity = startOpacity;
    crossfadeSphere.material.needsUpdate = true;
    if (incomingMaterial) {
      incomingMaterial.opacity = 0;
      incomingMaterial.needsUpdate = true;
    }
    // Camera position dolly towards hotspot during departure (first half),
    // back to center during arrival (second half).
    const dollyDistance = 60;
    const hotspotOffset = fovZoom?.hotspotDirection
      ? fovZoom.hotspotDirection.clone().multiplyScalar(dollyDistance)
      : null;
    const anchorPosition = fovZoom?.anchorPosition?.clone() ?? null;
    const startCameraPosition = camera?.position.clone() ?? new THREE.Vector3(0, 0, 0);
    const startFovValue = fovZoom?.startFov ?? camera?.fov ?? 75;
    const startDistance = anchorPosition
      ? startCameraPosition.distanceTo(anchorPosition)
      : null;
    const dollyTargetScale = SLIDE_TO_DOLLY_TARGET_SCALE;
    const startedAt = performance.now();
    const animate = (now: number) => {
      const progress = Math.min((now - startedAt) / durationMs, 1);
      const eased = easeOutCubic(progress);
      crossfadeSphere.material.opacity = THREE.MathUtils.lerp(startOpacity, 0, eased);
      crossfadeSphere.material.needsUpdate = true;
      if (incomingMaterial) {
        incomingMaterial.opacity = eased;
        incomingMaterial.needsUpdate = true;
      }
      if (useFadeOverlay && fadeOverlayMaterial) {
        const overlayProgress =
          progress < 0.5
            ? easeInOutCubic(progress / 0.5)
            : easeInOutCubic((1 - progress) / 0.5);
        fadeOverlayMaterial.opacity = overlayProgress;
        fadeOverlayMaterial.needsUpdate = true;
      }
      // Dolly/zoom coupling when we have an anchor position.
      // Keep motion continuous across the midpoint for one fluid move.
      if (anchorPosition && camera && startDistance && startDistance > 0) {
        const k =
          progress < 0.5
            ? THREE.MathUtils.lerp(1, dollyTargetScale, easeInOutCubic(progress / 0.5))
            : dollyTargetScale;
        const currentPos = anchorPosition
          .clone()
          .add(startCameraPosition.clone().sub(anchorPosition).multiplyScalar(k));
        const currentDistance = currentPos.distanceTo(anchorPosition);
        const fovRad =
          2 *
          Math.atan(
            Math.tan(THREE.MathUtils.degToRad(startFovValue) / 2) *
              (currentDistance / startDistance),
          );
        const fovDeg = THREE.MathUtils.radToDeg(fovRad);
        const currentFovDeg =
          progress < 0.5 || !fovZoom
            ? fovDeg
            : fovZoom.zoomedFov;

        camera.position.copy(currentPos);
        camera.fov = THREE.MathUtils.clamp(currentFovDeg, 18, 150);
        camera.updateProjectionMatrix();
        if (slideToDebugRef.current?.status === "active") {
          slideToDebugRef.current = {
            ...slideToDebugRef.current,
            progress,
            k,
            startDistance,
            currentDistance,
            startFov: startFovValue,
            currentFov: camera.fov,
            dollyTargetScale,
            startCamera: toVec3(startCameraPosition),
            currentCamera: toVec3(camera.position),
          };
        }
      } else {
        // FOV zoom in during outgoing half, then hold zoom during incoming half
        // so settling to center does not create a zoom-out feel.
        if (fovZoom && camera) {
          if (progress < 0.5) {
            const halfProgress = progress / 0.5;
            camera.fov = THREE.MathUtils.lerp(
              fovZoom.startFov,
              fovZoom.zoomedFov,
              easeInOutCubic(halfProgress),
            );
          } else {
            camera.fov = fovZoom.zoomedFov;
          }
          camera.updateProjectionMatrix();
        }
        // Camera position: move toward hotspot in outgoing half, then smoothly
        // settle toward center in incoming half (no midpoint jump).
        if (hotspotOffset && camera) {
          if (progress < 0.5) {
            const halfProgress = easeInOutCubic(progress / 0.5);
            camera.position.set(
              hotspotOffset.x * halfProgress,
              hotspotOffset.y * halfProgress,
              hotspotOffset.z * halfProgress,
            );
          } else {
            camera.position.set(
              hotspotOffset.x,
              hotspotOffset.y,
              hotspotOffset.z,
            );
          }
        }
        if (slideToDebugRef.current?.status === "active") {
          slideToDebugRef.current = {
            ...slideToDebugRef.current,
            progress,
            startFov: startFovValue,
            currentFov: camera?.fov ?? null,
            dollyTargetScale,
            startCamera: toVec3(startCameraPosition),
            currentCamera: toVec3(camera?.position ?? null),
          };
        }
      }


      if (progress < 1) {
        crossfadeAnimationFrameRef.current = window.requestAnimationFrame(animate);
        return;
      }

      crossfadeAnimationFrameRef.current = null;
      crossfadeSphere.material.opacity = 0;
      crossfadeSphere.material.map = null;
      crossfadeSphere.material.needsUpdate = true;
      if (fadeOverlayMaterial) {
        fadeOverlayMaterial.opacity = 0;
        fadeOverlayMaterial.needsUpdate = true;
      }
      // Reset crossfade sphere rotation in case it was counter-rotated
      crossfadeSphere.rotation.y = Math.PI / 2;
      if (incomingMaterial) {
        incomingMaterial.opacity = 1;
        incomingMaterial.transparent = false;
        incomingMaterial.needsUpdate = true;
      }
      if (fovZoom && camera) {
        const shouldRecenterAtEnd = fovZoom.recenterAtEnd !== false;
        if (anchorPosition && startDistance && startDistance > 0) {
          if (shouldRecenterAtEnd) {
            camera.position.set(0, 0, 0);
          }
          camera.fov = THREE.MathUtils.clamp(fovZoom.zoomedFov, 18, 150);
        } else {
          if (shouldRecenterAtEnd) {
            camera.position.set(0, 0, 0);
          }
          camera.fov = THREE.MathUtils.clamp(fovZoom.zoomedFov, 18, 150);
        }
        camera.updateProjectionMatrix();
      }
      if (crossfadeRetainedTextureRef.current) {
        crossfadeRetainedTextureRef.current.dispose();
        crossfadeRetainedTextureRef.current = null;
      }
      const finalizeCrossfade = () => {
        isPanoramaTransitioningRef.current = false;
        setPendingPanoramaViewRef.current(null);
        navigationLandingViewRef.current = null;
        if (slideToDebugRef.current?.status === "active") {
          slideToDebugRef.current = {
            ...slideToDebugRef.current,
            status: "complete",
            progress: 1,
            currentFov: camera?.fov ?? slideToDebugRef.current.currentFov ?? null,
            currentCamera: toVec3(camera?.position ?? null),
          };
        }
        logCrossfadeDebug("crossfadeFadeOutComplete");
        crossfadeDebugContextRef.current = null;
      };

      finalizeCrossfade();
    };

    crossfadeStartTimeoutRef.current = window.setTimeout(() => {
      crossfadeAnimationFrameRef.current = window.requestAnimationFrame(animate);
      crossfadeStartTimeoutRef.current = null;
    }, 16);
  }, []);

  const primePanoramaCrossfade = useCallback(() => {
    const sphere = sphereRef.current;
    const crossfadeSphere = crossfadeSphereRef.current;
    if (
      !sphere ||
      !(sphere.material instanceof THREE.MeshBasicMaterial) ||
      !(crossfadeSphere?.material instanceof THREE.MeshBasicMaterial) ||
      !lastPanoramaLoadRef.current
    ) {
      clearCrossfadeTransition();
      return false;
    }

    const outgoingTexture = sphere.material.map;
    if (!outgoingTexture) {
      clearCrossfadeTransition();
      return false;
    }

    crossfadeSphere.material.map = outgoingTexture;
    crossfadeSphere.material.opacity = 1;
    crossfadeSphere.material.needsUpdate = true;
    const isCachedTexture = [...textureCacheRef.current.values()].includes(outgoingTexture);
    crossfadeRetainedTextureRef.current = isCachedTexture ? null : outgoingTexture;
    isPanoramaTransitioningRef.current = true;
    logCrossfadeDebug("primePanoramaCrossfade", {
      isCachedTexture,
      outgoingTextureUuid: outgoingTexture.uuid,
    });
    return true;
  }, [clearCrossfadeTransition]);

  const resetPanoramaTransitionState = useCallback(() => {
    logCrossfadeDebug("resetPanoramaTransitionState");
    clearMorphTransitionTimeouts();
    clearPanoramaTransitionAnimation();
    clearPanoramaTransitionVisuals();
    clearCrossfadeTransition();
    pendingCameraDrivenArrivalRef.current = null;
    pendingCrossfadeArrivalRef.current = null;
    setPendingPanoramaViewRef.current(null);
    navigationLandingViewRef.current = null;
    isPanoramaTransitioningRef.current = false;
    setIsMorphTransitionActive(false);
  }, [
    clearCrossfadeTransition,
    clearMorphTransitionTimeouts,
    clearPanoramaTransitionAnimation,
    clearPanoramaTransitionVisuals,
  ]);

  useEffect(() => {
    return () => {
      resetPanoramaTransitionState();
    };
  }, [resetPanoramaTransitionState]);

  // CMS data state for conditional visibility
  const [currentCMSRowData, setCurrentCMSRowData] = useState<unknown>(null);
  const [navDropMenu, setNavDropMenu] = useState<{
    x: number;
    y: number;
    hotspotId: string;
    targetPanoramaId: string;
  } | null>(null);
  const [navDropMenuPos, setNavDropMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [backlinkPlacement, setBacklinkPlacement] = useState<BacklinkPlacementContext | null>(null);
  const [specificViewContext, setSpecificViewContext] = useState<{
    hotspotId: string;
    targetPanoramaId: string;
    returnPanoramaId: string | null;
    transitionType: "specific-view" | "morph";
  } | null>(null);
  const navMenuRef = useRef<HTMLDivElement | null>(null);
  const specificViewDialogRef = useRef<HTMLDivElement | null>(null);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    position3D: { x: number; y: number; z: number };
    hotspotId?: string;
  } | null>(null);

  const [contextualPopoverPos, setContextualPopoverPos] = useState<{ x: number; y: number } | null>(null);
  const contextualPopoverPosRef = useRef<{ x: number; y: number } | null>(null);
  const contextualPopoverAnchorRef = useRef<{ hotspotId: string | null }>({ hotspotId: null });


  useEffect(() => {
    contextMenuOpenRef.current = contextMenu !== null;
  }, [contextMenu]);

  // Load current CMS row data when dataset or row changes
  useEffect(() => {
    const loadCurrentRowData = async () => {
      if (!project?.craftspace_id || !activeCMSDatasetId) {
        setCurrentCMSRowData(null);
        return;
      }

      try {
        const { CMSService } = await import("@/services/cmsService");
        // Load full dataset with content from IndexedDB
        const targetDataset = await CMSService.loadCMSDataWithContent(
          activeCMSDatasetId,
          project.craftspace_id,
        );

        if (targetDataset?.data?.rows && targetDataset.data.rows.length > activeCMSRowIndex) {
          const rowData = targetDataset.data.rows[activeCMSRowIndex];
          setCurrentCMSRowData(rowData);
        } else {
          setCurrentCMSRowData(null);
        }
      } catch (error) {
        console.error("Error loading CMS row data:", error);
        setCurrentCMSRowData(null);
      }
    };

    loadCurrentRowData();
  }, [project?.craftspace_id, activeCMSDatasetId, activeCMSRowIndex]);

  useEffect(() => {
    if (!navDropMenu) return;

    const handleWindowClick = (event: MouseEvent) => {
      if (!navMenuRef.current) return;
      if (!navMenuRef.current.contains(event.target as Node)) {
        setNavDropMenu(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setNavDropMenu(null);
      }
    };

    window.addEventListener("mousedown", handleWindowClick);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("mousedown", handleWindowClick);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [navDropMenu]);

  useEffect(() => {
    if (!navDropMenu || !navMenuRef.current || !mountRef.current) {
      setNavDropMenuPos(null);
      return;
    }

    const menu = navMenuRef.current;
    const bounds = mountRef.current.getBoundingClientRect();
    const menuWidth = menu.offsetWidth;
    const menuHeight = menu.offsetHeight;

    const padding = 8;
    const desiredX = navDropMenu.x + 8;
    const desiredY = navDropMenu.y + 12;

    const minX = padding;
    const maxX = bounds.width - menuWidth - padding;
    const minY = padding;
    const maxY = bounds.height - menuHeight - padding;

    const clampedX = Math.min(Math.max(desiredX, minX), maxX);
    const clampedY = Math.min(Math.max(desiredY, minY), maxY);

    setNavDropMenuPos({ x: clampedX, y: clampedY });
  }, [navDropMenu]);

  // Filter hotspots for the current panorama
  const currentHotspots = useMemo(() => {
    if (!panorama) return [];
    return hotspots
      .filter((h) => h.panorama_id === panorama.id)
      .sort(compareHotspotsBySidebarOrder);
  }, [panorama, hotspots]);

  // Function to regenerate all hotspot SVGs when CSS variables change
  const regenerateAllHotspotSVGs = () => {
    console.log("🔍 CSS Variable change detected - regenerating all hotspot SVGs");

    currentHotspots.forEach((hotspot) => {
      if (hotspot.type === "svg" && hotspot.content) {
        updateHotspotFromViewer(hotspot.id, {
          content: {
            ...hotspot.content,
            _forceRegenerate: Date.now(),
          },
        });
      }
    });
  };

  // Watch for CSS variable changes globally
  useCSSVariableWatcher(routeProjectId, regenerateAllHotspotSVGs);

  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sphereRef = useRef<THREE.Mesh | null>(null);
  const crossfadeSphereRef = useRef<THREE.Mesh | null>(null);
  const fadeOverlaySphereRef = useRef<THREE.Mesh | null>(null);
  const hotspotManagerRef = useRef<HotspotManager | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Camera control state
  const isDraggingRef = useRef(false);
  const wasDraggingRef = useRef(false);
  const lastPointerXRef = useRef(0);
  const lastPointerYRef = useRef(0);
  const pendingDragDeltaRef = useRef({ x: 0, y: 0 });
  const smoothedDragDeltaRef = useRef({ x: 0, y: 0 });
  const isHotspotDraggingRef = useRef(false);
  const hotspotDragIdRef = useRef<string | null>(null);
  const hotspotDragPositionRef = useRef<{ x: number; y: number; z: number } | null>(null);
  const hotspotDragStartRef = useRef<{ x: number; y: number } | null>(null);
  const isHotspotResizingRef = useRef(false);
  const hotspotResizeRef = useRef<ResizeDragState | null>(null);
  const isHotspotRotatingRef = useRef(false);
  const hotspotRotateRef = useRef<RotateDragState | null>(null);
  const isPlacementDraggingRef = useRef(false);
  const placementDragRef = useRef<PlacementDragState | null>(null);
  const suppressClickRef = useRef(false);
  const [placementRect, setPlacementRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const selectionOverlayRef = useRef<HTMLDivElement | null>(null);
  const selectionOutlineRef = useRef<SVGPolygonElement | null>(null);
  const updateSelectionOverlayFnRef = useRef<() => void>(() => {});
  const hideSelectionOverlayFnRef = useRef<() => void>(() => {});
  // Cache of decoded THREE.Texture instances keyed by URL — avoids re-decoding on panorama switch
  const textureCacheRef = useRef<Map<string, THREE.Texture>>(new Map());
  const panoramaLoadRequestIdRef = useRef(0);

  const selectionHandleRefs = useRef<Record<ResizeHandle, HTMLButtonElement | null>>({
    n: null,
    s: null,
    e: null,
    w: null,
    ne: null,
    nw: null,
    se: null,
    sw: null,
  });

  // Camera stats state
  const [cameraStats, setCameraStats] = useState({
    yaw: 0,
    pitch: 0,
    roll: 0,
    fov: 75,
  });
  const slideToDebugRef = useRef<SlideToDebugStats | null>(null);
  const [slideToDebugStats, setSlideToDebugStats] = useState<SlideToDebugStats | null>(null);
  const slideToResolutionRef = useRef<{
    targetPanoramaId: string | null;
    resolvedFrom: SlideToLandingSource;
    view: PanoramaView | null;
  } | null>(null);

  // State
  const [isTextureLoading, setIsTextureLoading] = useState(false);
  const [panoramaLoadFailed, setPanoramaLoadFailed] = useState(false);
  const [panoramaLoadAttempt, setPanoramaLoadAttempt] = useState(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dragType, setDragType] = useState<
    "panorama" | "component" | "hotspot-add" | "hotspot-relocate" | null
  >(null);
  const [showMapOverlay, setShowMapOverlay] = useState(false);
  const [isDraggingRadar, setIsDraggingRadar] = useState(false);
  const [tempNorthOffset, setTempNorthOffset] = useState<number | null>(null);
  const mapOverlayRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const radarOverlayRef = useRef<HTMLDivElement>(null);
  const mapMarkerRef = useRef<unknown>(null);
  const radarPositionRef = useRef<{ x: number; y: number } | null>(null);
  const radarDragStartRef = useRef<{
    x: number;
    y: number;
    initialNorthOffset: number;
    initialYaw: number;
  } | null>(null);
  const tempNorthOffsetRef = useRef<number | null>(null);
  const isDraggingRadarRef = useRef(false);
  const overlayMarkersRef = useRef<Map<string, unknown>>(new Map());
  const overlayConnectionsRef = useRef<Map<string, unknown>>(new Map());

  // Raycaster for hotspot placement
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());

  // Track when a hotspot was just placed to prevent camera focus
  const lastPlacementModeRef = useRef(false);
  const justPlacedHotspotRef = useRef<string | null>(null);

  // ============================================================================
  // NEW 360 VIEWER IMPLEMENTATION
  // ============================================================================

  // Camera focus function - directly look at hotspot
  const focusCameraOnHotspot = useCallback((hotspot: Hotspot) => {
    if (!cameraRef.current || !hotspot?.position) return;

    const position = hotspot.position as { x?: number; y?: number; z?: number } | null;
    focusCameraOnPosition(cameraRef.current, {
      x: position?.x || 0,
      y: position?.y || 0,
      z: position?.z || 0,
    });
  }, []);

  const setSelectionHandleRef = useCallback(
    (handle: ResizeHandle) => (node: HTMLButtonElement | null) => {
      selectionHandleRefs.current[handle] = node;
    },
    [],
  );

  const hideSelectionOverlay = useCallback(() => {
    const overlay = selectionOverlayRef.current;
    if (!overlay) return;
    overlay.style.display = "none";
  }, []);

  const projectToScreen = useCallback(
    (point: THREE.Vector3, camera: THREE.PerspectiveCamera, container: HTMLElement) => {
      const projected = point.clone().project(camera);
      if (
        !Number.isFinite(projected.x) ||
        !Number.isFinite(projected.y) ||
        !Number.isFinite(projected.z)
      ) {
        return null;
      }

      if (projected.z < -1 || projected.z > 1) {
        return null;
      }

      return {
        x: ((projected.x + 1) * container.clientWidth) / 2,
        y: ((1 - projected.y) * container.clientHeight) / 2,
      };
    },
    [],
  );

  const getSelectedHotspotFrame = useCallback((): HotspotFrame | null => {
    const selected = selectedHotspotRef.current;
    const camera = cameraRef.current;
    const container = mountRef.current;
    const hotspotManager = hotspotManagerRef.current;

    if (!selected || !camera || !container || !hotspotManager) return null;
    if (isPreviewModeRef.current || placementModeRef.current) return null;

    let centerWorld: THREE.Vector3 | null = null;
    let xAxisWorld: THREE.Vector3 | null = null;
    let yAxisWorld: THREE.Vector3 | null = null;
    let halfWidthWorld = 0;
    let halfHeightWorld = 0;

    const scale = selected.scale as { x?: number; y?: number } | null;
    const scaleX = Number.isFinite(scale?.x) ? (scale?.x as number) : 1;
    const scaleY = Number.isFinite(scale?.y) ? (scale?.y as number) : 1;

    if (selected.type === "html") {
      const htmlObject = hotspotManager.getHTMLHotspots()[selected.id];
      if (!htmlObject) return null;

      const element = htmlObject.element as HTMLElement | undefined;
      if (!element) return null;

      const baseWidth = Math.max(1, element.offsetWidth || element.clientWidth || 1);
      const baseHeight = Math.max(1, element.offsetHeight || element.clientHeight || 1);
      const worldScale = htmlObject.scale;

      centerWorld = htmlObject.getWorldPosition(new THREE.Vector3());
      const quaternion = htmlObject.getWorldQuaternion(new THREE.Quaternion());
      xAxisWorld = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion).normalize();
      yAxisWorld = new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion).normalize();
      halfWidthWorld = (baseWidth * worldScale.x) / 2;
      halfHeightWorld = (baseHeight * worldScale.y) / 2;
    } else {
      const mesh = hotspotManager.getHotspotMeshes()[selected.id];
      if (!mesh) return null;

      centerWorld = mesh.getWorldPosition(new THREE.Vector3());
      xAxisWorld = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion).normalize();
      yAxisWorld = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion).normalize();

      const baseScaleX = mesh.userData?.baseScaleX || mesh.scale.x / Math.max(scaleX, 0.001) || 40;
      const baseScaleY = mesh.userData?.baseScaleY || mesh.scale.y / Math.max(scaleY, 0.001) || 40;
      halfWidthWorld = (baseScaleX * scaleX) / 2;
      halfHeightWorld = (baseScaleY * scaleY) / 2;
    }

    if (!centerWorld || !xAxisWorld || !yAxisWorld || halfWidthWorld <= 0 || halfHeightWorld <= 0) {
      return null;
    }

    const cameraDirection = new THREE.Vector3();
    camera.getWorldDirection(cameraDirection);
    const toHotspot = centerWorld.clone().sub(camera.position).normalize();
    if (cameraDirection.dot(toHotspot) <= 0) {
      return null;
    }

    const top = centerWorld.clone().add(yAxisWorld.clone().multiplyScalar(halfHeightWorld));
    const bottom = centerWorld.clone().add(yAxisWorld.clone().multiplyScalar(-halfHeightWorld));
    const left = xAxisWorld.clone().multiplyScalar(-halfWidthWorld);
    const right = xAxisWorld.clone().multiplyScalar(halfWidthWorld);

    const nwWorld = top.clone().add(left);
    const neWorld = top.clone().add(right);
    const seWorld = bottom.clone().add(right);
    const swWorld = bottom.clone().add(left);

    const centerScreen = projectToScreen(centerWorld, camera, container);
    const nw = projectToScreen(nwWorld, camera, container);
    const ne = projectToScreen(neWorld, camera, container);
    const se = projectToScreen(seWorld, camera, container);
    const sw = projectToScreen(swWorld, camera, container);

    if (!centerScreen || !nw || !ne || !se || !sw) {
      return null;
    }

    const xs = [nw.x, ne.x, se.x, sw.x];
    const ys = [nw.y, ne.y, se.y, sw.y];
    const fullyOutsideViewport =
      Math.max(...xs) < 0 ||
      Math.min(...xs) > container.clientWidth ||
      Math.max(...ys) < 0 ||
      Math.min(...ys) > container.clientHeight;
    if (fullyOutsideViewport) {
      return null;
    }

    const xAxisScreenPoint = projectToScreen(
      centerWorld.clone().add(xAxisWorld.clone()),
      camera,
      container,
    );
    const yAxisScreenPoint = projectToScreen(
      centerWorld.clone().add(yAxisWorld.clone()),
      camera,
      container,
    );
    if (!xAxisScreenPoint || !yAxisScreenPoint) {
      return null;
    }

    return {
      centerWorld,
      xAxisWorld,
      yAxisWorld,
      halfWidthWorld,
      halfHeightWorld,
      centerScreen,
      cornersScreen: [nw, ne, se, sw],
      xScreenVector: {
        x: xAxisScreenPoint.x - centerScreen.x,
        y: xAxisScreenPoint.y - centerScreen.y,
      },
      yScreenVector: {
        x: yAxisScreenPoint.x - centerScreen.x,
        y: yAxisScreenPoint.y - centerScreen.y,
      },
    };
  }, [projectToScreen]);

  const applyPlacementScaleToSelected = useCallback(
    (targetWidth: number, targetHeight: number) => {
      if (targetWidth <= 0 || targetHeight <= 0) return;
      let attempts = 0;

      const tryApply = () => {
        attempts += 1;
        const currentState = useEditorStore.getState();
        const selected = currentState.selectedHotspot;
        const frame = getSelectedHotspotFrame();

        if (!selected || !frame) {
          if (attempts < 10) requestAnimationFrame(tryApply);
          return;
        }

        const [nw, ne, _se, sw] = frame.cornersScreen;
        const currentWidth = Math.hypot(ne.x - nw.x, ne.y - nw.y);
        const currentHeight = Math.hypot(sw.x - nw.x, sw.y - nw.y);

        if (currentWidth < 1 || currentHeight < 1) {
          if (attempts < 10) requestAnimationFrame(tryApply);
          return;
        }

        const currentScale = selected.scale as { x?: number; y?: number; z?: number } | null;
        const baseScale = {
          x: Number.isFinite(currentScale?.x) ? (currentScale?.x as number) : 1,
          y: Number.isFinite(currentScale?.y) ? (currentScale?.y as number) : 1,
          z: Number.isFinite(currentScale?.z) ? (currentScale?.z as number) : 1,
        };

        const nextScale = {
          x: Math.max(0.05, baseScale.x * (targetWidth / currentWidth)),
          y: Math.max(0.05, baseScale.y * (targetHeight / currentHeight)),
          z: baseScale.z,
        };

        if (!Number.isFinite(nextScale.x) || !Number.isFinite(nextScale.y)) {
          return;
        }

        currentState
          .updateHotspotFromViewer(
            selected.id,
            { scale: nextScale },
            { historyBeforeOverride: { scale: baseScale } },
          )
          .catch((error) => {
            console.error("Error updating hotspot scale:", error);
            toastRef.current({
              variant: "destructive",
              title: "Error resizing hotspot",
              description: "Failed to update hotspot scale.",
            });
          });
      };

      requestAnimationFrame(tryApply);
    },
    [getSelectedHotspotFrame],
  );

  const updateContextualPopoverPos = useCallback(
    (nextPos: { x: number; y: number } | null) => {
      const prev = contextualPopoverPosRef.current;
      if (!nextPos) {
        if (prev) {
          contextualPopoverPosRef.current = null;
          setContextualPopoverPos(null);
        }
        return;
      }
      if (!prev || Math.hypot(prev.x - nextPos.x, prev.y - nextPos.y) > 0.5) {
        contextualPopoverPosRef.current = nextPos;
        setContextualPopoverPos(nextPos);
      }
    },
    [],
  );

  const updateContextualPopoverPosition = useCallback(() => {
    const anchorId = contextualPopoverAnchorRef.current.hotspotId;
    const selected = selectedHotspotRef.current;
    if (!anchorId || !selected || selected.id !== anchorId) return;
    const frame = getSelectedHotspotFrame();
    if (!frame) {
      updateContextualPopoverPos(null);
      return;
    }
    const xs = frame.cornersScreen.map((point) => point.x);
    const rightX = Math.max(...xs);
    const nextPos = { x: rightX + 12, y: frame.centerScreen.y };
    updateContextualPopoverPos(nextPos);
  }, [getSelectedHotspotFrame, updateContextualPopoverPos]);

  useEffect(() => {
    showContextualPopoverRef.current = (x: number, y: number) => {
      const selected = selectedHotspotRef.current;
      contextualPopoverAnchorRef.current = { hotspotId: selected?.id || null };
      if (selected) {
        updateContextualPopoverPosition();
      } else {
        updateContextualPopoverPos({ x, y });
      }
    };
  }, [updateContextualPopoverPos, updateContextualPopoverPosition]);

  useEffect(() => {
    if (!selectedHotspot) {
      contextualPopoverAnchorRef.current = { hotspotId: null };
      updateContextualPopoverPos(null);
    }
  }, [selectedHotspot, updateContextualPopoverPos]);

  const updateSelectionOverlay = useCallback(() => {
    const overlay = selectionOverlayRef.current;
    const outline = selectionOutlineRef.current;
    if (!overlay || !outline) return;

    const frame = getSelectedHotspotFrame();
    if (!frame) {
      overlay.style.display = "none";
      return;
    }

    overlay.style.display = "block";

    const [nw, ne, se, sw] = frame.cornersScreen;
    const centerX = (nw.x + ne.x + se.x + sw.x) / 4;
    const centerY = (nw.y + ne.y + se.y + sw.y) / 4;
    const orderedCorners = [nw, ne, se, sw].sort(
      (a, b) => Math.atan2(a.y - centerY, a.x - centerX) - Math.atan2(b.y - centerY, b.x - centerX),
    );

    outline.setAttribute(
      "points",
      orderedCorners.map((point) => `${point.x},${point.y}`).join(" "),
    );

    const handlePositions: Record<ResizeHandle, ScreenPoint> = {
      n: { x: (nw.x + ne.x) / 2, y: (nw.y + ne.y) / 2 },
      s: { x: (sw.x + se.x) / 2, y: (sw.y + se.y) / 2 },
      e: { x: (ne.x + se.x) / 2, y: (ne.y + se.y) / 2 },
      w: { x: (nw.x + sw.x) / 2, y: (nw.y + sw.y) / 2 },
      ne,
      nw,
      se,
      sw,
    };

    // Hide handles if hotspot is being dragged/repositioned or camera is being panned
    const isHotspotBeingDragged = isHotspotDraggingRef.current || isHotspotResizingRef.current || isHotspotRotatingRef.current;
    const isCameraBeingPanned = isDraggingRef.current && !isHotspotBeingDragged;

    // Get container bounds for clamping
    const container = mountRef.current;
    const containerRect = container ? container.getBoundingClientRect() : null;
    // Keep handles fully visible (w-4 h-4 with border-2 => ~20px total)
    const handleHalfSize = 10;

    RESIZE_HANDLES.forEach((handle) => {
      const element = selectionHandleRefs.current[handle];
      if (!element) return;
      const position = handlePositions[handle];

      // Clamp position to stay within container bounds
      let clampedX = position.x;
      let clampedY = position.y;

      if (containerRect) {
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        // Keep handles fully inside the container bounds
        clampedX = Math.max(handleHalfSize, Math.min(containerWidth - handleHalfSize, position.x));
        clampedY = Math.max(handleHalfSize, Math.min(containerHeight - handleHalfSize, position.y));
      }

      element.style.left = `${clampedX}px`;
      element.style.top = `${clampedY}px`;
      element.style.display = isHotspotBeingDragged || isCameraBeingPanned ? "none" : "block";
    });
  }, [getSelectedHotspotFrame]);

  const handleResizePointerDown = useCallback(
    (handle: ResizeHandle, e: ReactPointerEvent<HTMLButtonElement>) => {
      const selected = selectedHotspotRef.current;
      const hotspotManager = hotspotManagerRef.current;
      const container = mountRef.current;
      if (!selected || !hotspotManager || !container || isPreviewModeRef.current) return;

      const frame = getSelectedHotspotFrame();
      if (!frame) return;

      e.stopPropagation();
      e.preventDefault();

      // Check for Shift key to activate rotation mode (only in 3D mode)
      const isRotationMode = e.shiftKey && getHotspotFacingMode(selected) === "3d";

      if (!isRotationMode) {
        const scale = selected.scale as { x?: number; y?: number; z?: number } | null;
        const startScale = {
          x: Number.isFinite(scale?.x) ? (scale?.x as number) : 1,
          y: Number.isFinite(scale?.y) ? (scale?.y as number) : 1,
          z: Number.isFinite(scale?.z) ? (scale?.z as number) : 1,
        };

        const baseWidth = (frame.halfWidthWorld * 2) / Math.max(startScale.x, 0.001);
        const baseHeight = (frame.halfHeightWorld * 2) / Math.max(startScale.y, 0.001);
        const xMagnitude = Math.hypot(frame.xScreenVector.x, frame.xScreenVector.y);
        const yMagnitude = Math.hypot(frame.yScreenVector.x, frame.yScreenVector.y);

        if (xMagnitude < 0.001 || yMagnitude < 0.001 || baseWidth <= 0 || baseHeight <= 0) {
          return;
        }

        const selectedContent =
          selected.content && typeof selected.content === "object"
            ? ({ ...(selected.content as HotspotContentRecord) } as HotspotContentRecord)
            : ({} as HotspotContentRecord);
        const isHtmlResize = selected.type === "html";
        const authoredWidthPx = parseStrictPx(selectedContent.width);
        const authoredHeightPx = parseStrictPx(selectedContent.height);
        const normalizedStartWidthPx = Math.max(
          1,
          Math.round(
            (authoredWidthPx ?? baseWidth) *
              Math.max(startScale.x, 0.001),
          ),
        );
        const normalizedStartHeightPx = Math.max(
          1,
          Math.round(
            (authoredHeightPx ?? baseHeight) *
              Math.max(startScale.y, 0.001),
          ),
        );

        isHotspotResizingRef.current = true;
        hotspotResizeRef.current = {
          hotspotId: selected.id,
          handle,
          isHtml: isHtmlResize,
          startPointer: { x: e.clientX, y: e.clientY },
          startScale,
          startHalfWidth: frame.halfWidthWorld,
          startHalfHeight: frame.halfHeightWorld,
          baseWidth,
          baseHeight,
          startContentWidthPx: normalizedStartWidthPx,
          startContentHeightPx: normalizedStartHeightPx,
          startFontSizePx: parseStrictPx(selectedContent.fontSize),
          textResizeMode: resolveTextResizeMode(selectedContent.textResizeMode),
          startContentSnapshot: selectedContent,
          xPixelsPerWorld: xMagnitude,
          yPixelsPerWorld: yMagnitude,
          xScreenUnit: {
            x: frame.xScreenVector.x / xMagnitude,
            y: frame.xScreenVector.y / xMagnitude,
          },
          yScreenUnit: {
            x: frame.yScreenVector.x / yMagnitude,
            y: frame.yScreenVector.y / yMagnitude,
          },
          aspectRatio: frame.halfHeightWorld > 0 ? frame.halfWidthWorld / frame.halfHeightWorld : 1,
          lastScale: startScale,
          lastHtmlContent: null,
          lastHtmlDispatchAtMs: 0,
        };

        hotspotManager.setTransformingState(true, selected.id);
        container.style.cursor = RESIZE_CURSOR_BY_HANDLE[handle];
        document.body.style.cursor = RESIZE_CURSOR_BY_HANDLE[handle];
      } else {
        // Rotation mode - activated with Shift key (only in 3D mode)
        const rotation = selected.rotation as { x?: number; y?: number; z?: number } | null;
        const startRotation = {
          x: rotation?.x ?? 0,
          y: rotation?.y ?? 0,
          z: rotation?.z ?? 0,
        };

        isHotspotRotatingRef.current = true;
        hotspotRotateRef.current = {
          hotspotId: selected.id,
          axis: ROTATE_AXIS_BY_HANDLE[handle],
          startPointer: { x: e.clientX, y: e.clientY },
          startRotation,
        };

        hotspotManager.setTransformingState(true, selected.id);
        container.style.cursor = ROTATE_CURSOR;
        document.body.style.cursor = ROTATE_CURSOR;
      }

      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [getSelectedHotspotFrame],
  );

  useEffect(() => {
    updateSelectionOverlayFnRef.current = updateSelectionOverlay;
  }, [updateSelectionOverlay]);

  useEffect(() => {
    hideSelectionOverlayFnRef.current = hideSelectionOverlay;
  }, [hideSelectionOverlay]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    const container = mountRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Create scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Create camera at origin with YXZ rotation order
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.set(0, 0, 0);
    camera.rotation.order = "YXZ";
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.top = "0";
    renderer.domElement.style.left = "0";
    renderer.domElement.style.zIndex = "0";

    // Attach drag-drop handlers to canvas element for hotspot-add drops
    const handleCanvasDragOver = (e: DragEvent) => {
      if (
        e.dataTransfer?.types.includes("application/x-hotspot-add") ||
        e.dataTransfer?.types.includes("application/x-hotspot-relocate")
      ) {
        e.preventDefault();
        e.dataTransfer.dropEffect = e.dataTransfer.types.includes("application/x-hotspot-relocate")
          ? "move"
          : "copy";
      }
    };

    const handleCanvasDrop = async (e: DragEvent) => {
      const hotspotType = e.dataTransfer?.getData("application/x-hotspot-add");
      const relocateHotspotId = e.dataTransfer?.getData("application/x-hotspot-relocate");
      if (!hotspotType && !relocateHotspotId) return;

      e.preventDefault();
      e.stopPropagation();

      const currentState = useEditorStore.getState();
      const currentPanorama = currentState.selectedPanorama;
      const currentUser = getPlacementUser();

      if (!currentPanorama || !cameraRef.current || !sphereRef.current || !currentUser) return;

      const rect = renderer.domElement.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(new THREE.Vector2(nx, ny), cameraRef.current);
      const intersects = raycaster.intersectObject(sphereRef.current, false);

      const position = intersects.length > 0
        ? { x: intersects[0].point.x, y: intersects[0].point.y, z: intersects[0].point.z }
        : { x: 0, y: 0, z: -400 };

      try {
        if (relocateHotspotId) {
          currentState.setPendingHotspotPlacementId(relocateHotspotId);
          currentState.setPlacementMode(true);
          currentState.setIsDraggingToAddHotspot(false);
          await currentState.requestHotspotPlacement(position, currentUser);
          return;
        }
        await currentState.requestHotspotPlacement(position, currentUser);
        showContextualPopoverRef.current?.(e.clientX - rect.left, e.clientY - rect.top);
      } catch (error) {
        console.error("Error placing hotspot via drag:", error);
      }
    };

    renderer.domElement.addEventListener("dragover", handleCanvasDragOver);
    renderer.domElement.addEventListener("drop", handleCanvasDrop);

    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create sphere with placeholder material
    const sphereGeometry = new THREE.SphereGeometry(500, 60, 40);
    const placeholderMaterial = new THREE.MeshBasicMaterial({
      color: 0x333333,
      side: THREE.BackSide,
    });
    const sphere = new THREE.Mesh(sphereGeometry, placeholderMaterial);
    sphere.rotation.y = Math.PI / 2; // Align front to yaw = 0
    sphere.renderOrder = 1;
    scene.add(sphere);
    sphereRef.current = sphere;

    const crossfadeMaterial = new THREE.MeshBasicMaterial({
      map: null,
      transparent: true,
      opacity: 0,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
    });
    const crossfadeSphere = new THREE.Mesh(sphereGeometry, crossfadeMaterial);
    crossfadeSphere.rotation.y = Math.PI / 2;
    crossfadeSphere.renderOrder = 2;
    scene.add(crossfadeSphere);
    crossfadeSphereRef.current = crossfadeSphere;

    const fadeOverlayMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
    });
    const fadeOverlaySphere = new THREE.Mesh(sphereGeometry, fadeOverlayMaterial);
    fadeOverlaySphere.rotation.y = Math.PI / 2;
    fadeOverlaySphere.renderOrder = 3;
    scene.add(fadeOverlaySphere);
    fadeOverlaySphereRef.current = fadeOverlaySphere;

    // Initialize hotspot manager
    hotspotManagerRef.current = new HotspotManager(scene, container);
    hotspotManagerRef.current.setPreviewMode(isPreviewModeRef.current);
    hotspotManagerRef.current.setCurrentRoute(currentRouteRef.current);
    hotspotManagerRef.current.setVideoPlaybackActive(currentRouteRef.current === "editor");

    const hotspotToWorldPosition = (hotspot: Hotspot) => {
      const position = hotspot.position as { x?: number; y?: number; z?: number } | null;
      let x = position?.x || 0;
      let y = position?.y || 0;
      let z = position?.z || -400;

      if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(z)) {
        return null;
      }

      const distance = Math.sqrt(x * x + y * y + z * z);
      // Keep hotspot world radius normalization identical in edit and preview,
      // so slide-to transition math resolves against the same coordinates.
      const minRadius = 300;
      const maxRadius = 400;

      if (distance > 0 && (distance < minRadius || distance > maxRadius)) {
        const direction = new THREE.Vector3(x, y, z).normalize();
        const targetRadius = 400;
        const newPosition = direction.multiplyScalar(targetRadius);
        x = newPosition.x;
        y = newPosition.y;
        z = newPosition.z;
      }

      return new THREE.Vector3(x, y, z);
    };

    const pickHotspotFromPointer = (
      clientX: number,
      clientY: number,
      currentState: EditorState,
    ) => {
      if (!camera || !currentState.selectedPanorama) return null;

      const currentPanoramaId = currentState.selectedPanorama.id;
      const maxDistancePx = 56;
      const bounds = container.getBoundingClientRect();
      const orderedPanoramaHotspots = currentState.hotspots
        .filter((hotspot) => hotspot.panorama_id === currentPanoramaId && hotspot.visible !== false)
        .sort(compareHotspotsBySidebarOrder);
      const stackPriorityById = new Map(
        orderedPanoramaHotspots.map((hotspot, index) => [
          hotspot.id,
          orderedPanoramaHotspots.length - index,
        ]),
      );
      let bestHit: { id: string; distance: number; stackPriority: number } | null = null;
      const htmlHotspotMap = hotspotManagerRef.current?.getHTMLHotspots() || {};

      for (const hotspot of orderedPanoramaHotspots) {
        const stackPriority = stackPriorityById.get(hotspot.id) ?? 0;

        const worldPosition = hotspotToWorldPosition(hotspot);
        if (!worldPosition) continue;
        const cameraDirection = new THREE.Vector3();
        camera.getWorldDirection(cameraDirection);
        const toHotspot = worldPosition.clone().sub(camera.position).normalize();
        if (cameraDirection.dot(toHotspot) <= 0) {
          continue;
        }

        if (hotspot.type === "html") {
          const htmlObject = htmlHotspotMap[hotspot.id];
          const element = htmlObject?.element as HTMLElement | undefined;
          if (element) {
            const rect = element.getBoundingClientRect();
            const expandedRect = {
              left: rect.left - 6,
              right: rect.right + 6,
              top: rect.top - 6,
              bottom: rect.bottom + 6,
            };

            const isInsideRect =
              clientX >= expandedRect.left &&
              clientX <= expandedRect.right &&
              clientY >= expandedRect.top &&
              clientY <= expandedRect.bottom;

            if (isInsideRect) {
              const centerX = (rect.left + rect.right) / 2;
              const centerY = (rect.top + rect.bottom) / 2;
              const distance = Math.hypot(clientX - centerX, clientY - centerY);
              if (
                !bestHit ||
                stackPriority > bestHit.stackPriority ||
                (stackPriority === bestHit.stackPriority && distance < bestHit.distance)
              ) {
                bestHit = { id: hotspot.id, distance, stackPriority };
              }
              continue;
            }
          }
        }

        const projected = worldPosition.clone().project(camera);
        if (
          !Number.isFinite(projected.x) ||
          !Number.isFinite(projected.y) ||
          projected.z < -1 ||
          projected.z > 1
        ) {
          continue;
        }

        const screenX = ((projected.x + 1) / 2) * bounds.width + bounds.left;
        const screenY = ((1 - projected.y) / 2) * bounds.height + bounds.top;
        const distance = Math.hypot(clientX - screenX, clientY - screenY);

        if (distance <= maxDistancePx) {
          if (
            !bestHit ||
            stackPriority > bestHit.stackPriority ||
            (stackPriority === bestHit.stackPriority && distance < bestHit.distance)
          ) {
            bestHit = { id: hotspot.id, distance, stackPriority };
          }
        }
      }

      return bestHit?.id || null;
    };

    // Expose to handleContextMenu (outside useEffect)
    pickHotspotAtPointRef.current = (clientX: number, clientY: number) =>
      pickHotspotFromPointer(clientX, clientY, useEditorStore.getState());

    const pickHotspotIdFromIntersects = (
      intersects: THREE.Intersection<THREE.Object3D>[],
      currentState: EditorState,
    ): string | null => {
      const selectedPanoramaId = currentState.selectedPanorama?.id;
      if (!selectedPanoramaId || intersects.length === 0) {
        return null;
      }

      const orderedPanoramaHotspots = currentState.hotspots
        .filter(
          (hotspot) => hotspot.panorama_id === selectedPanoramaId && hotspot.visible !== false,
        )
        .sort(compareHotspotsBySidebarOrder);
      const stackPriorityById = new Map(
        orderedPanoramaHotspots.map((hotspot, index) => [
          hotspot.id,
          orderedPanoramaHotspots.length - index,
        ]),
      );

      let best: { id: string; stackPriority: number; distance: number } | null = null;
      for (const intersect of intersects) {
        const id = intersect.object.userData?.id;
        if (typeof id !== "string") continue;
        const stackPriority = stackPriorityById.get(id) ?? 0;
        if (
          !best ||
          stackPriority > best.stackPriority ||
          (stackPriority === best.stackPriority && intersect.distance < best.distance)
        ) {
          best = { id, stackPriority, distance: intersect.distance };
        }
      }

      return best?.id || null;
    };

    const getSphereIntersection = (clientX: number, clientY: number) => {
      if (!camera || !sphereRef.current) return null;
      const rect = container.getBoundingClientRect();
      const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(new THREE.Vector2(nx, ny), camera);
      const intersects = raycaster.intersectObject(sphereRef.current, false);
      if (intersects.length === 0) return null;
      return {
        position: intersects[0].point,
        containerPoint: { x: clientX - rect.left, y: clientY - rect.top },
      };
    };

    // Pointer controls for camera rotation
    const startHotspotDrag = (e: PointerEvent) => {
      if (e.button !== 0) return false;
      if (isHotspotResizingRef.current) return false;
      if (isPreviewModeRef.current) return false;

      const currentState = useEditorStore.getState();
      if (currentState.placementMode) return false;

      // Lock hotspots in skin editor when not in preview mode
      if (currentRouteRef.current === "skin" && !isPreviewModeRef.current) return false;

      if (!hotspotManagerRef.current || !camera) return false;

      let hotspotId: string | null = null;

      const target = e.target as HTMLElement | null;
      if (target?.closest?.("[data-hotspot-resize-handle='true']")) {
        return false;
      }
      const htmlHotspotEl = target?.closest?.(".html-hotspot") as HTMLElement | null;
      if (htmlHotspotEl?.dataset?.hotspotId) {
        hotspotId = htmlHotspotEl.dataset.hotspotId;
      }

      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      if (!hotspotId) {
        const hotspotMeshes = hotspotManagerRef.current.getHotspotMeshes();
        const hotspotObjects = Object.values(hotspotMeshes);
        if (hotspotObjects.length > 0) {
          const raycaster = raycasterRef.current;
          raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
          const hotspotIntersects = raycaster.intersectObjects(hotspotObjects, false);
          if (hotspotIntersects.length > 0) {
            hotspotId = pickHotspotIdFromIntersects(hotspotIntersects, currentState);
          }
        }
        if (!hotspotId) return false;
      }

      const hotspot = currentState.hotspots.find((h) => h.id === hotspotId);
      if (!hotspot) return false;

      // Check if this hotspot is already selected
      const isAlreadySelected = selectedHotspotRef.current?.id === hotspotId;

      // If not selected, just select it - don't start dragging yet
      if (!isAlreadySelected) {
        currentState.setSelectedHotspot(hotspot);
        return true; // Prevent camera panning when clicking on hotspot
      }

      const isRotationMode = e.shiftKey && getHotspotFacingMode(hotspot) === "3d";

      if (isRotationMode) {
        const rotation = hotspot.rotation as { x?: number; y?: number; z?: number } | null;
        const startRotation = {
          x: rotation?.x ?? 0,
          y: rotation?.y ?? 0,
          z: rotation?.z ?? 0,
        };

        isHotspotRotatingRef.current = true;
        hotspotRotateRef.current = {
          hotspotId,
          axis: "free",
          startPointer: { x: e.clientX, y: e.clientY },
          startRotation,
        };

        hotspotManagerRef.current.setTransformingState(true, hotspotId);
        container.style.cursor = ROTATE_CURSOR;
        document.body.style.cursor = ROTATE_CURSOR;
        return true;
      }

      // If already selected, now we can start dragging
      isHotspotDraggingRef.current = true;
      hotspotDragIdRef.current = hotspotId;
      hotspotDragPositionRef.current =
        (hotspot.position as { x: number; y: number; z: number }) || null;
      hotspotDragStartRef.current = { x: e.clientX, y: e.clientY };
      hotspotManagerRef.current.setDraggingState(true, hotspotId);
      container.style.cursor = "grabbing";
      return true;
    };

    const startPlacementDrag = (e: PointerEvent) => {
      const currentState = useEditorStore.getState();
      if (!currentState.placementMode) return false;
      if (isPreviewModeRef.current) return false;
      const intersection = getSphereIntersection(e.clientX, e.clientY);
      if (!intersection) return false;

      isPlacementDraggingRef.current = true;
      placementDragRef.current = {
        startPointer: { x: e.clientX, y: e.clientY },
        currentPointer: { x: e.clientX, y: e.clientY },
      };
      setPlacementRect({
        x: intersection.containerPoint.x,
        y: intersection.containerPoint.y,
        width: 0,
        height: 0,
      });
      wasDraggingRef.current = false;
      container.style.cursor = "crosshair";
      return true;
    };

    const isInsideContextualPopover = (e: MouseEvent | PointerEvent) =>
      e.composedPath().some(
        (el) => (el as HTMLElement).dataset?.contextualPopover === "true",
      );

    const isInsideNavDropMenu = (e: MouseEvent | PointerEvent) =>
      !!navMenuRef.current && e.composedPath().some((el) => el === navMenuRef.current);
    const isInsideSpecificViewDialog = (e: MouseEvent | PointerEvent) =>
      !!specificViewDialogRef.current &&
      e.composedPath().some((el) => el === specificViewDialogRef.current);

    const handlePointerDown = (e: PointerEvent) => {
      if (isPanoramaTransitioningRef.current) return;
      if (e.button !== 0) return;
      if (contextMenuOpenRef.current) return;
      if (isInsideNavDropMenu(e)) return;
      if (isInsideSpecificViewDialog(e)) return;
      if (isInsideContextualPopover(e)) return;
      if (isHotspotResizingRef.current) return;
      if (getIsSkinComponentResizing()) return;
      if (e.metaKey || e.ctrlKey) return;
      // Let React handle clicks on skin components so they can be selected
      if ((e.target as HTMLElement).closest("[data-skin-component]")) return;

      if (!container.hasPointerCapture(e.pointerId)) {
        container.setPointerCapture(e.pointerId);
      }
      if (startPlacementDrag(e)) return;
      if (startHotspotDrag(e)) return;

      isDraggingRef.current = true;
      wasDraggingRef.current = false;
      lastPointerXRef.current = e.clientX;
      lastPointerYRef.current = e.clientY;
      pendingDragDeltaRef.current = { x: 0, y: 0 };
      smoothedDragDeltaRef.current = { x: 0, y: 0 };
      container.style.cursor = "grabbing";
    };

    const handlePointerMove = (e: PointerEvent) => {
      const isPrimaryButtonDown = (e.buttons & 1) === 1;
      const hasPointerInteraction =
        isDraggingRef.current ||
        isHotspotDraggingRef.current ||
        isHotspotResizingRef.current ||
        isHotspotRotatingRef.current;
      if (!isPrimaryButtonDown && hasPointerInteraction) {
        handlePointerUp(e);
        return;
      }

      if (isHotspotRotatingRef.current) {
        const rotateState = hotspotRotateRef.current;
        if (!rotateState) return;

        const pointerDelta = {
          x: e.clientX - rotateState.startPointer.x,
          y: e.clientY - rotateState.startPointer.y,
        };

        // Rotation speed: degrees per pixel of mouse movement
        const rotationSpeed = 0.5;

        let nextRotation = { ...rotateState.startRotation };
        if (rotateState.axis === "free") {
          nextRotation = {
            x: rotateState.startRotation.x + pointerDelta.y * rotationSpeed,
            y: rotateState.startRotation.y + pointerDelta.x * rotationSpeed,
            z: rotateState.startRotation.z,
          };
        } else {
          // Determine rotation based on axis and mouse direction
          let deltaRotation = 0;
          if (rotateState.axis === "x") {
            deltaRotation = pointerDelta.y * rotationSpeed;
          } else if (rotateState.axis === "y") {
            deltaRotation = pointerDelta.x * rotationSpeed;
          } else if (rotateState.axis === "z") {
            deltaRotation =
              Math.hypot(pointerDelta.x, pointerDelta.y) *
              rotationSpeed *
              (pointerDelta.x > 0 ? 1 : -1);
          }

          nextRotation = {
            x:
              rotateState.axis === "x"
                ? rotateState.startRotation.x + deltaRotation
                : rotateState.startRotation.x,
            y:
              rotateState.axis === "y"
                ? rotateState.startRotation.y + deltaRotation
                : rotateState.startRotation.y,
            z:
              rotateState.axis === "z"
                ? rotateState.startRotation.z + deltaRotation
                : rotateState.startRotation.z,
          };
        }

        const currentState = useEditorStore.getState();
        currentState.updateHotspot(rotateState.hotspotId, { rotation: nextRotation });
        wasDraggingRef.current = true;

        e.preventDefault();
        return;
      }

      if (isPlacementDraggingRef.current) {
        const dragState = placementDragRef.current;
        if (!dragState) return;
        dragState.currentPointer = { x: e.clientX, y: e.clientY };

        const rect = container.getBoundingClientRect();
        const x = Math.min(dragState.startPointer.x, dragState.currentPointer.x) - rect.left;
        const y = Math.min(dragState.startPointer.y, dragState.currentPointer.y) - rect.top;
        const width = Math.abs(dragState.currentPointer.x - dragState.startPointer.x);
        const height = Math.abs(dragState.currentPointer.y - dragState.startPointer.y);
        setPlacementRect({ x, y, width, height });

        if (width > 2 || height > 2) {
          wasDraggingRef.current = true;
        }

        e.preventDefault();
        return;
      }

      if (isHotspotResizingRef.current) {
        const resizeState = hotspotResizeRef.current;
        if (!resizeState || !hotspotManagerRef.current) return;

        const pointerDelta = {
          x: e.clientX - resizeState.startPointer.x,
          y: e.clientY - resizeState.startPointer.y,
        };
        const direction = RESIZE_DIRECTION_BY_HANDLE[resizeState.handle];

        const localDeltaX =
          (pointerDelta.x * resizeState.xScreenUnit.x +
            pointerDelta.y * resizeState.xScreenUnit.y) /
          resizeState.xPixelsPerWorld;
        const localDeltaY =
          (pointerDelta.x * resizeState.yScreenUnit.x +
            pointerDelta.y * resizeState.yScreenUnit.y) /
          resizeState.yPixelsPerWorld;

        let halfWidth = resizeState.startHalfWidth;
        let halfHeight = resizeState.startHalfHeight;

        if (direction.x !== 0) {
          halfWidth = resizeState.startHalfWidth + direction.x * localDeltaX;
        }
        if (direction.y !== 0) {
          halfHeight = resizeState.startHalfHeight + direction.y * localDeltaY;
        }

        const minHalfWidth = Math.max((resizeState.baseWidth * 0.05) / 2, 0.5);
        const minHalfHeight = Math.max((resizeState.baseHeight * 0.05) / 2, 0.5);
        halfWidth = Math.max(minHalfWidth, halfWidth);
        halfHeight = Math.max(minHalfHeight, halfHeight);

        const isCornerHandle = direction.x !== 0 && direction.y !== 0;
        // Get aspect ratio lock state from store
        const isAspectRatioLocked = useEditorStore.getState().isAspectRatioLocked;

        // Maintain aspect ratio only if locked AND Shift is not held
        // Shift bypasses the lock temporarily, allowing free resizing
        if (isCornerHandle && resizeState.aspectRatio > 0 && isAspectRatioLocked && !e.shiftKey) {
          const widthRatio = halfWidth / resizeState.startHalfWidth;
          const heightRatio = halfHeight / resizeState.startHalfHeight;

          if (Math.abs(widthRatio - 1) >= Math.abs(heightRatio - 1)) {
            halfHeight = halfWidth / resizeState.aspectRatio;
          } else {
            halfWidth = halfHeight * resizeState.aspectRatio;
          }

          halfWidth = Math.max(minHalfWidth, halfWidth);
          halfHeight = Math.max(minHalfHeight, halfHeight);
        }

        if (resizeState.isHtml) {
          const widthRatio =
            resizeState.startHalfWidth > 0 ? halfWidth / resizeState.startHalfWidth : 1;
          const heightRatio =
            resizeState.startHalfHeight > 0 ? halfHeight / resizeState.startHalfHeight : 1;

          const nextWidthPx = Math.max(1, Math.round(resizeState.startContentWidthPx * widthRatio));
          const nextHeightPx = Math.max(
            1,
            Math.round(resizeState.startContentHeightPx * heightRatio),
          );

          const nextHtmlContent: HtmlResizeContentSnapshot = {
            width: toPxString(nextWidthPx),
            height: toPxString(nextHeightPx),
          };

          if (
            resizeState.textResizeMode === "scale-with-box" &&
            resizeState.startFontSizePx !== null &&
            Number.isFinite(resizeState.startFontSizePx)
          ) {
            const widthScale = nextWidthPx / Math.max(1, resizeState.startContentWidthPx);
            const heightScale = nextHeightPx / Math.max(1, resizeState.startContentHeightPx);
            let textScaleFactor = 1;
            if (direction.x !== 0 && direction.y !== 0) {
              textScaleFactor = Math.sqrt(Math.max(0, widthScale * heightScale));
            } else if (direction.x !== 0) {
              textScaleFactor = widthScale;
            } else if (direction.y !== 0) {
              textScaleFactor = heightScale;
            }
            nextHtmlContent.fontSize = toPxString(
              Math.max(1, resizeState.startFontSizePx * textScaleFactor),
            );
          }

          const lastHtmlContent = resizeState.lastHtmlContent;
          const contentChanged =
            !lastHtmlContent ||
            lastHtmlContent.width !== nextHtmlContent.width ||
            lastHtmlContent.height !== nextHtmlContent.height ||
            lastHtmlContent.fontSize !== nextHtmlContent.fontSize;

          if (contentChanged) {
            resizeState.lastHtmlContent = nextHtmlContent;
            resizeState.lastScale = { x: 1, y: 1, z: resizeState.startScale.z };

            const nowMs = performance.now();
            if (nowMs - resizeState.lastHtmlDispatchAtMs >= 24) {
              resizeState.lastHtmlDispatchAtMs = nowMs;
              const currentState = useEditorStore.getState();
              const liveHotspot = currentState.hotspots.find((h) => h.id === resizeState.hotspotId);
              const liveContent =
                liveHotspot?.content && typeof liveHotspot.content === "object"
                  ? (liveHotspot.content as HotspotContentRecord)
                  : {};
              const nextContent: HotspotContentRecord = {
                ...liveContent,
                ...nextHtmlContent,
              };
              const rawLocalStyle = liveContent[HOTSPOT_LOCAL_STYLE_CONTENT_KEY];
              if (rawLocalStyle && typeof rawLocalStyle === "object" && !Array.isArray(rawLocalStyle)) {
                nextContent[HOTSPOT_LOCAL_STYLE_CONTENT_KEY] = {
                  ...(rawLocalStyle as HotspotContentRecord),
                  ...nextHtmlContent,
                };
              }
              currentState.updateHotspot(resizeState.hotspotId, {
                scale: { x: 1, y: 1, z: resizeState.startScale.z },
                content: nextContent,
              });
              wasDraggingRef.current = true;
            }
          }

          e.preventDefault();
          return;
        }

        const nextScale = {
          x: Math.max(0.05, (halfWidth * 2) / resizeState.baseWidth),
          y: Math.max(0.05, (halfHeight * 2) / resizeState.baseHeight),
          z: resizeState.startScale.z,
        };

        const scaleChanged =
          Math.abs(nextScale.x - resizeState.lastScale.x) > 0.0005 ||
          Math.abs(nextScale.y - resizeState.lastScale.y) > 0.0005;

        if (scaleChanged) {
          resizeState.lastScale = nextScale;
          hotspotManagerRef.current.updateHotspotScaleImmediate(resizeState.hotspotId, nextScale);
          const currentState = useEditorStore.getState();
          currentState.updateHotspot(resizeState.hotspotId, { scale: nextScale });
          wasDraggingRef.current = true;
        }

        e.preventDefault();
        return;
      }

      if (isHotspotDraggingRef.current && camera && sphere) {
        const rect = container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        const raycaster = raycasterRef.current;
        raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
        const intersects = raycaster.intersectObject(sphere, false);

        if (intersects.length > 0) {
          const position = intersects[0].point;
          const radius = 400;
          const normalized = position.clone().normalize().multiplyScalar(radius);
          const hotspotId = hotspotDragIdRef.current;

          const dragStart = hotspotDragStartRef.current;
          if (dragStart) {
            const deltaX = e.clientX - dragStart.x;
            const deltaY = e.clientY - dragStart.y;
            if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
              wasDraggingRef.current = true;
            }
          }

          if (hotspotId && hotspotManagerRef.current) {
            const hotspotMeshes = hotspotManagerRef.current.getHotspotMeshes();
            const hotspotMesh = hotspotMeshes[hotspotId];

            hotspotDragPositionRef.current = {
              x: normalized.x,
              y: normalized.y,
              z: normalized.z,
            };

            if (hotspotMesh) {
              hotspotMesh.position.set(normalized.x, normalized.y, normalized.z);
              hotspotMesh.userData.lastPosition = {
                x: normalized.x,
                y: normalized.y,
                z: normalized.z,
              };
            }

            hotspotManagerRef.current.updateHTMLHotspotPosition(hotspotId, {
              x: normalized.x,
              y: normalized.y,
              z: normalized.z,
            });
          }
        }
        e.preventDefault();
        return;
      }

      if (!isDraggingRef.current || !camera) return;
      if (getIsSkinComponentResizing()) return;

      const deltaX = e.clientX - lastPointerXRef.current;
      const deltaY = e.clientY - lastPointerYRef.current;

      // If there's any movement, mark as dragging
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        wasDraggingRef.current = true;
      }

      // Accumulate drag delta; camera rotation is applied in RAF for smoother motion.
      pendingDragDeltaRef.current.x += deltaX;
      pendingDragDeltaRef.current.y += deltaY;

      lastPointerXRef.current = e.clientX;
      lastPointerYRef.current = e.clientY;
    };

    const handlePointerUp = (e: PointerEvent) => {
      // Handle drag-to-add-hotspot (pointer held on button, released over panorama)
      const currentState = useEditorStore.getState();
      const hasActivePointerInteraction =
        isDraggingRef.current ||
        isHotspotDraggingRef.current ||
        isHotspotResizingRef.current ||
        isHotspotRotatingRef.current;

      if (container.hasPointerCapture(e.pointerId)) {
        container.releasePointerCapture(e.pointerId);
      }

      if (currentState.isDraggingToAddHotspot && !hasActivePointerInteraction) {
        currentState.setIsDraggingToAddHotspot(false);
        const currentUser = getPlacementUser();
        const rect = container.getBoundingClientRect();
        const isWithinContainer =
          e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom;
        if (currentUser && cameraRef.current && sphereRef.current && isWithinContainer) {
          const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
          const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
          const raycaster = raycasterRef.current;
          raycaster.setFromCamera(new THREE.Vector2(nx, ny), cameraRef.current);
          const intersects = raycaster.intersectObject(sphereRef.current, false);
          const position = intersects.length > 0
            ? { x: intersects[0].point.x, y: intersects[0].point.y, z: intersects[0].point.z }
            : { x: 0, y: 0, z: -400 };
          currentState.requestHotspotPlacement(position, currentUser)
            .then(() => {
              showContextualPopoverRef.current?.(e.clientX - rect.left, e.clientY - rect.top);
            })
            .catch((err) => console.error("Error placing hotspot via drag:", err));
        }
        return;
      }

      if (isPlacementDraggingRef.current) {
        const dragState = placementDragRef.current;
        isPlacementDraggingRef.current = false;
        placementDragRef.current = null;
        setPlacementRect(null);
        container.style.cursor = "grab";

        if (dragState) {
          const currentUser = getPlacementUser();
          const dragWidth = Math.abs(e.clientX - dragState.startPointer.x);
          const dragHeight = Math.abs(e.clientY - dragState.startPointer.y);
          const dragDistance = Math.hypot(dragWidth, dragHeight);
          const minDragPx = 6;
          const targetClientPoint = dragDistance >= minDragPx
            ? {
                x: (dragState.startPointer.x + e.clientX) / 2,
                y: (dragState.startPointer.y + e.clientY) / 2,
              }
            : dragState.startPointer;

          const intersection = getSphereIntersection(targetClientPoint.x, targetClientPoint.y);
          if (currentUser && intersection) {
            suppressClickRef.current = true;
            currentState
              .requestHotspotPlacement(
                {
                  x: intersection.position.x,
                  y: intersection.position.y,
                  z: intersection.position.z,
                },
                currentUser,
              )
              .then(() => {
                if (dragDistance >= minDragPx) {
                  applyPlacementScaleToSelected(dragWidth, dragHeight);
                }
                showContextualPopoverRef.current?.(
                  intersection.containerPoint.x,
                  intersection.containerPoint.y,
                );
              })
              .catch((error) => {
                console.error("Error placing hotspot:", error);
                toastRef.current({
                  variant: "destructive",
                  title: "Error placing hotspot",
                  description: "Failed to create hotspot at this location.",
                });
              });
          }
        }
        return;
      }

      if (isHotspotRotatingRef.current) {
        const rotateState = hotspotRotateRef.current;
        isHotspotRotatingRef.current = false;
        hotspotRotateRef.current = null;

        hotspotManagerRef.current?.setTransformingState(false, rotateState?.hotspotId || null);
        document.body.style.cursor = "";
        container.style.cursor = "grab";
        return;
      }

      if (isHotspotResizingRef.current) {
        const resizeState = hotspotResizeRef.current;
        isHotspotResizingRef.current = false;
        hotspotResizeRef.current = null;

        hotspotManagerRef.current?.setTransformingState(false, resizeState?.hotspotId || null);
        document.body.style.cursor = "";
        container.style.cursor = "grab";

        if (resizeState) {
          if (resizeState.isHtml) {
            const currentState = useEditorStore.getState();
            const hasContentUpdate = resizeState.lastHtmlContent !== null;
            const scaleChanged =
              Math.abs(resizeState.startScale.x - 1) > 0.0005 ||
              Math.abs(resizeState.startScale.y - 1) > 0.0005;

            if (hasContentUpdate || scaleChanged) {
              const liveHotspot = currentState.hotspots.find((h) => h.id === resizeState.hotspotId);
              const liveContent =
                liveHotspot?.content && typeof liveHotspot.content === "object"
                  ? (liveHotspot.content as HotspotContentRecord)
                  : {};
              const finalContent = hasContentUpdate
                ? ({ ...liveContent, ...resizeState.lastHtmlContent } as HotspotContentRecord)
                : liveContent;
              if (hasContentUpdate) {
                const rawLocalStyle = liveContent[HOTSPOT_LOCAL_STYLE_CONTENT_KEY];
                if (rawLocalStyle && typeof rawLocalStyle === "object" && !Array.isArray(rawLocalStyle)) {
                  finalContent[HOTSPOT_LOCAL_STYLE_CONTENT_KEY] = {
                    ...(rawLocalStyle as HotspotContentRecord),
                    ...resizeState.lastHtmlContent,
                  };
                }
              }

              const updates: Partial<Hotspot> = {
                scale: { x: 1, y: 1, z: resizeState.startScale.z },
                content: finalContent,
              };

              currentState
                .updateHotspotFromViewer(
                  resizeState.hotspotId,
                  updates,
                  {
                    historyBeforeOverride: {
                      scale: resizeState.startScale,
                      content: resizeState.startContentSnapshot,
                    },
                  },
                )
                .catch((error) => {
                  console.error("Error updating HTML hotspot resize:", error);
                  toastRef.current({
                    variant: "destructive",
                    title: "Error resizing hotspot",
                    description: "Failed to update hotspot size.",
                  });
                });
            }
            return;
          }

          const scaleChanged =
            Math.abs(resizeState.lastScale.x - resizeState.startScale.x) > 0.0005 ||
            Math.abs(resizeState.lastScale.y - resizeState.startScale.y) > 0.0005;

          if (scaleChanged) {
            const currentState = useEditorStore.getState();
            currentState
              .updateHotspotFromViewer(
                resizeState.hotspotId,
                { scale: resizeState.lastScale },
                { historyBeforeOverride: { scale: resizeState.startScale } },
              )
              .catch((error) => {
                console.error("Error updating hotspot scale:", error);
                toastRef.current({
                  variant: "destructive",
                  title: "Error resizing hotspot",
                  description: "Failed to update hotspot scale.",
                });
              });
          }
        }
        return;
      }

      if (isHotspotDraggingRef.current) {
        const hotspotId = hotspotDragIdRef.current;
        const position = hotspotDragPositionRef.current;

        isHotspotDraggingRef.current = false;
        hotspotDragIdRef.current = null;
        hotspotDragPositionRef.current = null;
        hotspotDragStartRef.current = null;
        hotspotManagerRef.current?.setDraggingState(false, null);
        container.style.cursor = "grab";
        document.body.style.cursor = "";

        if (hotspotId && position) {
          const currentState = useEditorStore.getState();
          currentState.updateHotspotFromViewer(hotspotId, { position }).catch((error) => {
            console.error("Error updating hotspot position:", error);
            toastRef.current({
              variant: "destructive",
              title: "Error moving hotspot",
              description: "Failed to update hotspot position.",
            });
          });
        }
        return;
      }

      isDraggingRef.current = false;
      container.style.cursor = "grab";
      document.body.style.cursor = "";
    };

    // Mouse wheel zoom functionality
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      if (isPanoramaTransitioningRef.current) return;
      if (!camera) return;

      // Zoom by changing FOV
      const zoomSpeed = 0.1;
      const deltaY = e.deltaY;

      // Decrease FOV for zoom in (wheel up), increase FOV for zoom out (wheel down)
      const newFOV = camera.fov + deltaY * zoomSpeed;

      // Clamp FOV between 10 and 120 degrees
      camera.fov = THREE.MathUtils.clamp(newFOV, 10, 120);
      camera.updateProjectionMatrix();
    };

    const handleClick = (e: MouseEvent) => {
      if (isPanoramaTransitioningRef.current) return;
      if (contextMenuOpenRef.current) return;
      if (isInsideNavDropMenu(e)) return;
      if (isInsideSpecificViewDialog(e)) return;
      if (isInsideContextualPopover(e)) return;
      // Skin component clicks are handled by React — don't interfere
      if ((e.target as HTMLElement).closest("[data-skin-component]")) return;
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      if (isHotspotResizingRef.current) {
        return;
      }

      // Don't process clicks if user was just dragging the camera
      if (wasDraggingRef.current) {
        wasDraggingRef.current = false;
        return;
      }

      // Get current state directly from store to avoid stale closures
      const currentState = useEditorStore.getState();

      // Get click position relative to container
      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      // Perform raycasting to find intersection with sphere
      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

      if (currentState.chromaKeyPickHotspotId && hotspotManagerRef.current) {
        const targetMesh =
          hotspotManagerRef.current.getHotspotMeshes()[currentState.chromaKeyPickHotspotId];
        if (targetMesh) {
          const hit = raycaster.intersectObject(targetMesh, false)[0];
          if (hit?.uv) {
            const videoElement = (targetMesh as any).userData?.videoElement as
              | HTMLVideoElement
              | undefined;
            if (videoElement && videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
              const canvas =
                chromaSampleCanvasRef.current || document.createElement("canvas");
              chromaSampleCanvasRef.current = canvas;
              canvas.width = videoElement.videoWidth;
              canvas.height = videoElement.videoHeight;
              const ctx = canvas.getContext("2d");
              if (ctx) {
                ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
                const px = Math.min(
                  videoElement.videoWidth - 1,
                  Math.max(0, Math.floor(hit.uv.x * videoElement.videoWidth)),
                );
                const py = Math.min(
                  videoElement.videoHeight - 1,
                  Math.max(0, Math.floor((1 - hit.uv.y) * videoElement.videoHeight)),
                );
                const [r, g, b] = ctx.getImageData(px, py, 1, 1).data;
                const toHex = (value: number) =>
                  value.toString(16).padStart(2, "0");
                const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

                const targetHotspot = currentState.hotspots.find(
                  (h) => h.id === currentState.chromaKeyPickHotspotId,
                );
                const currentContent =
                  (targetHotspot?.content as Record<string, unknown>) || {};
                const nextContent = {
                  ...currentContent,
                  chromaKeyColor: hex,
                  chromaKeyEnabled: true,
                };
                currentState
                  .updateHotspotFromViewer(currentState.chromaKeyPickHotspotId, {
                    content: nextContent,
                  })
                  .catch((error) => {
                    console.error("Failed to set chroma key color:", error);
                    toastRef.current({
                      variant: "destructive",
                      title: "Chroma Key Failed",
                      description: "Could not set the key color.",
                    });
                  });

                currentState.setChromaKeyPickHotspotId(null);
                e.stopPropagation();
                return;
              }
            } else {
              toastRef.current({
                variant: "destructive",
                title: "Video Not Ready",
                description: "Wait for the video to load, then try again.",
              });
            }
          }
        }
        e.stopPropagation();
        return;
      }

      // Lock hotspots in skin editor when not in preview mode
      const isHotspotLocked = currentRouteRef.current === "skin" && !isPreviewModeRef.current;

      // First check for hotspot intersections (SVG hotspots)
      if (hotspotManagerRef.current) {
        const hotspotMeshes = hotspotManagerRef.current.getHotspotMeshes();
        const hotspotObjects = Object.values(hotspotMeshes);

        if (hotspotObjects.length > 0) {
          const hotspotIntersects = raycaster.intersectObjects(hotspotObjects, false);

          if (hotspotIntersects.length > 0) {
            const hotspotId = pickHotspotIdFromIntersects(hotspotIntersects, currentState);

            if (hotspotId) {
              // Find the hotspot data
              const hotspot = currentState.hotspots.find((h) => h.id === hotspotId);

              if (hotspot) {
                // If hotspot is locked, don't allow any interaction
                if (isHotspotLocked) {
                  e.stopPropagation();
                  return;
                }

                // Check if we should trigger actions based on mode and modifier key
                const isModifierPressed = e.metaKey || e.ctrlKey; // Command key on Mac, Ctrl on Windows/Linux
                const shouldTriggerActions = isPreviewModeRef.current || isModifierPressed;

                if (shouldTriggerActions) {
                  // Execute hotspot actions
                  executeHotspotActions(hotspot, currentState, navigateToPanorama);
                } else {
                  // In editor mode without modifier key, just select the hotspot
                  currentState.setSelectedHotspot(hotspot);
                }

                e.stopPropagation();
                return;
              }
            }
          }
        }
      }

      // Fallback hit-testing for HTML hotspots (and any hotspot without a mesh).
      const pickedHotspotId = pickHotspotFromPointer(e.clientX, e.clientY, currentState);
      if (pickedHotspotId) {
        const hotspot = currentState.hotspots.find((h) => h.id === pickedHotspotId);
        if (hotspot) {
          // If hotspot is locked, don't allow any interaction
          if (isHotspotLocked) {
            e.stopPropagation();
            return;
          }

          const isModifierPressed = e.metaKey || e.ctrlKey;
          const shouldTriggerActions = isPreviewModeRef.current || isModifierPressed;

          if (shouldTriggerActions) {
            executeHotspotActions(hotspot, currentState, navigateToPanorama);
          } else {
            currentState.setSelectedHotspot(hotspot);
          }

          e.stopPropagation();
          return;
        }
      }

      // In editor mode, clicking empty viewer space should clear hotspot selection.
      if (!isPreviewModeRef.current) {
        currentState.setSelectedHotspot(null);
      }
    };

    const handleDblClick = (e: MouseEvent) => {
      if (isInsideNavDropMenu(e)) return;
      if (isInsideSpecificViewDialog(e)) return;
      if (isInsideContextualPopover(e)) return;
      if (isPreviewModeRef.current || wasDraggingRef.current) return;
      if (currentRouteRef.current === "skin" && !isPreviewModeRef.current) return;

      const currentState = useEditorStore.getState();
      const rect = container.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      const raycaster = raycasterRef.current;
      raycaster.setFromCamera(new THREE.Vector2(nx, ny), camera);

      // Check SVG hotspot meshes
      if (hotspotManagerRef.current) {
        const hotspotMeshes = hotspotManagerRef.current.getHotspotMeshes();
        const hotspotObjects = Object.values(hotspotMeshes);
        if (hotspotObjects.length > 0) {
          const hotspotIntersects = raycaster.intersectObjects(hotspotObjects, false);
          if (hotspotIntersects.length > 0) {
            const hotspotId = pickHotspotIdFromIntersects(hotspotIntersects, currentState);
            if (hotspotId) {
              const hotspot = currentState.hotspots.find((h) => h.id === hotspotId);
              if (hotspot) {
                currentState.setSelectedHotspot(hotspot);
                showContextualPopoverRef.current?.(e.clientX - rect.left, e.clientY - rect.top);
                e.stopPropagation();
                return;
              }
            }
          }
        }
      }

      // Fallback for HTML hotspots
      const pickedHotspotId = pickHotspotFromPointer(e.clientX, e.clientY, currentState);
      if (pickedHotspotId) {
        const hotspot = currentState.hotspots.find((h) => h.id === pickedHotspotId);
        if (hotspot) {
          currentState.setSelectedHotspot(hotspot);
          showContextualPopoverRef.current?.(e.clientX - rect.left, e.clientY - rect.top);
          e.stopPropagation();
        }
      }
    };

    // Add event listeners
    container.addEventListener("pointerdown", handlePointerDown);
    container.addEventListener("pointermove", handlePointerMove);
    container.addEventListener("pointerup", handlePointerUp);
    container.addEventListener("pointerleave", handlePointerUp);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    container.addEventListener("click", handleClick);
    container.addEventListener("dblclick", handleDblClick);
    container.addEventListener("wheel", handleWheel, { passive: false });
    container.style.cursor = "grab";

    // Animation loop with performance optimizations
    let lastStatsUpdate = 0;
    const STATS_UPDATE_INTERVAL = 100; // Update stats at ~10fps instead of 60fps for better performance

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      const now = performance.now();
      const deltaTime = now - lastStatsUpdate;

      // During crossfade arrival, keep camera locked to the computed landing view
      // so no intermediate render path can show a transient wrong orientation.
      const lockedView = navigationLandingViewRef.current;
      if (lockedView) {
        const currentPanorama = useEditorStore.getState().selectedPanorama;
        const shouldLockCamera =
          currentPanorama?.id === lockedView.panoramaId &&
          isPanoramaTransitioningRef.current &&
          !pendingCrossfadeArrivalRef.current;
        if (shouldLockCamera) {
          const debugContext = crossfadeDebugContextRef.current;
          if (debugContext && !debugContext.cameraLockLogged) {
            debugContext.cameraLockLogged = true;
            logCrossfadeDebug("renderLoopCameraLockApplied", {
              panoramaId: currentPanorama?.id || null,
              lockedYaw: lockedView.view.yaw,
              lockedPitch: lockedView.view.pitch,
              cameraFov: camera.fov,
            });
          }
          applyDisplayCoordsToCamera(camera, lockedView.view.yaw, lockedView.view.pitch);
        }
      }

      // Smooth, frame-based camera panning to reduce pointer-event jitter.
      if (
        isDraggingRef.current &&
        !isPanoramaTransitioningRef.current &&
        !isHotspotDraggingRef.current &&
        !isHotspotResizingRef.current &&
        !isHotspotRotatingRef.current &&
        !getIsSkinComponentResizing()
      ) {
        const sensitivity = 0.002;
        const smoothFactor = 0.22;
        const pending = pendingDragDeltaRef.current;
        const smoothed = smoothedDragDeltaRef.current;
        const targetYawDelta = pending.x * sensitivity;
        const targetPitchDelta = pending.y * sensitivity;

        smoothed.x += (targetYawDelta - smoothed.x) * smoothFactor;
        smoothed.y += (targetPitchDelta - smoothed.y) * smoothFactor;

        camera.rotation.y += smoothed.x;
        camera.rotation.x += smoothed.y;
        camera.rotation.x = THREE.MathUtils.clamp(camera.rotation.x, -Math.PI / 2, Math.PI / 2);

        pendingDragDeltaRef.current = { x: 0, y: 0 };
      } else {
        smoothedDragDeltaRef.current.x *= 0.7;
        smoothedDragDeltaRef.current.y *= 0.7;
      }

      // Camera focus is now handled directly in focusCameraOnHotspot function
      // No animation loop needed

      // Update camera stats only at 10fps to reduce computation
      if (camera && deltaTime >= STATS_UPDATE_INTERVAL) {
        const { yaw, pitch, roll } = cameraToDisplayCoords(camera);

        setCameraStats({ yaw, pitch, roll, fov: camera.fov });
        setSlideToDebugStats(slideToDebugRef.current);

        // Update camera view in store for "Set Default View" functionality
        setCurrentCameraViewRef.current({ yaw, pitch, fov: camera.fov });

        lastStatsUpdate = now;
      }

      // Render hotspots
      if (hotspotManagerRef.current && camera) {
        hotspotManagerRef.current.render(camera);
      }

      updateSelectionOverlayFnRef.current();
      updateContextualPopoverPosition();

      renderer.render(scene, camera);
    };
    animate();

    // Handle window resize
    const handleResize = () => {
      if (!container || !camera || !renderer) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);

      // Update hotspot manager size for HTML hotspots
      if (hotspotManagerRef.current) {
        hotspotManagerRef.current.updateSize();
      }
    };

    // Handle custom viewer resize events (from drag slider)
    const handleViewerResize = () => {
      if (!container || !camera || !renderer) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);

      // Update hotspot manager size for HTML hotspots
      if (hotspotManagerRef.current) {
        hotspotManagerRef.current.updateSize();
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("viewer-resize", handleViewerResize);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      container.removeEventListener("pointerdown", handlePointerDown);
      container.removeEventListener("pointermove", handlePointerMove);
      container.removeEventListener("pointerup", handlePointerUp);
      container.removeEventListener("pointerleave", handlePointerUp);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      container.removeEventListener("click", handleClick);
      container.removeEventListener("dblclick", handleDblClick);
      container.removeEventListener("wheel", handleWheel);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("viewer-resize", handleViewerResize);
      document.body.style.cursor = "";

      if (hotspotManagerRef.current) {
        hotspotManagerRef.current.cleanup();
      }

      hideSelectionOverlayFnRef.current();

      if (renderer) {
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          renderer.domElement.removeEventListener("dragover", handleCanvasDragOver);
          renderer.domElement.removeEventListener("drop", handleCanvasDrop);
          container.removeChild(renderer.domElement);
        }
      }

      if (sphereGeometry) sphereGeometry.dispose();
      if (placeholderMaterial) placeholderMaterial.dispose();
      if (crossfadeSphereRef.current?.material instanceof THREE.Material) {
        crossfadeSphereRef.current.material.dispose();
      }
      if (fadeOverlaySphereRef.current?.material instanceof THREE.Material) {
        fadeOverlaySphereRef.current.material.dispose();
      }

      // Dispose all cached textures
      for (const tex of textureCacheRef.current.values()) tex.dispose();
      textureCacheRef.current.clear();
    };
  }, []);

  // Load panorama texture when panorama changes
  useEffect(() => {
    if (!panorama || !sphereRef.current || !cameraRef.current || !project) return;

    setPanoramaLoadFailed(false);

    const sphere = sphereRef.current;
    const camera = cameraRef.current;
    const panoramaCacheKey = getTextureCacheKey(panorama);
    const panoramaData = (panorama.data as Record<string, unknown> | undefined) || {};
    const originalFileUrl =
      typeof panoramaData.original_file_url === "string" ? panoramaData.original_file_url : null;
    const useOriginalFile = panoramaData.useOriginalFile === true;
    const isLikelyThumbnailUrl = (url: string | null | undefined): boolean => {
      if (!url) return false;
      const lower = url.toLowerCase();
      return lower.includes("thumbnail_") || lower.includes("/thumbnails/");
    };
    const getPreferredPanoramaUrl = (
      urls: Array<string | null | undefined>,
    ): string | null => {
      for (const url of urls) {
        if (!url) continue;
        if (!isLikelyThumbnailUrl(url)) return url;
      }
      return null;
    };
    const sourceCandidates = useOriginalFile
      ? [originalFileUrl, panorama.private_file_url, panorama.file_url_webp, panorama.file_url]
      : [originalFileUrl, panorama.private_file_url, panorama.file_url_webp, panorama.file_url];
    const fallbackUrl = getPreferredPanoramaUrl(sourceCandidates);

    const getCachedPanorama = async (): Promise<{ data: ArrayBuffer; mimeType: string } | null> => {
      if (!isCacheInitializedRef.current) return null;

      try {
        const cachedPanorama = await getPanorama(panorama.id);
        if (cachedPanorama?.data && cachedPanorama.mimeType) {
          return {
            data: cachedPanorama.data,
            mimeType: cachedPanorama.mimeType,
          };
        }
      } catch (error) {
        console.warn("Failed to load panorama from IndexedDB cache:", error);
      }

      return null;
    };

    const getCachedPanoramaThumbnail = async (): Promise<{ data: ArrayBuffer; mimeType: string } | null> => {
      if (!isCacheInitializedRef.current) return null;

      try {
        let cachedThumbnail = await getThumbnail(panorama.id, true);
        if (!cachedThumbnail) {
          cachedThumbnail = await getThumbnail(panorama.id, false);
        }

        if (cachedThumbnail?.data && cachedThumbnail.mimeType) {
          return {
            data: cachedThumbnail.data,
            mimeType: cachedThumbnail.mimeType,
          };
        }
      } catch (error) {
        console.warn("Failed to load panorama thumbnail from IndexedDB cache:", error);
      }

      return null;
    };

    const getPanoramaPreviewSource = async ():
      | { source: string | ArrayBuffer; mimeType?: string }
      | null => {
      const cachedThumbnail = await getCachedPanoramaThumbnail();
      if (cachedThumbnail) {
        return {
          source: cachedThumbnail.data,
          mimeType: cachedThumbnail.mimeType,
        };
      }

      const thumbnailUrl = panorama.thumbnail_url_webp || panorama.thumbnail_url;
      if (thumbnailUrl) {
        return { source: thumbnailUrl };
      }

      return null;
    };

    const clearVisibleTexture = () => {
      if (!(sphere.material instanceof THREE.MeshBasicMaterial)) return;
      const oldMap = sphere.material.map;
      const crossfadeMap =
        crossfadeSphereRef.current?.material instanceof THREE.MeshBasicMaterial
          ? crossfadeSphereRef.current.material.map
          : null;

      sphere.material.map = null;
      sphere.material.color = new THREE.Color(0x111827);
      sphere.material.needsUpdate = true;

      if (
        oldMap &&
        oldMap !== crossfadeMap &&
        ![...textureCacheRef.current.values()].includes(oldMap)
      ) {
        oldMap.dispose();
      }
    };

    const markPanoramaLoadFailure = () => {
      clearVisibleTexture();
      setPanoramaLoadFailed(true);
      setIsTextureLoading(false);
    };

    const startPanoramaLoad = async (): Promise<void> => {
      const requestId = ++panoramaLoadRequestIdRef.current;
      const applyToCurrentLoad = () => panoramaLoadRequestIdRef.current === requestId;
      const cacheKey = `${panoramaCacheKey}:preview`;
      const hasFallbackUrl = Boolean(fallbackUrl);
      const isTransitionTargetLoad =
        isPanoramaTransitioningRef.current &&
        (pendingCrossfadeArrivalRef.current?.panoramaId === panorama.id ||
          navigationLandingViewRef.current?.panoramaId === panorama.id);

      const cachedTexture = textureCacheRef.current.get(panoramaCacheKey);
      if (cachedTexture) {
        if (!applyToCurrentLoad()) {
          return;
        }
        applyTexture(cachedTexture);
        setIsTextureLoading(false);
        return;
      }

      const [cachedPanorama, previewSource] = await Promise.all([
        getCachedPanorama(),
        getPanoramaPreviewSource(),
      ]);

      if (!hasFallbackUrl && !cachedPanorama) {
        console.warn("No panorama source available for panorama:", panorama.id, {
          hasPrivateUrl: !!panorama.private_file_url,
          hasFileUrl: !!panorama.file_url,
          hasCachedPanorama: !!cachedPanorama,
          panoramaName: panorama.name,
        });

        toastRef.current({
          variant: "destructive",
          title: "Panorama not available",
          description: `The panorama "${panorama.name}" has no accessible image file. Please check if the file was uploaded correctly.`,
        });
        markPanoramaLoadFailure();
        return;
      }

      if (cachedPanorama) {
        setIsTextureLoading(true);
        try {
          if (!applyToCurrentLoad()) {
            return;
          }

            await loadTexture(cachedPanorama.data, panoramaCacheKey, 0, {
              mimeType: cachedPanorama.mimeType,
              isCached: true,
              requestId,
            });

          if (!applyToCurrentLoad()) {
            return;
          }

          lastPanoramaLoadRef.current = { id: panorama.id, url: `indexeddb://${panorama.id}` };
          return;
        } catch (_cacheError) {
          console.info("Falling back to network panorama load after cached decode failure.");
          if (!applyToCurrentLoad()) {
            return;
          }
          if (!fallbackUrl) {
            setIsTextureLoading(false);
            return;
          }
        }
      }

      if (hasFallbackUrl) {
        if (previewSource && !isTransitionTargetLoad) {
          void (async () => {
            try {
              if (!applyToCurrentLoad()) {
                return;
              }

              const previewLoadResult = await loadTexture(previewSource.source, cacheKey, 0, {
                mimeType: previewSource.mimeType || "image/jpeg",
                cacheTexture: false,
                isCached: false,
                requestId,
                suppressLoadingState: true,
                suppressFailureToasts: true,
              });

              if (!applyToCurrentLoad()) {
                return;
              }

              setIsTextureLoading(!previewLoadResult);
            } catch {
              if (!applyToCurrentLoad()) {
                return;
              }
              setIsTextureLoading(false);
            }
          })();
        }

        // Start loading from network if cache path missed
        if (applyToCurrentLoad()) {
          setIsTextureLoading(true);
        }
        lastPanoramaLoadRef.current = { id: panorama.id, url: fallbackUrl };
        await loadTexture(fallbackUrl, panoramaCacheKey, 0, { requestId });
      }
    };

    // PERFORMANCE FIX: Disabled automatic thumbnail checking on every panorama change
    // This was causing slow loading times by making multiple network requests per panorama
    // Thumbnails are still checked/regenerated automatically in SceneHierarchy when thumbnail loading fails
    /*
    const checkThumbnails = async () => {
      try {
        const result = await checkAndRegenerateThumbnails(
          panorama,
          project.id,
          project.craftspace_id || 'default'
        );
        
        if (result.regenerated) {
          const { updatePanorama } = useEditorStore.getState();
          updatePanorama(panorama.id, {
            thumbnail_url: result.thumbnail_url,
            thumbnail_url_webp: result.thumbnail_url_webp
          });
        }
      } catch (error) {
        console.warn('Failed to check/regenerate thumbnails:', error);
      }
    };

    checkThumbnails();
    */

    // Function to refresh expired URL
    const refreshUrl = async (url: string): Promise<string | null> => {
      try {
        // Bunny CDN URL (token auth or legacy without token) — regenerate via Convex action
        const bunnyPullZoneHost = import.meta.env.VITE_BUNNY_PULL_ZONE_HOST as string | undefined;
        const isBunnyUrl = (bunnyPullZoneHost && url.includes(bunnyPullZoneHost)) || url.includes(".b-cdn.net");
        if (isBunnyUrl) {
          const path = new URL(url).pathname.replace(/^\/+/, "");
          const { StorageService } = await import("@/services/storageService");
          const result = await StorageService.getPrivateUrl("staging", path, 3600, projectCraftspaceId);
          const newUrl = result.data?.signedUrl;
          if (newUrl) {
            await convex.mutation(api.panoramas.update, { id: panorama.id, private_file_url: newUrl });
            const { updatePanorama } = useEditorStore.getState();
            updatePanorama(panorama.id, { private_file_url: newUrl });
          }
          return newUrl ?? null;
        }

        const signedMarker = "/storage/v1/object/sign/";
        const publicMarker = "/storage/v1/object/public/";
        let filePath: string | null = null;

        if (url.includes(signedMarker)) {
          filePath =
            url
              .split(signedMarker)[1]
              ?.split("?")[0]
              ?.replace(/^staging\//, "") || null;
        } else if (url.includes(publicMarker)) {
          filePath =
            url
              .split(publicMarker)[1]
              ?.split("?")[0]
              ?.replace(/^staging\//, "") || null;
        }

        if (filePath) {
          // Generate new cloud signed URL
          const { StorageService } = await import("@/services/storageService");
          const result = await StorageService.getPrivateUrl("staging", filePath);

          if (result.error || !result.data.signedUrl) {
            console.error("Failed to refresh URL:", result.error);
            return null;
          }

          const newUrl = result.data.signedUrl;

          // Update the panorama in database
          await convex.mutation(api.panoramas.update, {
            id: panorama.id,
            private_file_url: newUrl,
          });

          // Update the panorama in the store
          const { updatePanorama } = useEditorStore.getState();
          updatePanorama(panorama.id, { private_file_url: newUrl });

          return newUrl;
        }

        // If it's an S3 URL, try S3 refresh
        if (url.includes("amazonaws.com") || url.includes("s3.")) {
          let s3FilePath: string | null = null;
          try {
            const { StorageServiceFactory } = await import(
              "@/services/storage/StorageServiceFactory"
            );
            const storageService = await StorageServiceFactory.createStorageService(
              projectCraftspaceId,
            );

            const urlObj = new URL(url);
            const pathname = urlObj.pathname.replace(/^\/+/, "");
            const segments = pathname.split("/").filter(Boolean);
            s3FilePath = segments.length > 1 ? segments.slice(1).join("/") : pathname;

            const newUrl = await storageService.getFileUrl(s3FilePath);
            if (newUrl) {
              await convex.mutation(api.panoramas.update, {
                id: panorama.id,
                private_file_url: newUrl,
              });

              // Update the panorama in the store
              const { updatePanorama } = useEditorStore.getState();
              updatePanorama(panorama.id, { private_file_url: newUrl });

              return newUrl;
            }
          } catch (s3Error) {
            console.error("S3 URL refresh failed:", s3Error);
            try {
              const serverSignedUrl = s3FilePath
                ? ((await convex.action(api.actions.s3SignedUrls.getDownloadUrl, {
                    craftspaceId: projectCraftspaceId,
                    path: s3FilePath,
                    expiresIn: 3600,
                  })) as string | null)
                : null;

              if (serverSignedUrl) {
                await convex.mutation(api.panoramas.update, {
                  id: panorama.id,
                  private_file_url: serverSignedUrl,
                });

                const { updatePanorama } = useEditorStore.getState();
                updatePanorama(panorama.id, { private_file_url: serverSignedUrl });
                return serverSignedUrl;
              }
            } catch (serverSignError) {
              console.error("Server-side S3 URL refresh failed:", serverSignError);
            }
          }
        }

        return null;
      } catch (error) {
        console.error("Error refreshing URL:", error);
        return null;
      }
    };

    const applyTexture = (texture: THREE.Texture) => {
      setIsTextureLoading(false);
      let crossfadeArrivalFadeDurationMs: number | undefined;
      let crossfadeArrivalFadeColor: string | undefined;
      let crossfadeArrivalIsFade = false;

      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.repeat.x = -1; // Fix horizontal mirroring
      const cameraBeforeApply = cameraToDisplayCoords(camera);
      const isCrossfadeArrival =
        isPanoramaTransitioningRef.current &&
        pendingCrossfadeArrivalRef.current?.panoramaId === panorama.id;
      logCrossfadeDebug("applyTexture:start", {
        panoramaId: panorama.id,
        isCrossfadeArrival,
        cameraBeforeYaw: cameraBeforeApply.yaw,
        cameraBeforePitch: cameraBeforeApply.pitch,
        cameraBeforeFov: camera.fov,
        pendingCrossfadePanoramaId: pendingCrossfadeArrivalRef.current?.panoramaId || null,
        pendingViewPanoramaId: pendingPanoramaViewRef.current?.panoramaId || null,
        forcedViewPanoramaId: navigationLandingViewRef.current?.panoramaId || null,
      });

      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.BackSide,
        transparent: isCrossfadeArrival,
        opacity: isCrossfadeArrival ? 0 : 1,
      });

      if (sphere.material instanceof THREE.Material) {
        const oldMap = (sphere.material as THREE.MeshBasicMaterial).map;
        const crossfadeMap =
          crossfadeSphereRef.current?.material instanceof THREE.MeshBasicMaterial
            ? crossfadeSphereRef.current.material.map
            : null;
        // Only dispose textures that are NOT in the cache — cached textures must stay alive
        // so switching back to a panorama is instant without re-decoding.
        if (
          oldMap &&
          oldMap !== crossfadeMap &&
          ![...textureCacheRef.current.values()].includes(oldMap)
        ) {
          oldMap.dispose();
        }
        sphere.material.dispose();
      }

      const forcedLandingView = navigationLandingViewRef.current;
      if (forcedLandingView && forcedLandingView.panoramaId === panorama.id) {
        logCrossfadeDebug("applyTexture:forcedLandingView", {
          panoramaId: panorama.id,
          yaw: forcedLandingView.view.yaw,
          pitch: forcedLandingView.view.pitch,
          fov: forcedLandingView.view.fov,
          deferredForCrossfade: isCrossfadeArrival,
        });
        // During crossfade arrival, don't rotate camera yet — the crossfade sphere
        // still shows the old panorama and would flash at the wrong angle.
        // The arrival animation will handle the transition to this view.
        if (!isCrossfadeArrival) {
          applyDisplayCoordsToCamera(
            camera,
            forcedLandingView.view.yaw,
            forcedLandingView.view.pitch,
          );
          camera.fov = forcedLandingView.view.fov;
          camera.updateProjectionMatrix();
        }
      } else {
        // Apply initial view if available, otherwise reset camera
        const data = panorama.data as PanoramaDataRecord | undefined;
        if (data?.initialView) {
          const { yaw = 0, pitch = 0, fov = 75 } = data.initialView;
          const yawRad = THREE.MathUtils.degToRad(-yaw);
          const pitchRad = THREE.MathUtils.degToRad(-pitch);
          camera.rotation.set(pitchRad, yawRad, 0);
          camera.fov = fov;
          camera.updateProjectionMatrix();
        } else {
          camera.rotation.set(0, 0, 0);
        }
      }

      sphere.material = material;

      const pendingView = pendingPanoramaViewRef.current;
      if (pendingView && pendingView.panoramaId === panorama.id) {
        logCrossfadeDebug("applyTexture:pendingView", {
          panoramaId: panorama.id,
          yaw: pendingView.view.yaw,
          pitch: pendingView.view.pitch,
          fov: pendingView.view.fov,
          deferredForCrossfade: isCrossfadeArrival,
        });
        if (!isCrossfadeArrival) {
          applyDisplayCoordsToCamera(camera, pendingView.view.yaw, pendingView.view.pitch);
          camera.fov = pendingView.view.fov;
          camera.updateProjectionMatrix();
        }
        if (!isPanoramaTransitioningRef.current) {
          setPendingPanoramaViewRef.current(null);
          navigationLandingViewRef.current = null;
        }
      }

      if (pendingCrossfadeArrivalRef.current?.panoramaId === panorama.id) {
        const currentView = cameraToDisplayCoords(camera);
        const pendingArrival = pendingCrossfadeArrivalRef.current;
        const incomingFov = pendingArrival.incomingFov;
        const restoreFov = THREE.MathUtils.clamp(pendingArrival.restoreFov, 18, 150);
        const fadeDurationMs = pendingArrival.fadeDurationMs;
        const fadeColor = pendingArrival.fadeColor;
        const isFadeTransition = pendingArrival.isFade;
        const forceLandingView = pendingArrival.forceLandingView;
        let slideToYawDelta = 0;
        crossfadeArrivalFadeDurationMs = fadeDurationMs;
        crossfadeArrivalFadeColor = fadeColor;
        crossfadeArrivalIsFade = Boolean(isFadeTransition);
        // Save landing view before clearing — we'll animate to it after the crossfade.
        const landingView = forcedLandingView?.panoramaId === panorama.id
          ? forcedLandingView.view
          : pendingView?.panoramaId === panorama.id
            ? pendingView.view
            : null;
        logCrossfadeDebug("applyTexture:pendingCrossfadeArrival", {
          panoramaId: panorama.id,
          currentYaw: currentView.yaw,
          currentPitch: currentView.pitch,
          landingYaw: landingView?.yaw,
          landingPitch: landingView?.pitch,
          incomingFov,
          restoreFov,
        });
        // Adjust camera yaw for the target panorama by subtracting the target
        // hotspot yaw, so the view feels continuous. Counter-rotate the crossfade
        // sphere so the departing (old) panorama stays at the original angle.
        pendingCrossfadeArrivalRef.current = null;
        navigationLandingViewRef.current = null;
        if (crossfadeFovZoomRef.current?.recenterAtEnd !== false) {
          camera.position.set(0, 0, 0);
        }
        if (landingView) {
          if (forceLandingView) {
            const crossfadeSphere = crossfadeSphereRef.current;
            if (crossfadeSphere) {
              crossfadeSphere.rotation.y = Math.PI / 2;
            }
            applyDisplayCoordsToCamera(camera, landingView.yaw, landingView.pitch);
            camera.updateProjectionMatrix();
          } else {
            const isAutoSlideTo = pendingArrival.isAutomaticSlideTo;
            const precomputedYaw = pendingArrival.slideToResolvedYaw;
            const precomputedPitch = pendingArrival.slideToResolvedPitch;

            if (isAutoSlideTo && precomputedYaw !== undefined && precomputedPitch !== undefined) {
              // Offset-invariant landing: snap camera to landing view now so the
              // new panorama (fading in) is always at the correct angle.
              // Counter-rotate the crossfade sphere so the old panorama stays stable.
              const yawDelta = shortestRotation(currentView.yaw, precomputedYaw);
              slideToYawDelta = yawDelta;
              if (slideToDebugRef.current?.status === "active") {
                slideToDebugRef.current = {
                  ...slideToDebugRef.current,
                  landingView,
                  landingYawDelta: yawDelta,
                };
              }
              const crossfadeSphere = crossfadeSphereRef.current;
              if (crossfadeSphere) {
                crossfadeSphere.rotation.y = Math.PI / 2 - THREE.MathUtils.degToRad(yawDelta);
              }
              applyDisplayCoordsToCamera(camera, precomputedYaw, precomputedPitch);
              camera.updateProjectionMatrix();
            } else {
              // Legacy path for non-slide-to crossfade transitions
              const targetHotspotYaw = normalizeAngle(landingView.yaw + 180);
              const adjustedYaw = normalizeAngle(currentView.yaw - targetHotspotYaw);
              const adjustedPitch = currentView.pitch;
              const yawDelta = adjustedYaw - currentView.yaw;
              slideToYawDelta = yawDelta;
              if (slideToDebugRef.current?.status === "active") {
                slideToDebugRef.current = {
                  ...slideToDebugRef.current,
                  landingView,
                  landingYawDelta: yawDelta,
                };
              }
              // Counter-rotate the crossfade sphere so the old panorama appears unchanged
              const crossfadeSphere = crossfadeSphereRef.current;
              if (crossfadeSphere) {
                crossfadeSphere.rotation.y = Math.PI / 2 - THREE.MathUtils.degToRad(yawDelta);
              }
              applyDisplayCoordsToCamera(camera, adjustedYaw, adjustedPitch);
              camera.updateProjectionMatrix();
            }
          }
        }
        // Store FOV zoom params for the isCrossfadeArrival block below,
        // preserving the hotspot direction set in startCrossfadeTransition.
        const existingHotspotDir = crossfadeFovZoomRef.current?.hotspotDirection;
        const existingRecenterAtEnd = crossfadeFovZoomRef.current?.recenterAtEnd !== false;
        if (!isFadeTransition) {
          const rotatedHotspotDir =
            existingHotspotDir && slideToYawDelta !== 0
              ? existingHotspotDir
                  .clone()
                  .applyAxisAngle(new THREE.Vector3(0, 1, 0), -THREE.MathUtils.degToRad(slideToYawDelta))
                  .normalize()
              : existingHotspotDir;
          crossfadeFovZoomRef.current = {
            startFov: restoreFov,
            zoomedFov: restoreFov,
            endFov: restoreFov,
            hotspotDirection: rotatedHotspotDir,
            recenterAtEnd: existingRecenterAtEnd,
          };
        } else {
          crossfadeFovZoomRef.current = null;
        }
      }

      if (pendingCameraDrivenArrivalRef.current?.panoramaId === panorama.id) {
        const destinationView = cameraToDisplayCoords(camera);
        const destinationFov = camera.fov;
        const incomingFov = Math.min(
          pendingCameraDrivenArrivalRef.current.incomingFov,
          Math.max(18, destinationFov),
        );

        pendingCameraDrivenArrivalRef.current = null;
        camera.fov = incomingFov;
        camera.updateProjectionMatrix();

        animateCameraTransition({
          targetYaw: destinationView.yaw,
          targetPitch: destinationView.pitch,
          targetFov: destinationFov,
          duration: 560,
          easing: easeOutCubic,
          onComplete: () => {
            isPanoramaTransitioningRef.current = false;
            setIsMorphTransitionActive(false);
            setPendingPanoramaViewRef.current(null);
            navigationLandingViewRef.current = null;
          },
        });
      }

      if (isCrossfadeArrival) {
        window.requestAnimationFrame(() => {
          if (sphere.material !== material) return;

          // Don't rotate camera here — the crossfade sphere still shows the old
          // panorama at full opacity. The arrival animation in the
          // pendingCrossfadeArrival block handles the camera transition.

          if (crossfadeFovZoomRef.current?.recenterAtEnd !== false) {
            camera.position.set(0, 0, 0);
          }
          const cameraAtReveal = cameraToDisplayCoords(camera);
          logCrossfadeDebug("applyTexture:revealIncoming", {
            panoramaId: panorama.id,
            yaw: cameraAtReveal.yaw,
            pitch: cameraAtReveal.pitch,
            fov: camera.fov,
          });
          // Crossfade sphere stays at full opacity — startCrossfadeFadeOut
          // will gradually fade it out while fading the incoming material in.
          // Pass FOV zoom for V-shaped dolly effect (zoom in then out).
          const fovZoom = crossfadeFovZoomRef.current;
          crossfadeFovZoomRef.current = null;
          startCrossfadeFadeOut(
            crossfadeArrivalFadeDurationMs ??
              (crossfadeArrivalIsFade ? 500 : PANORAMA_CROSSFADE_TOTAL_DURATION_MS),
            material,
            fovZoom ?? undefined,
            crossfadeArrivalFadeColor,
          );
        });
        return;
      }

      startCrossfadeFadeOut();
    };

  const loadOptimizedTexture = async (
    source: string | ArrayBuffer,
    key: string,
    mimeType: string = "image/jpeg",
    cacheTexture = true,
  ): Promise<THREE.Texture> => {
      // Return cached texture immediately — avoids re-decoding on panorama switch
      const cached = textureCacheRef.current.get(key);
      if (cached && cacheTexture) {
        cached.needsUpdate = true; // Re-upload if GPU resources were freed
        return cached;
      }

      let blob: Blob;
      if (typeof source === "string") {
        // Load blob — XHR for app:// (Electron custom protocol), fetch for http/https
        if (source.startsWith("app://")) {
          blob = await new Promise<Blob>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("GET", source);
            xhr.responseType = "blob";
            xhr.onload = () =>
              xhr.status < 400
                ? resolve(xhr.response as Blob)
                : reject(new Error(`HTTP ${xhr.status}`));
            xhr.onerror = () => reject(new Error(`XHR failed for ${source}`));
            xhr.send();
          });
        } else {
          const response = await fetch(source, {
            method: "GET",
            mode: "cors",
            credentials: "omit",
            referrerPolicy: "no-referrer",
            cache: "no-store",
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          blob = await response.blob();
        }
      } else {
        blob = new Blob([source], { type: mimeType });
      }

      const renderer = rendererRef.current;
      const maxTextureSize = renderer?.capabilities.maxTextureSize || 8192;
      const targetMax = Math.min(maxTextureSize, 8192);

      // Read first 64 KB to get image dimensions from file header — no full decode needed.
      // This lets us call createImageBitmap exactly once with the right resize params,
      // instead of decoding the full ~540 MB uncompressed image just to read its width/height.
      const headerBuf = await blob.slice(0, 65536).arrayBuffer();
      const dims = parseDimensionsFromHeader(new Uint8Array(headerBuf));

      const bitmapOpts: Record<string, unknown> = { imageOrientation: "flipY" };
      if (dims && (dims.w > targetMax || dims.h > targetMax)) {
        const scale = Math.min(targetMax / dims.w, targetMax / dims.h);
        bitmapOpts.resizeWidth = Math.max(1, Math.floor(dims.w * scale));
        bitmapOpts.resizeHeight = Math.max(1, Math.floor(dims.h * scale));
        bitmapOpts.resizeQuality = "high";
      }

      const bitmap = await createImageBitmap(blob, bitmapOpts as ImageBitmapOptions);
      const texture = new THREE.Texture(bitmap);
      texture.needsUpdate = true;

      if (cacheTexture) {
        textureCacheRef.current.set(key, texture);
      }
      return texture;
  };

    // Function to load texture with retry and better error handling
    const loadTexture = async (
      source: ArrayBuffer | string,
      cacheKey: string,
      retryCount = 0,
      options: {
        mimeType?: string;
        isCached?: boolean;
        cacheTexture?: boolean;
        requestId?: number;
        suppressLoadingState?: boolean;
        suppressFailureToasts?: boolean;
      } = {},
    ) => {
      try {
        if (options.requestId !== undefined && options.requestId !== panoramaLoadRequestIdRef.current) {
          return false;
        }
        const optimizedTexture = await loadOptimizedTexture(
          source,
          cacheKey,
          options.mimeType || "image/jpeg",
          options.cacheTexture !== false,
        );
        if (options.requestId !== undefined && options.requestId !== panoramaLoadRequestIdRef.current) {
          return false;
        }
        applyTexture(optimizedTexture);
        return true;
      } catch (error) {
        // Cached data failed - fall back to network URL path.
        if (options.isCached) {
          throw error;
        }

        if (typeof source !== "string") {
          throw error;
        }

        // Fallback to TextureLoader for browsers that block bitmap decode paths.
        try {
          const texture = await new Promise<THREE.Texture>((resolve, reject) => {
            const loader = new THREE.TextureLoader();
            loader.setCrossOrigin("anonymous");
            loader.load(source, resolve, undefined, reject);
          });
          applyTexture(texture);
          return true;
        } catch (loaderError) {
          console.error("❌ Error loading panorama:", loaderError || error);

          // Try one cache-busted request first (helps when stale cache is reused in another browser/profile).
          if (
            retryCount === 0 &&
            typeof source === "string" &&
            (source.startsWith("http://") || source.startsWith("https://"))
          ) {
            const separator = source.includes("?") ? "&" : "?";
            const cacheBustedSource = `${source}${separator}cb=${Date.now()}`;
            return loadTexture(cacheBustedSource, cacheKey, retryCount + 1, {
              requestId: options.requestId,
              suppressLoadingState: options.suppressLoadingState,
              suppressFailureToasts: options.suppressFailureToasts,
            });
          }

          // If first attempt failed and we haven't retried yet, try refreshing the URL
          if (retryCount === 1) {
            const newUrl = await refreshUrl(source);

            if (newUrl) {
              return loadTexture(newUrl, cacheKey, retryCount + 1, {
                requestId: options.requestId,
              });
            }
          }

          // If second attempt also failed, try using the refreshPrivateUrls function
          if (retryCount === 2) {
            try {
              const { refreshProjectUrls, forceCloudStorage } = await import(
                "@/utils/refreshPrivateUrls"
              );

              // First try to force cloud storage provider if S3 is failing
              try {
                await forceCloudStorage(projectCraftspaceId);
              } catch (forceError) {
                console.warn("⚠️ Could not force cloud storage provider:", forceError);
              }

              const result = await refreshProjectUrls(projectId);

              if (result.success && result.refreshedCount > 0) {
                // Get the updated panorama from the store
                const { panoramas } = useEditorStore.getState();
                const updatedPanorama = panoramas.find((p) => p.id === panorama.id);
                const updatedData =
                  (updatedPanorama?.data as Record<string, unknown> | undefined) || {};
                const updatedOriginalFileUrl =
                  typeof updatedData.original_file_url === "string"
                    ? updatedData.original_file_url
                    : null;
                const updatedFallbackUrl = getPreferredPanoramaUrl([
                  updatedOriginalFileUrl,
                  updatedPanorama?.private_file_url,
                  updatedPanorama?.file_url_webp,
                  updatedPanorama?.file_url,
                ]);
                if (updatedFallbackUrl) {
                  return loadTexture(updatedFallbackUrl, cacheKey, retryCount + 1, {
                    requestId: options.requestId,
                  });
                }
              }
            } catch (refreshError) {
              console.error("❌ Global refresh failed:", refreshError);
            }
          }

          if (!options.suppressLoadingState) {
            setIsTextureLoading(false);
          }

          // Add a small delay to allow texture to load before showing error
          if (!options.suppressFailureToasts) {
            setTimeout(() => {
            // Only show error toast if we've actually failed to load the panorama
            // Don't show error if the panorama is actually displaying (check if sphere has a texture)
            const sphere = sphereRef.current;
            if (sphere?.material) {
              // Handle both single material and material array
              const materials = Array.isArray(sphere.material)
                ? sphere.material
                : [sphere.material];
              for (const material of materials) {
                if (material && "map" in material && material.map) {
                  // Check if the texture is actually loaded and has content
                  const texture = material.map as THREE.Texture;
                  if (texture?.image?.complete && texture.image.naturalWidth > 0) {
                    // Panorama is actually loaded and visible, don't show error
                    return;
                  }
                }
              }
            }

            // Show error toast only after retry failed and panorama is not displaying
            toast({
              variant: "destructive",
              title: "Failed to load panorama",
              description:
                retryCount > 2
                  ? `Could not load "${panorama.name}" even after multiple refresh attempts. Please check your storage configuration.`
                  : `Could not load "${panorama.name}". The image may be corrupted or inaccessible.`,
            });
            }, 1000); // Wait 1 second before showing error
          }

          if (!options.suppressFailureToasts) {
            markPanoramaLoadFailure();
          }
        }
      }

      return false;
    };

    startPanoramaLoad();
  }, [
    panorama,
    panoramaLoadAttempt,
    projectId,
    projectCraftspaceId,
    convex,
    getPanorama,
    startCrossfadeFadeOut,
  ]);

  // Process hotspots when they change or preview mode toggles
  useEffect(() => {
    if (!hotspotManagerRef.current) return;
    hotspotManagerRef.current.setRenderingEnabled(!isTextureLoading);
    hotspotManagerRef.current.setPreviewMode(isPreviewMode);

    if (isTextureLoading) {
      hotspotManagerRef.current.processHotspots([]).catch(console.error);
      hideSelectionOverlayFnRef.current();
      return;
    }

    hotspotManagerRef.current.processHotspots(currentHotspots).catch(console.error);
  }, [currentHotspots, isTextureLoading, isPreviewMode]);

  // Update current route in hotspot manager
  useEffect(() => {
    if (hotspotManagerRef.current) {
      hotspotManagerRef.current.setCurrentRoute(currentRoute);
    }
  }, [currentRoute]);

  // Update hotspot render style (billboard vs 3D)
  useEffect(() => {
    if (hotspotManagerRef.current) {
      hotspotManagerRef.current.setRenderStyle(hotspotRenderStyle);
    }
  }, [hotspotRenderStyle]);

  // Track placement mode changes to detect newly placed hotspots
  useEffect(() => {
    // If placement mode just turned off, mark the next selected hotspot as "just placed"
    if (lastPlacementModeRef.current && !placementMode) {
      justPlacedHotspotRef.current = "pending"; // Will be set to actual ID when hotspot is selected
    }
    lastPlacementModeRef.current = placementMode;
  }, [placementMode]);

  // Focus camera on selected hotspot (disabled to keep camera stable on drop)
  useEffect(() => {
    if (selectedHotspot && cameraRef.current) {
      const wasJustPlaced =
        justPlacedHotspotRef.current === "pending" ||
        justPlacedHotspotRef.current === selectedHotspot.id;

      if (focusHotspotRequestId === selectedHotspot.id && !wasJustPlaced) {
        focusCameraOnHotspot(selectedHotspot);
        requestFocusHotspot(null);
      }

      if (wasJustPlaced) {
        justPlacedHotspotRef.current = selectedHotspot.id;
        setTimeout(() => {
          justPlacedHotspotRef.current = null;
        }, 1000);
      }
    }
  }, [focusCameraOnHotspot, focusHotspotRequestId, requestFocusHotspot, selectedHotspot]);

  // Listen for camera movement events from PanoramaPropertiesPanel
  useEffect(() => {
    const handleMoveCameraToView = (event: CustomEvent) => {
      const { yaw, pitch, fov } = event.detail;

      if (cameraRef.current) {
        // Apply the camera rotation using the coordinate system utilities
        applyDisplayCoordsToCamera(cameraRef.current, yaw, pitch);

        // Update FOV if provided
        if (fov !== undefined) {
          cameraRef.current.fov = fov;
          cameraRef.current.updateProjectionMatrix();
        }
      }
    };

    window.addEventListener("moveCameraToView", handleMoveCameraToView as EventListener);

    return () => {
      window.removeEventListener("moveCameraToView", handleMoveCameraToView as EventListener);
    };
  }, []);

  // Listen for map overlay open events
  useEffect(() => {
    const handleOpenMapOverlay = (_event: CustomEvent) => {
      setShowMapOverlay(true);
    };

    window.addEventListener("openMapOverlay", handleOpenMapOverlay as EventListener);

    return () => {
      window.removeEventListener("openMapOverlay", handleOpenMapOverlay as EventListener);
    };
  }, []);

  // Close map overlay when panorama changes
  useEffect(() => {
    if (showMapOverlay && panorama) {
      const gpsLocation = panorama.gps_location as {
        lat?: number;
        lng?: number;
        latitude?: number;
        longitude?: number;
      } | null;
      if (!gpsLocation) {
        setShowMapOverlay(false);
        return;
      }
      const lat = gpsLocation.lat ?? gpsLocation.latitude;
      const lng = gpsLocation.lng ?? gpsLocation.longitude;
      if (lat === undefined || lng === undefined) {
        setShowMapOverlay(false);
      }
    }
  }, [panorama, showMapOverlay]);

  // Extract connections from hotspots for the overlay map
  const extractConnections = useCallback((): Connection[] => {
    if (hotspots.length === 0 || panoramas.length === 0) {
      return [];
    }

    const existingConnections: Connection[] = [];
    const connectionSet = new Set<string>();

    hotspots.forEach((hotspot) => {
      const content = hotspot.content as HotspotContentRecord;
      if (content?.linkedPanoramaId) {
        const fromPanorama = panoramas.find((p) => p.id === hotspot.panorama_id);
        const toPanorama = panoramas.find((p) => p.id === content.linkedPanoramaId);

        if (fromPanorama && toPanorama) {
          const gpsFrom = fromPanorama.gps_location as {
            lat?: number;
            lng?: number;
            latitude?: number;
            longitude?: number;
          } | null;
          const gpsTo = toPanorama.gps_location as {
            lat?: number;
            lng?: number;
            latitude?: number;
            longitude?: number;
          } | null;

          if (gpsFrom && gpsTo) {
            const latFrom = gpsFrom.lat ?? gpsFrom.latitude;
            const lngFrom = gpsFrom.lng ?? gpsFrom.longitude;
            const latTo = gpsTo.lat ?? gpsTo.latitude;
            const lngTo = gpsTo.lng ?? gpsTo.longitude;
            if (
              latFrom === undefined ||
              lngFrom === undefined ||
              latTo === undefined ||
              lngTo === undefined
            ) {
              return;
            }

            const connectionKey = `${fromPanorama.id}-${toPanorama.id}`;
            if (!connectionSet.has(connectionKey)) {
              connectionSet.add(connectionKey);
              existingConnections.push({
                fromPanoramaId: fromPanorama.id,
                toPanoramaId: toPanorama.id,
                fromLat: latFrom,
                fromLng: lngFrom,
                toLat: latTo,
                toLng: lngTo,
              });
            }
          }
        }
      }
    });

    return existingConnections;
  }, [hotspots, panoramas]);

  // Render connection arrows on overlay map
  const renderOverlayConnections = useCallback(async (connectionsToRender: Connection[]) => {
    if (!mapInstanceRef.current || connectionsToRender.length === 0) return;

    const L = await import("leaflet");
    const map = mapInstanceRef.current;

    // Clear existing connections
    overlayConnectionsRef.current.forEach((connection) => {
      map.removeLayer(connection);
    });
    overlayConnectionsRef.current.clear();

    // Draw each connection
    for (const connection of connectionsToRender) {
      const fromMarker = overlayMarkersRef.current.get(connection.fromPanoramaId);
      const toMarker = overlayMarkersRef.current.get(connection.toPanoramaId);

      if (!fromMarker || !toMarker) continue;

      const fromLatLng = fromMarker.getLatLng();
      const toLatLng = toMarker.getLatLng();

      // Create polyline
      const polyline = L.polyline([fromLatLng, toLatLng], {
        color: "#3B82F6",
        weight: 2,
        opacity: 0.7,
        dashArray: "5, 5",
        interactive: false,
      }).addTo(map);

      // Calculate bearing for arrow
      const dx = toLatLng.lng - fromLatLng.lng;
      const dy = toLatLng.lat - fromLatLng.lat;
      const bearing = (Math.atan2(dx, dy) * 180) / Math.PI;

      // Create arrow markers along the line
      const linePoints = [fromLatLng, toLatLng];
      const startPoint = map.latLngToContainerPoint(linePoints[0]);
      const endPoint = map.latLngToContainerPoint(linePoints[1]);
      const totalDistance = Math.sqrt(
        (endPoint.x - startPoint.x) ** 2 + (endPoint.y - startPoint.y) ** 2,
      );

      const arrowSpacing = 40;
      const numArrowsToCreate = Math.max(2, Math.floor(totalDistance / arrowSpacing));

      for (let i = 1; i <= numArrowsToCreate; i++) {
        const t = i / (numArrowsToCreate + 1);
        const arrowPoint = L.point(
          startPoint.x + (endPoint.x - startPoint.x) * t,
          startPoint.y + (endPoint.y - startPoint.y) * t,
        );
        const arrowLatLng = map.containerPointToLatLng(arrowPoint);

        const arrowMarker = L.marker(arrowLatLng, {
          icon: L.divIcon({
            html: `<div style="
              width: 0;
              height: 0;
              border-left: 4px solid transparent;
              border-right: 4px solid transparent;
              border-bottom: 8px solid #3B82F6;
              transform: rotate(${bearing}deg);
              transform-origin: center;
            "></div>`,
            className: "connection-arrow",
            iconSize: [8, 8],
            iconAnchor: [4, 8],
          }),
        }).addTo(map);

        overlayConnectionsRef.current.set(
          `${connection.fromPanoramaId}-${connection.toPanoramaId}-arrow-${i}`,
          arrowMarker,
        );
      }

      overlayConnectionsRef.current.set(
        `${connection.fromPanoramaId}-${connection.toPanoramaId}`,
        polyline,
      );
    }
  }, []);

  // Initialize map overlay when shown
  useEffect(() => {
    if (!showMapOverlay || !mapOverlayRef.current || !panorama) return;

    const gpsLocation = panorama.gps_location as {
      lat?: number;
      lng?: number;
      latitude?: number;
      longitude?: number;
    } | null;
    if (!gpsLocation) {
      setShowMapOverlay(false);
      return;
    }

    const lat = gpsLocation.lat ?? gpsLocation.latitude;
    const lng = gpsLocation.lng ?? gpsLocation.longitude;

    if (lat === undefined || lng === undefined) {
      setShowMapOverlay(false);
      return;
    }

    const initMap = async () => {
      try {
        const L = await import("leaflet");
        await import("leaflet/dist/leaflet.css");

        if (!mapOverlayRef.current) return;

        // Remove existing map if any
        if (mapInstanceRef.current) {
          // Clear markers and connections
          overlayMarkersRef.current.forEach((marker) => {
            mapInstanceRef.current.removeLayer(marker);
          });
          overlayMarkersRef.current.clear();
          overlayConnectionsRef.current.forEach((connection) => {
            mapInstanceRef.current.removeLayer(connection);
          });
          overlayConnectionsRef.current.clear();

          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }

        const map = L.map(mapOverlayRef.current);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
          maxZoom: 19,
        }).addTo(map);

        mapInstanceRef.current = map;

        // Load all panorama markers
        const panoramaLocations: [number, number][] = [];
        let currentPanoramaMarker: unknown = null;

        for (const p of panoramas) {
          const pGpsLocation = p.gps_location as {
            lat?: number;
            lng?: number;
            latitude?: number;
            longitude?: number;
          } | null;
          if (!pGpsLocation) continue;

          const pLat = pGpsLocation.lat ?? pGpsLocation.latitude;
          const pLng = pGpsLocation.lng ?? pGpsLocation.longitude;

          if (pLat === undefined || pLng === undefined) continue;

          panoramaLocations.push([pLat, pLng]);

          try {
            // Get thumbnail
            let thumbnailUrl: string | null = null;

            if (isCacheInitialized) {
              try {
                let cachedThumbnail = await getThumbnail(p.id, true);
                if (!cachedThumbnail) {
                  cachedThumbnail = await getThumbnail(p.id, false);
                }
                if (cachedThumbnail) {
                  thumbnailUrl = createBlobURL(cachedThumbnail.data, cachedThumbnail.mimeType);
                }
              } catch (_cacheError) {
                // Cache error, fall through
              }
            }

            if (!thumbnailUrl) {
              thumbnailUrl = p.thumbnail_url_webp || p.thumbnail_url || null;
            }

            const iconSize = p.id === panorama.id ? 30 : 25; // Slightly larger for current panorama
            const iconHtml = thumbnailUrl
              ? `<div style="
                  width: ${iconSize}px;
                  height: ${iconSize}px;
                  border-radius: 50%;
                  border: 3px solid ${p.id === panorama.id ? "#3B82F6" : "white"};
                  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                  overflow: hidden;
                  background: #1F2937;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">
                  <img 
                    src="${thumbnailUrl}" 
                    style="width: 100%; height: 100%; object-fit: cover;"
                    alt="${p.name}"
                  />
                </div>`
              : `<div style="
                  width: ${iconSize}px;
                  height: ${iconSize}px;
                  border-radius: 50%;
                  border: 3px solid ${p.id === panorama.id ? "#3B82F6" : "white"};
                  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                  background: ${p.id === panorama.id ? "#3B82F6" : "#6B7280"};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: white;
                  font-size: ${iconSize === 30 ? "16px" : "14px"};
                ">📍</div>`;

            const customIcon = L.divIcon({
              html: iconHtml,
              className: "panorama-map-marker",
              iconSize: [iconSize, iconSize],
              iconAnchor: [iconSize / 2, iconSize / 2],
            });

            const marker = L.marker([pLat, pLng], { icon: customIcon }).addTo(map);
            overlayMarkersRef.current.set(p.id, marker);

            if (p.id === panorama.id) {
              currentPanoramaMarker = marker;
              mapMarkerRef.current = marker;
            }
          } catch (error) {
            console.error(`Failed to create marker for panorama ${p.id}:`, error);
          }
        }

        // Set map view to fit all panoramas or center on current
        if (panoramaLocations.length > 0) {
          if (panoramaLocations.length === 1) {
            map.setView(panoramaLocations[0], 15);
          } else {
            const bounds = L.latLngBounds(panoramaLocations);
            map.fitBounds(bounds, { padding: [20, 20], maxZoom: 16 });
          }
        }

        // Extract and render connections
        const connections = extractConnections();
        if (connections.length > 0) {
          setTimeout(() => {
            renderOverlayConnections(connections);
          }, 200);
        }

        // Add radar to Leaflet's overlay pane (z-index 400, below markers at 600)
        if (radarOverlayRef.current) {
          const overlayPane = map.getPane("overlayPane");
          if (overlayPane && radarOverlayRef.current.parentNode !== overlayPane) {
            overlayPane.appendChild(radarOverlayRef.current);
            // Set z-index to ensure it's below markers (markers are at 600)
            radarOverlayRef.current.style.zIndex = "400";
          }
        }

        // Function to update radar position
        const updateRadarPosition = () => {
          // Don't update position while dragging radar
          if (isDraggingRadarRef.current) return;

          if (!map || !radarOverlayRef.current || !mapOverlayRef.current || !currentPanoramaMarker)
            return;

          const markerLatLng = currentPanoramaMarker.getLatLng();
          const containerPoint = map.latLngToContainerPoint(markerLatLng);

          if (radarOverlayRef.current) {
            radarOverlayRef.current.style.left = `${containerPoint.x}px`;
            radarOverlayRef.current.style.top = `${containerPoint.y}px`;
            radarOverlayRef.current.style.marginLeft = "-50px";
            radarOverlayRef.current.style.marginTop = "-50px";
          }
        };

        // Update radar position initially and on map events
        setTimeout(() => {
          map.invalidateSize();
          updateRadarPosition();
        }, 100);

        // Update radar position when map moves or zooms
        map.on("move", updateRadarPosition);
        map.on("zoom", updateRadarPosition);
        map.on("resize", updateRadarPosition);
      } catch (error) {
        console.error("Failed to load map:", error);
      }
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        // Remove event listeners
        const map = mapInstanceRef.current;
        map.off("move");
        map.off("zoom");
        map.off("resize");

        // Clear markers and connections
        overlayMarkersRef.current.forEach((marker) => {
          map.removeLayer(marker);
        });
        overlayMarkersRef.current.clear();
        overlayConnectionsRef.current.forEach((connection) => {
          map.removeLayer(connection);
        });
        overlayConnectionsRef.current.clear();

        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        mapMarkerRef.current = null;
        radarPositionRef.current = null;
      }
    };
  }, [
    showMapOverlay,
    panorama,
    panoramas,
    extractConnections,
    renderOverlayConnections,
    isCacheInitialized,
    getThumbnail,
    createBlobURL,
  ]);

  // Update FOV indicator rotation and size based on camera yaw and FOV
  useEffect(() => {
    if (!showMapOverlay || !radarOverlayRef.current || !panorama) return;

    // Get north offset from panorama data if available, or use temp offset during drag
    const panoramaData = panorama.data as PanoramaDataRecord | undefined;
    const northOffset = tempNorthOffset !== null ? tempNorthOffset : (panoramaData?.north ?? 0);

    // Get current FOV from camera stats
    const fovDegrees = cameraStats.fov;
    const yawDegrees = cameraStats.yaw;

    // Adjust for north offset: if north is set to 45°, then yaw 45° should point north on the map
    // The yaw from cameraToDisplayCoords uses: yaw = -deg(euler.y)
    // When camera rotates right: euler.y increases (positive) → yaw becomes negative
    // On the map: pointing right (east) = clockwise rotation = positive CSS rotation
    // So we need to invert the sign: rotation = -yaw (negative yaw → positive rotation)
    const relativeYaw = yawDegrees - northOffset;

    // Update FOV indicator rotation and size
    if (radarOverlayRef.current) {
      // Rotate the entire indicator based on yaw
      // CSS rotation: positive = clockwise, negative = counter-clockwise
      // The yaw from cameraToDisplayCoords: yaw = -deg(euler.y)
      // When dragging right: euler.y increases → yaw becomes negative
      // But the yaw value shown to user (cameraStats.yaw) is already negated
      // So when user sees yaw = 49.7° (looking right), we want clockwise rotation (point right)
      // Therefore: use relativeYaw directly (no negation) for CSS rotation
      radarOverlayRef.current.style.transform = `rotate(${relativeYaw}deg)`;

      // Update the FOV arc size based on camera FOV
      const arcElement = radarOverlayRef.current.querySelector(".fov-arc") as SVGPathElement;
      if (arcElement) {
        // Calculate arc path for the FOV angle
        // FOV is the total angle, so we need half on each side (FOV/2)
        const halfFov = fovDegrees / 2;
        const radius = 45; // Radius of the arc circle
        const startAngle = -halfFov;
        const endAngle = halfFov;

        // Convert angles to radians
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;

        // Calculate arc path
        const startX = 50 + radius * Math.sin(startRad);
        const startY = 50 - radius * Math.cos(startRad);
        const endX = 50 + radius * Math.sin(endRad);
        const endY = 50 - radius * Math.cos(endRad);

        // Large arc flag: 1 if angle > 180°, 0 otherwise
        const largeArcFlag = fovDegrees > 180 ? 1 : 0;

        // Create the arc path
        const arcPath = `M 50 50 L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
        arcElement.setAttribute("d", arcPath);
      }
    }
  }, [cameraStats.yaw, cameraStats.fov, showMapOverlay, panorama, tempNorthOffset]);

  // Handle radar drag to calibrate north orientation
  const handleRadarMouseDown = (e: React.MouseEvent) => {
    if (!panorama || !radarOverlayRef.current || !mapOverlayRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    // Disable map dragging while dragging radar
    if (mapInstanceRef.current) {
      mapInstanceRef.current.dragging.disable();
    }

    const panoramaData = panorama.data as PanoramaDataRecord | undefined;
    const initialNorthOffset = panoramaData?.north ?? 0;
    const initialYaw = cameraStats.yaw;

    radarDragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      initialNorthOffset,
      initialYaw,
    };

    setIsDraggingRadar(true);
    isDraggingRadarRef.current = true;
    setTempNorthOffset(initialNorthOffset);
    tempNorthOffsetRef.current = initialNorthOffset;
  };

  // Add global mouse event listeners for radar dragging
  useEffect(() => {
    if (!isDraggingRadar) return;

    const handleRadarMouseMove = (e: MouseEvent) => {
      if (!radarDragStartRef.current || !radarOverlayRef.current || !panorama) return;

      // Prevent map from panning
      e.preventDefault();
      e.stopPropagation();

      const rect = radarOverlayRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate angle from center to mouse position
      const dx = e.clientX - centerX;
      const dy = e.clientY - centerY;

      // Calculate angle in degrees (0° = up/north, increases clockwise)
      // Math.atan2(dx, -dy) gives us angle where -dy is up (north)
      const angle = Math.atan2(dx, -dy) * (180 / Math.PI);

      // Convert map angle to north offset
      // The angle represents where the radar is pointing on the map
      // We need to calculate what north offset would make the current yaw point in that direction
      // Formula: northOffset = yaw - angle
      const currentYaw = radarDragStartRef.current.initialYaw;
      let newNorthOffset = currentYaw - angle;
      newNorthOffset = normalizeAngle(newNorthOffset);

      setTempNorthOffset(newNorthOffset);
      tempNorthOffsetRef.current = newNorthOffset;
    };

    const handleRadarMouseUp = async () => {
      if (!radarDragStartRef.current || !panorama) return;

      // Re-enable map dragging
      if (mapInstanceRef.current) {
        mapInstanceRef.current.dragging.enable();
      }

      setIsDraggingRadar(false);
      isDraggingRadarRef.current = false;

      // Get the current temp offset value from ref (always up to date)
      const finalNorthOffset = tempNorthOffsetRef.current;
      if (finalNorthOffset === null) {
        radarDragStartRef.current = null;
        setTempNorthOffset(null);
        tempNorthOffsetRef.current = null;
        return;
      }

      try {
        // Update panorama data with new north offset
        const updatedData = {
          ...(panorama.data || {}),
          north: finalNorthOffset,
        };

        const { updatePanorama } = useEditorStore.getState();
        await updatePanorama(panorama.id, {
          data: updatedData,
        });

        // Get current hotspots and panoramas from store
        const currentState = useEditorStore.getState();
        const currentHotspots = currentState.hotspots;
        const currentPanoramas = currentState.panoramas;

        // Reposition existing navigation hotspots
        const repositioned = await repositionHotspotsForNorthChange(
          { ...panorama, data: updatedData },
          currentHotspots,
          currentPanoramas,
        );

        // Refresh hotspots from database
        const hotspotsData = await convex.query(api.hotspots.listByProject, {
          projectId: panorama.project_id,
        });
        if (hotspotsData) {
          const { setHotspots } = useEditorStore.getState();
          setHotspots(hotspotsData);
        }

        toast({
          title: "North orientation calibrated",
          description: `North has been set to ${finalNorthOffset.toFixed(1)}°. ${repositioned > 0 ? `${repositioned} navigation hotspot${repositioned > 1 ? "s" : ""} repositioned.` : ""}`,
        });

        setTempNorthOffset(null);
        tempNorthOffsetRef.current = null;
        radarDragStartRef.current = null;
      } catch (error) {
        console.error("Error updating north offset:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to save north orientation.",
        });
        // Revert temp offset on error
        setTempNorthOffset(null);
        tempNorthOffsetRef.current = null;
        radarDragStartRef.current = null;
      }
    };

    document.addEventListener("mousemove", handleRadarMouseMove);
    document.addEventListener("mouseup", handleRadarMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleRadarMouseMove);
      document.removeEventListener("mouseup", handleRadarMouseUp);
    };
  }, [convex, isDraggingRadar, panorama, toast]);

  // ============================================================================
  // DRAG AND DROP HANDLERS (Preserved from original)
  // ============================================================================

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isPreviewModeRef.current || placementModeRef.current) return;
    // Lock context menu in skin editor when not in preview mode
    if (currentRouteRef.current === "skin" && !isPreviewModeRef.current) return;
    if (!cameraRef.current || !sphereRef.current) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(new THREE.Vector2(nx, ny), cameraRef.current);
    const intersects = raycasterRef.current.intersectObject(sphereRef.current, false);

    let position3D = { x: 0, y: 0, z: -400 };
    if (intersects.length > 0) {
      const pt = intersects[0].point;
      position3D = { x: pt.x, y: pt.y, z: pt.z };
    }

    // Check if we right-clicked on a hotspot
    const hotspotId = pickHotspotAtPointRef.current?.(e.clientX, e.clientY) ?? undefined;

    setContextMenu({ x: e.clientX - rect.left, y: e.clientY - rect.top, position3D, hotspotId });
  };

  const handleContextMenuQuickEdit = () => {
    if (!contextMenu?.hotspotId) return;
    const hotspot = useEditorStore.getState().hotspots.find((h) => h.id === contextMenu.hotspotId);
    if (!hotspot) return;
    const { x, y } = contextMenu;
    setContextMenu(null);
    useEditorStore.getState().setSelectedHotspot(hotspot);
    setContextualPopoverPos({ x, y });
  };

  const handleContextMenuAddHotspot = async () => {
    if (!contextMenu) return;
    const pos = contextMenu.position3D;
    const clickX = contextMenu.x;
    const clickY = contextMenu.y;
    setContextMenu(null);
    const currentUser = getPlacementUser();
    if (currentUser) {
      try {
        const store = useEditorStore.getState();
        const prevPlacementType = store.placementType;
        store.setPlacementType("html");
        await store.requestHotspotPlacement(pos, currentUser);
        useEditorStore.getState().setPlacementType(prevPlacementType);
        setContextualPopoverPos({ x: clickX, y: clickY });
      } catch (error) {
        console.error("Error placing hotspot:", error);
        toastRef.current({
          variant: "destructive",
          title: "Error placing hotspot",
          description: "Failed to create hotspot at this location.",
        });
      }
    }
  };

  const handleContextMenuDeleteHotspot = async () => {
    if (!contextMenu?.hotspotId) return;
    const hotspotId = contextMenu.hotspotId;
    setContextMenu(null);
    await handleFloatingDeleteHotspot(hotspotId);
  };

  const handleSetDefaultView = async () => {
    if (!contextMenu || !panorama || !cameraRef.current) return;
    setContextMenu(null);

    const { yaw, pitch } = cameraToDisplayCoords(cameraRef.current);
    const fov = cameraRef.current.fov;

    // Persist to panorama data
    const currentData = (panorama.data as PanoramaDataRecord) || {};
    const updatedData: PanoramaDataRecord = { ...currentData, initialView: { yaw, pitch, fov } };

    try {
      if (isElectron()) {
        await updateLocalPanorama({ id: panorama.id, craftspace_id: projectCraftspaceId, data: updatedData });
      } else {
        await convex.mutation(api.panoramas.update, { id: panorama.id, data: updatedData });
      }
      useEditorStore.getState().updatePanorama(panorama.id, { data: updatedData });
      useEditorStore.getState().setDefaultViewSaveEvent({
        panoramaId: panorama.id,
        view: { yaw, pitch, fov },
        nonce: Date.now(),
      });
      const sound = clickSoundRef.current ?? new Audio("/sounds/click.mp3");
      clickSoundRef.current = sound;
      sound.currentTime = 0;
      sound.play().catch(() => {});
    } catch (error) {
      console.error("Error setting default view:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to save default view." });
    }
  };

  const handleFloatingUpdateHotspot = async (hotspotId: string, updates: Partial<Hotspot>) => {
    const existingHotspot = useEditorStore
      .getState()
      .hotspots.find((hotspot) => hotspot.id === hotspotId);
    const snapshotBefore = existingHotspot
      ? {
          id: existingHotspot.id,
          project_id: existingHotspot.project_id,
          panorama_id: existingHotspot.panorama_id,
          name: existingHotspot.name,
          type: existingHotspot.type,
          content: existingHotspot.content,
          position: existingHotspot.position,
          rotation: existingHotspot.rotation,
          scale: existingHotspot.scale,
          visible: existingHotspot.visible,
          order: existingHotspot.order,
          cms_action: (existingHotspot as { cms_action?: unknown }).cms_action,
          cmsAction: (existingHotspot as { cmsAction?: unknown }).cmsAction,
        }
      : null;

    useEditorStore.getState().updateHotspot(hotspotId, updates);
    try {
      if (isElectron()) {
        await updateLocalHotspot({ id: hotspotId, craftspace_id: projectCraftspaceId, ...(updates as Parameters<typeof updateLocalHotspot>[0]) });
      } else {
        await convex.mutation(api.hotspots.update, { id: hotspotId, ...updates });
      }

      if (snapshotBefore) {
        const snapshotAfter = { ...snapshotBefore, ...updates };
        useEditorStore
          .getState()
          .recordHotspotUpdateWithSnapshots(hotspotId, snapshotBefore, snapshotAfter);
      }
    } catch (error) {
      console.error("Error updating hotspot from floating panel:", error);
    }
  };

  const handleFloatingDeleteHotspot = async (hotspotId: string) => {
    try {
      const hotspot = useEditorStore.getState().hotspots.find((h) => h.id === hotspotId);
      if (!hotspot) {
        throw new Error("Hotspot not found");
      }

      if (isElectron()) {
        await removeLocalHotspot(hotspotId, projectCraftspaceId);
      } else {
        await convex.mutation(api.hotspots.remove, { id: hotspotId });
      }
      const { setHotspots, setSelectedHotspot, hotspots: currentHotspots } = useEditorStore.getState();
      setHotspots(currentHotspots.filter((h) => h.id !== hotspotId));
      setSelectedHotspot(null);
      setContextualPopoverPos(null);
      useEditorStore.getState().recordHotspotDelete(hotspot);
    } catch (error) {
      console.error("Error deleting hotspot from floating panel:", error);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();

    const hasPanoramaData = e.dataTransfer.types.includes("text/plain");
    const hasComponentData = e.dataTransfer.types.includes("application/json");
    const hasHotspotAdd = e.dataTransfer.types.includes("application/x-hotspot-add");
    const hasHotspotRelocate = e.dataTransfer.types.includes("application/x-hotspot-relocate");

    if (hasPanoramaData && !hasComponentData) {
      e.dataTransfer.dropEffect = "copy";
      setIsDraggingOver(true);
      setDragType("panorama");
    } else if (hasComponentData) {
      e.dataTransfer.dropEffect = "copy";
      setIsDraggingOver(true);
      setDragType("component");
    } else if (hasHotspotAdd) {
      e.dataTransfer.dropEffect = "copy";
      setIsDraggingOver(true);
      setDragType("hotspot-add");
    } else if (hasHotspotRelocate) {
      e.dataTransfer.dropEffect = "move";
      setIsDraggingOver(true);
      setDragType("hotspot-relocate");
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingOver(false);
      setDragType(null);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    // If files are being dropped from the OS, let the window-level handler process them
    if (e.dataTransfer.files.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(false);
      setDragType(null);
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    setDragType(null);

    try {
      // Check for panorama drop (text/plain data)
      const panoramaId = e.dataTransfer.getData("text/plain");
      if (panoramaId) {
        const targetPanorama = panoramas.find((p) => p.id === panoramaId);
        if (targetPanorama && panorama && cameraRef.current && sphereRef.current) {
          // Calculate 3D position from drop coordinates using raycasting
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
          const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

          // Perform raycasting to find intersection with sphere
          const raycaster = raycasterRef.current;
          raycaster.setFromCamera(new THREE.Vector2(x, y), cameraRef.current);

          // Raycast against the sphere
          const intersects = raycaster.intersectObject(sphereRef.current, false);

          // Default position if raycasting fails
          let position = { x: 0, y: 0, z: -400 };

          if (intersects.length > 0) {
            const intersect = intersects[0];
            position = {
              x: intersect.point.x,
              y: intersect.point.y,
              z: intersect.point.z,
            };
          }

          const newHotspot = {
            project_id: panorama.project_id,
            panorama_id: panorama.id,
            name: `${targetPanorama.name} (nav)`,
            type: "html",
            position: position,
            scale: { x: 1, y: 1, z: 1 },
            content: {
              text: "",
              contentType: "icon",
              textColor: "#ffffff",
              backgroundColor: "#2196f3",
              borderColor: "#ffffff",
              borderWidth: 0,
              borderRadius: 0,
              padding: 6,
              fontSize: "24px",
              fontWeight: "700",
              textAlign: "center",
              variant: "default",
              showPointer: false,
              pointerDirection: "bottom",
              pointerSize: 8,
              pointerOverlap: 2,
              tooltipArrowDirection: "top",
              title: `${targetPanorama.name} (nav)`,
              description: `Click to navigate to ${targetPanorama.name}`,
              linkedPanoramaId: targetPanorama.id,
            },
            visible: true,
            order: currentHotspots.length,
          };

          let hotspotData: Hotspot;
          if (isElectron()) {
            const id = await createLocalHotspot({ ...newHotspot, craftspace_id: projectCraftspaceId });
            hotspotData = { ...newHotspot, id, _id: id, _creationTime: Date.now() } as Hotspot;
          } else {
            hotspotData = (await convex.mutation(api.hotspots.create, newHotspot)) as Hotspot;
          }

          const { addHotspot, setSelectedHotspot } = useEditorStore.getState();
          addHotspot(hotspotData);
          setSelectedHotspot(hotspotData);

          setNavDropMenu({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            hotspotId: hotspotData.id,
            targetPanoramaId: targetPanorama.id,
          });

          toast({
            title: "Navigation Hotspot Created",
            description: `Hotspot created to navigate to ${targetPanorama.name}`,
          });
        }
        return;
      }

      // Check for component template drop (application/json data)
      const data = e.dataTransfer.getData("application/json");
      if (data) {
        const parsed = JSON.parse(data);
        if (parsed.type === "component-template" && parsed.componentType) {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          const leftPercent = (x / rect.width) * 100;
          const topPercent = (y / rect.height) * 100;

          const { addSkinComponent, setSelectedSkinComponent } = useEditorStore.getState();

          addSkinComponent(parsed.componentType);

          const currentComponents = useEditorStore.getState().skinComponents;
          const newComponent = currentComponents[currentComponents.length - 1];

          if (newComponent) {
            useEditorStore.getState().updateSkinComponent(newComponent.id, {
              position: {
                left: `${leftPercent.toFixed(1)}%`,
                top: `${topPercent.toFixed(1)}%`,
              },
            });

            setSelectedSkinComponent(newComponent);
          }
        }
        return;
      }

      // Check for hotspot add drag (application/x-hotspot-add data)
      const relocateHotspotId = e.dataTransfer.getData("application/x-hotspot-relocate");
      if (relocateHotspotId && panorama && cameraRef.current && sphereRef.current) {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        // Perform raycasting to find intersection with sphere
        const raycaster = raycasterRef.current;
        raycaster.setFromCamera(new THREE.Vector2(x, y), cameraRef.current);
        const intersects = raycaster.intersectObject(sphereRef.current, false);

        let position = { x: 0, y: 0, z: -400 };
        if (intersects.length > 0) {
          const intersect = intersects[0];
          position = {
            x: intersect.point.x,
            y: intersect.point.y,
            z: intersect.point.z,
          };
        }

        const currentState = useEditorStore.getState();
        const currentUser = getPlacementUser();
        if (currentUser) {
          currentState.setPendingHotspotPlacementId(relocateHotspotId);
          currentState.setPlacementMode(true);
          currentState.setIsDraggingToAddHotspot(false);
          await currentState.requestHotspotPlacement(position, currentUser).catch((error) => {
            console.error("Error placing hotspot:", error);
            toast({
              variant: "destructive",
              title: "Error placing hotspot",
              description: "Failed to position hotspot at this location.",
            });
          });
        }
        return;
      }

      const hotspotType = e.dataTransfer.getData("application/x-hotspot-add");
      if (hotspotType && panorama && cameraRef.current && sphereRef.current) {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        // Perform raycasting to find intersection with sphere
        const raycaster = raycasterRef.current;
        raycaster.setFromCamera(new THREE.Vector2(x, y), cameraRef.current);

        // Raycast against the sphere
        const intersects = raycaster.intersectObject(sphereRef.current, false);

        // Default position if raycasting fails
        let position = { x: 0, y: 0, z: -400 };

        if (intersects.length > 0) {
          const intersect = intersects[0];
          position = {
            x: intersect.point.x,
            y: intersect.point.y,
            z: intersect.point.z,
          };
        }

        const currentState = useEditorStore.getState();
        const currentUser = getPlacementUser();

        if (currentUser) {
          await currentState
            .requestHotspotPlacement(position, currentUser)
            .then(() => {
              showContextualPopoverRef.current?.(e.clientX - rect.left, e.clientY - rect.top);
            })
            .catch((error) => {
              console.error("Error placing hotspot:", error);
              toast({
                variant: "destructive",
                title: "Error placing hotspot",
                description: "Failed to create hotspot at this location.",
              });
            });
        }
        return;
      }
    } catch (error) {
      console.error("Error handling drop:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to handle drop",
      });
    }
  };

  const applyDefaultViewForHotspot = useCallback(
    async (hotspotId: string) => {
      const hotspot = useEditorStore.getState().hotspots.find((h) => h.id === hotspotId);
      if (!hotspot) return;

      const currentContent = (hotspot.content as HotspotContentRecord) || {};
      const updatedContent = { ...currentContent, linkTransitionType: "default" };
      delete (updatedContent as { linkedPanoramaView?: unknown }).linkedPanoramaView;

      await updateHotspotFromViewer(hotspotId, { content: updatedContent });
    },
    [updateHotspotFromViewer],
  );

  const applySlideToViewForHotspot = useCallback(
    async (hotspotId: string) => {
      const hotspot = useEditorStore.getState().hotspots.find((h) => h.id === hotspotId);
      if (!hotspot) return;

      const currentContent = (hotspot.content as HotspotContentRecord) || {};
      const updatedContent = { ...currentContent, linkTransitionType: "slide-to" };
      delete (updatedContent as { linkedPanoramaView?: unknown }).linkedPanoramaView;

      await updateHotspotFromViewer(hotspotId, { content: updatedContent });

      const linkedPanoramaId = updatedContent.linkedPanoramaId as string | undefined;
      if (!linkedPanoramaId) return;
      const targetPanorama = panoramas.find((p) => p.id === linkedPanoramaId);
      if (!targetPanorama) return;
      const sourcePanoramaId = hotspot.panorama_id as string;
      const sourcePanorama = panoramas.find((p) => p.id === sourcePanoramaId) || null;

      try {
        const refreshResult = await StorageService.refreshPanoramaUrls(targetPanorama.id);
        if (refreshResult.privateUrl) {
          useEditorStore.getState().updatePanorama(targetPanorama.id, {
            private_file_url: refreshResult.privateUrl,
          });
        }
      } catch (error) {
        console.warn("Failed to refresh target panorama URL for modal viewer:", error);
      }

      setBacklinkPlacement({
        sourcePanoramaId,
        sourcePanoramaName: sourcePanorama?.name ?? null,
        targetPanoramaId: targetPanorama.id,
        targetPanoramaName: targetPanorama.name ?? null,
        sourceHotspotId: hotspotId,
      });
    },
    [panoramas, updateHotspotFromViewer],
  );

  const createBacklinkHotspotAt = useCallback(
    async (position: { x: number; y: number; z: number }) => {
      if (!backlinkPlacement) return;

      const targetPanorama = panoramas.find((p) => p.id === backlinkPlacement.targetPanoramaId);
      const sourcePanorama = panoramas.find((p) => p.id === backlinkPlacement.sourcePanoramaId);
      if (!targetPanorama || !sourcePanorama) {
        toast({
          variant: "destructive",
          title: "Unable to place return hotspot",
          description: "The target panorama could not be found.",
        });
        setBacklinkPlacement(null);
        return;
      }

      const state = useEditorStore.getState();
      const existingHotspots = state.hotspots.filter((h) => h.panorama_id === targetPanorama.id);
      const backlinkName = `${sourcePanorama.name} (return)`;
      const newHotspot = {
        project_id: targetPanorama.project_id,
        panorama_id: targetPanorama.id,
        name: backlinkName,
        type: "html",
        position,
        scale: { x: 1, y: 1, z: 1 },
        content: {
          text: "",
          contentType: "icon",
          textColor: "#ffffff",
          backgroundColor: "#2196f3",
          borderColor: "#ffffff",
          borderWidth: 0,
          borderRadius: 0,
          padding: 6,
          fontSize: "24px",
          fontWeight: "700",
          textAlign: "center",
          variant: "default",
          showPointer: false,
          pointerDirection: "bottom",
          pointerSize: 8,
          pointerOverlap: 2,
          tooltipArrowDirection: "top",
          title: backlinkName,
          description: `Click to navigate to ${sourcePanorama.name}`,
          linkedPanoramaId: sourcePanorama.id,
          linkTransitionType: "slide-to",
          positioned: true,
        },
        visible: true,
        order: existingHotspots.length,
      };

      try {
        let hotspotData: Hotspot;
        if (isElectron()) {
          const id = await createLocalHotspot({
            ...newHotspot,
            craftspace_id: projectCraftspaceId,
          });
          hotspotData = { ...newHotspot, id, _id: id, _creationTime: Date.now() } as Hotspot;
        } else {
          hotspotData = (await convex.mutation(api.hotspots.create, newHotspot)) as Hotspot;
        }

        const { addHotspot } = useEditorStore.getState();
        addHotspot(hotspotData);

        toast({
          title: "Return hotspot placed",
          description: `Backlink added to ${targetPanorama.name}.`,
        });
      } catch (error) {
        console.error("Error creating return hotspot:", error);
        toast({
          variant: "destructive",
          title: "Unable to create return hotspot",
          description: "Please try again.",
        });
      } finally {
        setBacklinkPlacement(null);
      }
    },
    [backlinkPlacement, convex, panoramas, projectCraftspaceId, toast],
  );

  const openSpecificViewDialog = useCallback(
    (
      hotspotId: string,
      targetPanoramaId: string,
      transitionType: "specific-view" | "morph" = "specific-view",
    ) => {
      setSpecificViewContext({
        hotspotId,
        targetPanoramaId,
        returnPanoramaId: panorama?.id || null,
        transitionType,
      });

      const targetPanorama = panoramas.find((p) => p.id === targetPanoramaId);
      if (targetPanorama) {
        setSelectedPanorama(targetPanorama);
      }
    },
    [panorama?.id, panoramas, setSelectedPanorama],
  );

  const closeSpecificViewDialog = useCallback(() => {
    if (specificViewContext?.returnPanoramaId) {
      const returnPanorama = panoramas.find((p) => p.id === specificViewContext.returnPanoramaId);
      if (returnPanorama) {
        setSelectedPanorama(returnPanorama);
      }
    }
    setSpecificViewContext(null);
  }, [panoramas, setSelectedPanorama, specificViewContext]);


  const handleSaveSpecificView = useCallback(async () => {
    if (!specificViewContext) return;
    if (!currentCameraView) {
      toast({
        variant: "destructive",
        title: "No view selected",
        description: "Move the camera to the desired view before saving.",
      });
      return;
    }

    const hotspot = useEditorStore
      .getState()
      .hotspots.find((h) => h.id === specificViewContext.hotspotId);
    if (!hotspot) return;

    const currentContent = (hotspot.content as HotspotContentRecord) || {};
    const updatedContent = {
      ...currentContent,
      linkTransitionType: specificViewContext.transitionType,
      linkedPanoramaView: {
        yaw: currentCameraView.yaw,
        pitch: currentCameraView.pitch,
        fov: currentCameraView.fov,
      },
    };

    await updateHotspotFromViewer(specificViewContext.hotspotId, { content: updatedContent });
    closeSpecificViewDialog();
  }, [
    closeSpecificViewDialog,
    currentCameraView,
    specificViewContext,
    toast,
    updateHotspotFromViewer,
  ]);

  const getProjectedHotspotScreenPoint = useCallback(
    (hotspotPosition?: HotspotWorldPosition | null) => {
      const camera = cameraRef.current;
      const container = mountRef.current;
      if (!camera || !container || !hotspotPosition) return null;

      const projected = new THREE.Vector3(
        hotspotPosition.x,
        hotspotPosition.y,
        hotspotPosition.z,
      ).project(camera);

      if (
        !Number.isFinite(projected.x) ||
        !Number.isFinite(projected.y) ||
        projected.z < -1 ||
        projected.z > 1
      ) {
        return null;
      }

      return {
        x: ((projected.x + 1) / 2) * container.clientWidth,
        y: ((1 - projected.y) / 2) * container.clientHeight,
      };
    },
    [],
  );

  const animateCameraTransition = useCallback(
    ({
      targetYaw,
      targetPitch,
      targetFov,
      targetPosition,
      duration,
      easing,
      onComplete,
    }: {
      targetYaw: number;
      targetPitch: number;
      targetFov: number;
      targetPosition?: THREE.Vector3;
      duration: number;
      easing: (progress: number) => number;
      onComplete?: () => void;
    }) => {
      const camera = cameraRef.current;
      if (!camera) {
        onComplete?.();
        return;
      }

      clearPanoramaTransitionAnimation();

      const startView = cameraToDisplayCoords(camera);
      const startFov = camera.fov;
      const startPosition = camera.position.clone();
      const yawDelta = shortestRotation(startView.yaw, targetYaw);
      const pitchDelta = targetPitch - startView.pitch;
      const startedAt = performance.now();

      const step = (now: number) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const easedProgress = easing(progress);
        const yaw = normalizeAngle(startView.yaw + yawDelta * easedProgress);
        const pitch = THREE.MathUtils.clamp(
          startView.pitch + pitchDelta * easedProgress,
          -89,
          89,
        );

        applyDisplayCoordsToCamera(camera, yaw, pitch);
        camera.fov = THREE.MathUtils.lerp(startFov, targetFov, easedProgress);
        if (targetPosition) {
          camera.position.lerpVectors(startPosition, targetPosition, easedProgress);
        }
        camera.updateProjectionMatrix();

        if (progress < 1) {
          panoramaTransitionAnimationFrameRef.current = window.requestAnimationFrame(step);
          return;
        }

        panoramaTransitionAnimationFrameRef.current = null;
        onComplete?.();
      };

      panoramaTransitionAnimationFrameRef.current = window.requestAnimationFrame(step);
    },
    [clearPanoramaTransitionAnimation],
  );

  const commitPanoramaNavigation = useCallback(
    (targetPanorama: Panorama, options?: { view?: PanoramaView }) => {
      logCrossfadeDebug("commitPanoramaNavigation", {
        targetPanoramaId: targetPanorama.id,
        hasView: Boolean(options?.view),
        viewYaw: options?.view?.yaw,
        viewPitch: options?.view?.pitch,
        viewFov: options?.view?.fov,
      });
      if (options?.view) {
        const camera = cameraRef.current;
        navigationLandingViewRef.current = {
          panoramaId: targetPanorama.id,
          view: options.view,
        };
        if (camera && !pendingCrossfadeArrivalRef.current) {
          // Apply landing direction immediately to avoid a transient wrong orientation
          // while the next panorama texture is being swapped in.
          // Skip during crossfade — the crossfade sphere covers the scene with the old
          // panorama, so rotating now would show the old panorama from the wrong angle.
          applyDisplayCoordsToCamera(camera, options.view.yaw, options.view.pitch);
        }
        setPendingPanoramaView({
          panoramaId: targetPanorama.id,
          view: options.view,
        });
      } else {
        setPendingPanoramaView(null);
        navigationLandingViewRef.current = null;
      }

      if (onPanoramaChange) {
        onPanoramaChange(targetPanorama);
      } else {
        setSelectedPanorama(targetPanorama);
      }
    },
    [onPanoramaChange, setPendingPanoramaView, setSelectedPanorama],
  );

  const resolveNavigationView = useCallback(
    (targetPanorama: Panorama, options?: PanoramaNavigationOptions): PanoramaView | undefined => {
      const isSlideTo = options?.transitionType === "slide-to";
      const effectiveSourcePanoramaId = panorama?.id ?? options?.sourcePanoramaId ?? null;
      if (options?.view) {
        if (isSlideTo) {
          slideToResolutionRef.current = {
            targetPanoramaId: targetPanorama.id,
            resolvedFrom: "explicit-view",
            view: options.view,
          };
        }
        return options.view;
      }
      const transitionType = options?.transitionType || "default";
      if (
        transitionType !== "crossfade" &&
        transitionType !== "fade" &&
        transitionType !== "fade-same-view" &&
        transitionType !== "slide-to" &&
        transitionType !== "default"
      ) {
        return undefined;
      }

      const targetData = targetPanorama.data as PanoramaDataRecord | undefined;
      if (transitionType !== "slide-to") {
        if (
          targetData?.initialView &&
          typeof targetData.initialView === "object" &&
          "yaw" in targetData.initialView &&
          "pitch" in targetData.initialView &&
          "fov" in targetData.initialView &&
          typeof targetData.initialView.yaw === "number" &&
          typeof targetData.initialView.pitch === "number" &&
          typeof targetData.initialView.fov === "number"
        ) {
          if (isSlideTo) {
            slideToResolutionRef.current = {
              targetPanoramaId: targetPanorama.id,
              resolvedFrom: "initial-view",
              view: {
                yaw: targetData.initialView.yaw,
                pitch: targetData.initialView.pitch,
                fov: targetData.initialView.fov,
              },
            };
          }
          return {
            yaw: targetData.initialView.yaw,
            pitch: targetData.initialView.pitch,
            fov: targetData.initialView.fov,
          };
        }
      }
      const fallbackFov =
        (targetData?.initialView &&
        typeof targetData.initialView === "object" &&
        "fov" in targetData.initialView &&
        typeof targetData.initialView.fov === "number"
          ? targetData.initialView.fov
          : undefined) ??
        cameraRef.current?.fov ??
        currentCameraView?.fov ??
        75;

      const allHotspots = useEditorStore.getState().hotspots;
      const targetHotspots = allHotspots
        .filter((hotspot) => hotspot.panorama_id === targetPanorama.id)
        .map((hotspot) => ({
          hotspot,
          position: parseHotspotWorldPosition(hotspot.position),
        }))
        .filter(
          (item): item is { hotspot: Hotspot; position: HotspotWorldPosition } =>
            Boolean(item.position),
        );
      const backlinkHotspot =
        effectiveSourcePanoramaId
          ? targetHotspots.find(({ hotspot }) =>
              hotspotLinksToPanorama(hotspot, effectiveSourcePanoramaId),
            )
          : null;
      const linkedTargetHotspot = targetHotspots.find(({ hotspot }) =>
        hotspotHasLinkedPanorama(hotspot),
      );
      const targetEntryHotspot = backlinkHotspot || linkedTargetHotspot || targetHotspots[0];

      if (targetEntryHotspot) {
        const entryView = positionToYawPitch(targetEntryHotspot.position);
        const resolvedView = {
          yaw: normalizeAngle(entryView.yaw + 180),
          pitch: entryView.pitch,
          fov: fallbackFov,
        };
        if (isSlideTo) {
          const resolvedFrom = backlinkHotspot
            ? "backlink-hotspot"
            : linkedTargetHotspot
              ? "linked-hotspot"
              : "any-hotspot";
          slideToResolutionRef.current = {
            targetPanoramaId: targetPanorama.id,
            resolvedFrom,
            view: resolvedView,
          };
        }
        logCrossfadeDebug("resolveNavigationView:fromTargetHotspot", {
          targetPanoramaId: targetPanorama.id,
          sourcePanoramaId: effectiveSourcePanoramaId,
          entryYaw: entryView.yaw,
          entryPitch: entryView.pitch,
          resolvedYaw: resolvedView.yaw,
          resolvedPitch: resolvedView.pitch,
          resolvedFov: resolvedView.fov,
        });
        return resolvedView;
      }

      const sourceHotspotPosition = parseHotspotWorldPosition(options?.hotspotPosition);
      if (sourceHotspotPosition) {
        const sourceLinkView = positionToYawPitch(sourceHotspotPosition);
        const resolvedView = {
          yaw: sourceLinkView.yaw,
          pitch: sourceLinkView.pitch,
          fov: fallbackFov,
        };
        if (isSlideTo) {
          slideToResolutionRef.current = {
            targetPanoramaId: targetPanorama.id,
            resolvedFrom: "source-hotspot",
            view: resolvedView,
          };
        }
        logCrossfadeDebug("resolveNavigationView:fromSourceHotspot", {
          targetPanoramaId: targetPanorama.id,
          sourcePanoramaId: effectiveSourcePanoramaId,
          resolvedYaw: resolvedView.yaw,
          resolvedPitch: resolvedView.pitch,
          resolvedFov: resolvedView.fov,
        });
        return resolvedView;
      }

      logCrossfadeDebug("resolveNavigationView:none", {
        targetPanoramaId: targetPanorama.id,
      });
      if (isSlideTo) {
        slideToResolutionRef.current = {
          targetPanoramaId: targetPanorama.id,
          resolvedFrom: "none",
          view: null,
        };
      }
      return undefined;
    },
    [currentCameraView?.fov, panorama?.id],
  );

  const resolveAutomaticSlideToLandingView = (params: {
    currentView: { yaw: number; pitch: number };
    sourceHotspotPosition: { x: number; y: number; z: number };
    targetBacklinkPosition: { x: number; y: number; z: number };
  }) => {
    const sourceExitView = positionToYawPitch(params.sourceHotspotPosition);
    const targetEntryView = positionToYawPitch(params.targetBacklinkPosition);
    const targetBaseYaw = normalizeAngle(targetEntryView.yaw + 180);
    const relativeYawFromExit = shortestRotation(sourceExitView.yaw, params.currentView.yaw);
    const resolvedYaw = normalizeAngle(targetBaseYaw + relativeYawFromExit);
    const resolvedPitch = Math.max(-89, Math.min(89, params.currentView.pitch));
    warnSlideToDev("viewer.resolveAutomaticSlideToLandingView", {
      mode: isPreviewModeRef.current ? "preview" : "edit",
      currentView: params.currentView,
      sourceHotspotPosition: params.sourceHotspotPosition,
      targetBacklinkPosition: params.targetBacklinkPosition,
      sourceExitYaw: sourceExitView.yaw,
      targetEntryYaw: targetEntryView.yaw,
      targetBaseYaw,
      relativeYawFromExit,
      resolvedYaw,
      resolvedPitch,
    });
    return {
      yaw: resolvedYaw,
      pitch: resolvedPitch,
      debug: {
        sourceExitYaw: sourceExitView.yaw,
        targetEntryYaw: targetEntryView.yaw,
        targetBaseYaw,
        relativeYawFromExit,
      },
    };
  };

  const resolveSlideToDollyDirection = useCallback(
    ({
      targetPanoramaId,
      sourcePanoramaId,
      viewMode,
      manualView,
    }: {
      targetPanoramaId: string;
      sourcePanoramaId?: string | null;
      viewMode: "automatic" | "manual";
      manualView?: PanoramaView;
    }): { direction: THREE.Vector3; anchorPosition: THREE.Vector3; anchorHotspot?: Hotspot } | null => {
      if (viewMode === "manual") {
        if (!manualView) return null;
        const direction = yawPitchToVector(manualView.yaw, manualView.pitch);
        return {
          direction,
          anchorPosition: direction.clone().multiplyScalar(400),
        };
      }

      const targetHotspots = hotspots.filter((hotspot) => {
        const panoramaId =
          (hotspot as { panorama_id?: string; panoramaId?: string }).panorama_id ||
          (hotspot as { panorama_id?: string; panoramaId?: string }).panoramaId;
        return panoramaId === targetPanoramaId;
      });
      if (targetHotspots.length === 0) return null;
      const backlinkHotspot = sourcePanoramaId
        ? targetHotspots.find((hotspot) => hotspotLinksToPanorama(hotspot, sourcePanoramaId))
        : null;
      const linkedTargetHotspot = targetHotspots.find((hotspot) => hotspotHasLinkedPanorama(hotspot));
      const anchorHotspot = backlinkHotspot ?? linkedTargetHotspot ?? targetHotspots[0];
      const anchorPosition = parseHotspotWorldPosition(anchorHotspot?.position);
      if (!anchorPosition) return null;
      const entryView = positionToYawPitch(anchorPosition);
      return {
        direction: yawPitchToVector(entryView.yaw, entryView.pitch),
        anchorPosition: new THREE.Vector3(anchorPosition.x, anchorPosition.y, anchorPosition.z),
        anchorHotspot,
      };
    },
    [hotspots],
  );

  const getHotspotTargetView = useCallback(
    (
      currentView: { yaw: number; pitch: number },
      hotspotPosition?: HotspotWorldPosition,
      centerThreshold: number = 0.18,
      strengthScale: number = 1,
    ) => {
      const hotspotView = hotspotPosition
        ? positionToYawPitch(hotspotPosition)
        : { yaw: currentView.yaw, pitch: currentView.pitch };
      const hotspotPoint = getProjectedHotspotScreenPoint(hotspotPosition);
      const container = mountRef.current;
      const halfWidth = container ? Math.max(container.clientWidth / 2, 1) : 1;
      const halfHeight = container ? Math.max(container.clientHeight / 2, 1) : 1;
      const horizontalOffset = hotspotPoint ? (hotspotPoint.x - halfWidth) / halfWidth : 0;
      const verticalOffset = hotspotPoint ? (hotspotPoint.y - halfHeight) / halfHeight : 0;
      const centerDistance = Math.hypot(horizontalOffset, verticalOffset);
      const baseStrength =
        hotspotPoint && centerDistance > centerThreshold
          ? THREE.MathUtils.clamp((centerDistance - centerThreshold) / (1 - centerThreshold), 0, 1)
          : 0;
      const dragStrength = THREE.MathUtils.clamp(baseStrength * strengthScale, 0, 1);

      return {
        dragStrength,
        targetYaw:
          dragStrength > 0
            ? normalizeAngle(
                currentView.yaw + shortestRotation(currentView.yaw, hotspotView.yaw) * dragStrength,
              )
            : currentView.yaw,
        targetPitch:
          dragStrength > 0
            ? THREE.MathUtils.clamp(
                currentView.pitch + (hotspotView.pitch - currentView.pitch) * dragStrength,
                -89,
                89,
              )
            : currentView.pitch,
      };
    },
    [getProjectedHotspotScreenPoint],
  );

  const startMorph1Transition = useCallback(
    (targetPanorama: Panorama, options?: PanoramaNavigationOptions) => {
      const resolvedView = resolveNavigationView(targetPanorama, options);
      const camera = cameraRef.current;
      if (!camera) {
        commitPanoramaNavigation(targetPanorama, { view: resolvedView });
        return;
      }

      clearMorphTransitionTimeouts();
      clearPanoramaTransitionAnimation();
      clearPanoramaTransitionVisuals();
      pendingCameraDrivenArrivalRef.current = null;
      pendingCrossfadeArrivalRef.current = null;
      isPanoramaTransitioningRef.current = true;
      setIsMorphTransitionActive(true);

      const currentView = cameraToDisplayCoords(camera);
      const targetView = getHotspotTargetView(currentView, options?.hotspotPosition, 0.18, 1);
      const zoomedFov = THREE.MathUtils.clamp(camera.fov * 0.58, 32, 52);

      animateCameraTransition({
        targetYaw: targetView.targetYaw,
        targetPitch: targetView.targetPitch,
        targetFov: zoomedFov,
        duration: 340,
        easing: easeInOutCubic,
        onComplete: () => {
          pendingCameraDrivenArrivalRef.current = {
            panoramaId: targetPanorama.id,
            incomingFov: Math.max(24, zoomedFov * 0.82),
          };
          commitPanoramaNavigation(targetPanorama, { view: resolvedView });

          morphResetTimeoutRef.current = window.setTimeout(() => {
            isPanoramaTransitioningRef.current = false;
            setIsMorphTransitionActive(false);
          }, 1400);
        },
      });
    },
    [
      animateCameraTransition,
      clearMorphTransitionTimeouts,
      clearPanoramaTransitionAnimation,
      clearPanoramaTransitionVisuals,
      commitPanoramaNavigation,
      getHotspotTargetView,
      resolveNavigationView,
    ],
  );

  const startMorph2Transition = useCallback(
    (targetPanorama: Panorama, options?: PanoramaNavigationOptions) => {
      const resolvedView = resolveNavigationView(targetPanorama, options);
      const camera = cameraRef.current;
      if (!camera) {
        commitPanoramaNavigation(targetPanorama, { view: resolvedView });
        return;
      }

      clearMorphTransitionTimeouts();
      clearPanoramaTransitionAnimation();
      clearPanoramaTransitionVisuals();
      pendingCameraDrivenArrivalRef.current = null;
      pendingCrossfadeArrivalRef.current = null;
      isPanoramaTransitioningRef.current = true;
      setIsMorphTransitionActive(true);

      const currentView = cameraToDisplayCoords(camera);
      const targetView = getHotspotTargetView(currentView, options?.hotspotPosition, 0.12, 0.8);
      const zoomedFov = THREE.MathUtils.clamp(camera.fov * 0.74, 44, 66);

      animateCameraTransition({
        targetYaw: targetView.targetYaw,
        targetPitch: targetView.targetPitch,
        targetFov: zoomedFov,
        duration: 520,
        easing: easeInOutCubic,
        onComplete: () => {
          pendingCameraDrivenArrivalRef.current = {
            panoramaId: targetPanorama.id,
            incomingFov: Math.max(40, zoomedFov * 0.92),
          };
          commitPanoramaNavigation(targetPanorama, { view: resolvedView });

          morphResetTimeoutRef.current = window.setTimeout(() => {
            isPanoramaTransitioningRef.current = false;
            setIsMorphTransitionActive(false);
          }, 1500);
        },
      });
    },
    [
      animateCameraTransition,
      clearMorphTransitionTimeouts,
      clearPanoramaTransitionAnimation,
      clearPanoramaTransitionVisuals,
      commitPanoramaNavigation,
      getHotspotTargetView,
      resolveNavigationView,
    ],
  );

  const startMorph3Transition = useCallback(
    (targetPanorama: Panorama, options?: PanoramaNavigationOptions) => {
      const resolvedView = resolveNavigationView(targetPanorama, options);
      const camera = cameraRef.current;
      if (!camera) {
        commitPanoramaNavigation(targetPanorama, { view: resolvedView });
        return;
      }

      clearMorphTransitionTimeouts();
      clearPanoramaTransitionAnimation();
      clearPanoramaTransitionVisuals();
      pendingCameraDrivenArrivalRef.current = null;
      pendingCrossfadeArrivalRef.current = null;
      isPanoramaTransitioningRef.current = true;
      setIsMorphTransitionActive(true);

      const renderer = rendererRef.current;
      const transitionElement = renderer?.domElement;
      const currentView = cameraToDisplayCoords(camera);
      const glideView = getHotspotTargetView(currentView, options?.hotspotPosition, 0.1, 0.62);
      const glideFov = THREE.MathUtils.clamp(camera.fov * 0.86, 54, 72);
      const burstFov = THREE.MathUtils.clamp(camera.fov * 0.66, 38, 58);

      animateCameraTransition({
        targetYaw: glideView.targetYaw,
        targetPitch: glideView.targetPitch,
        targetFov: glideFov,
        duration: 420,
        easing: easeInOutCubic,
        onComplete: () => {
          if (transitionElement) {
            transitionElement.style.transition =
              "transform 180ms cubic-bezier(0.22, 1, 0.36, 1), filter 180ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms cubic-bezier(0.22, 1, 0.36, 1)";
            transitionElement.style.willChange = "transform, filter, opacity";
            requestAnimationFrame(() => {
              transitionElement.style.transform = "scale(1.045)";
              transitionElement.style.filter = "blur(8px) brightness(1.03)";
              transitionElement.style.opacity = "0.94";
            });
          }

          morphSwitchTimeoutRef.current = window.setTimeout(() => {
            pendingCameraDrivenArrivalRef.current = {
              panoramaId: targetPanorama.id,
              incomingFov: Math.max(34, burstFov * 0.88),
            };
            commitPanoramaNavigation(targetPanorama, { view: resolvedView });
          }, 160);

          morphResetTimeoutRef.current = window.setTimeout(() => {
            clearPanoramaTransitionVisuals();
            isPanoramaTransitioningRef.current = false;
            setIsMorphTransitionActive(false);
          }, 1100);
        },
      });
    },
    [
      animateCameraTransition,
      clearMorphTransitionTimeouts,
      clearPanoramaTransitionAnimation,
      clearPanoramaTransitionVisuals,
      commitPanoramaNavigation,
      getHotspotTargetView,
      resolveNavigationView,
    ],
  );

  const startCrossfadeTransition = useCallback(
    (targetPanorama: Panorama, options?: PanoramaNavigationOptions) => {
      const resolvedView = resolveNavigationView(targetPanorama, options);
      const requestedTransitionType = options?.transitionType || "default";
      const transitionType = requestedTransitionType;
      if (transitionType !== "slide-to") {
        slideToDebugRef.current = null;
      }
      const forceLandingView =
        Boolean(options?.forceLandingView) ||
        Boolean(options?.view) ||
        transitionType === "default" ||
        transitionType === "fade" ||
        transitionType === "fade-same-view";
      const camera = cameraRef.current;
      if (!camera) {
        resetPanoramaTransitionState();
        primePanoramaCrossfade();
        commitPanoramaNavigation(targetPanorama, { view: resolvedView });
        return;
      }

      const isFadeTransition =
        transitionType === "fade" || transitionType === "fade-same-view";

      const currentView = cameraToDisplayCoords(camera);
      const startingFov = camera.fov;
      const incomingFov = isFadeTransition
        ? startingFov
        : THREE.MathUtils.clamp(startingFov + 25, 18, 150);
      const debugId = ++crossfadeDebugCounterRef.current;
      crossfadeDebugContextRef.current = {
        id: debugId,
        startedAt: performance.now(),
        sourcePanoramaId: panorama?.id || null,
        targetPanoramaId: targetPanorama.id,
        cameraLockLogged: false,
      };
      logCrossfadeDebug("startCrossfadeTransition", {
        sourcePanoramaId: panorama?.id || null,
        targetPanoramaId: targetPanorama.id,
        resolvedViewYaw: resolvedView?.yaw,
        resolvedViewPitch: resolvedView?.pitch,
        resolvedViewFov: resolvedView?.fov,
        currentYaw: currentView.yaw,
        currentPitch: currentView.pitch,
        incomingFov,
        restoreFov: startingFov,
      });
      if (transitionType === "slide-to") {
        warnSlideToDev("viewer.startCrossfadeTransition", {
          mode: isPreviewModeRef.current ? "preview" : "edit",
          sourcePanoramaId: panorama?.id || null,
          targetPanoramaId: targetPanorama.id,
          hotspotId: options?.hotspotId,
          hotspotPosition: options?.hotspotPosition,
          transitionViewMode: options?.transitionViewMode,
          transitionDirectionView: options?.transitionDirectionView,
          currentView,
          resolvedView,
        });
      }

      // Precomputed offset-invariant landing for automatic slide-to (set below if applicable)
      let slideToPrecomputedLanding: {
        yaw: number;
        pitch: number;
        debug: {
          sourceExitYaw: number;
          targetEntryYaw: number;
          targetBaseYaw: number;
          relativeYawFromExit: number;
        };
      } | null = null;
      const isAutomaticSlideTo =
        transitionType === "slide-to" &&
        !(
          options?.transitionViewMode === "manual" ||
          (!options?.transitionViewMode && options?.transitionDirectionView)
        );

      // Compute hotspot direction for the dolly offset during crossfade
      if (!isFadeTransition) {
        if (transitionType === "slide-to") {
          const viewMode =
            options?.transitionViewMode === "manual" ||
            (!options?.transitionViewMode && options?.transitionDirectionView)
              ? "manual"
              : "automatic";
          const sourcePanoramaId = panorama?.id ?? options?.sourcePanoramaId ?? null;
          const sourcePanoramaName = panorama?.name ?? null;
          const targetPanoramaName = targetPanorama.name ?? null;
          const sourceHotspot =
            options?.hotspotId && hotspots.length
              ? hotspots.find((hotspot) => hotspot.id === options.hotspotId) ?? null
              : null;
          let slideToDirection:
            | { direction: THREE.Vector3; anchorPosition: THREE.Vector3; anchorHotspot?: Hotspot }
            | null = null;
          if (viewMode === "manual") {
            slideToDirection = resolveSlideToDollyDirection({
              targetPanoramaId: targetPanorama.id,
              sourcePanoramaId,
              viewMode,
              manualView: options?.transitionDirectionView,
            });
            if (!slideToDirection) {
              slideToDebugRef.current = {
                status: "invalid",
                viewMode,
                sourcePanoramaId,
                targetPanoramaId: targetPanorama.id,
                sourcePanoramaName,
                targetPanoramaName,
                sourceHotspotId: sourceHotspot?.id ?? null,
                sourceHotspotName: sourceHotspot?.name ?? null,
                reason: "Manual view not set",
              };
            }
          } else {
            // Automatic slide-to requires a backlink in the target panorama,
            // but uses the source hotspot position to drive the dolly direction.
            const backlinkDirectionCheck = resolveSlideToDollyDirection({
              targetPanoramaId: targetPanorama.id,
              sourcePanoramaId,
              viewMode,
              manualView: options?.transitionDirectionView,
            });
            if (!backlinkDirectionCheck) {
              const fallbackDirection = yawPitchToVector(currentView.yaw, currentView.pitch).normalize();
              slideToDirection = {
                direction: fallbackDirection,
                anchorPosition: fallbackDirection.clone().multiplyScalar(-400),
              };
            } else {
            const sourceHotspotPosition =
              parseHotspotWorldPosition(options?.hotspotPosition) ??
              parseHotspotWorldPosition(
                options?.hotspotId
                  ? hotspots.find((hotspot) => hotspot.id === options.hotspotId)?.position
                  : null,
              );
              if (sourceHotspotPosition) {
              const hView = positionToYawPitch(sourceHotspotPosition);
              const sourceDirection = new THREE.Vector3(
                sourceHotspotPosition.x,
                sourceHotspotPosition.y,
                sourceHotspotPosition.z,
              );
              const normalizedSourceDirection =
                sourceDirection.lengthSq() > 0
                  ? sourceDirection.normalize()
                  : yawPitchToVector(hView.yaw, hView.pitch);
              slideToDirection = {
                direction: normalizedSourceDirection,
                anchorPosition: backlinkDirectionCheck.anchorPosition,
                anchorHotspot: backlinkDirectionCheck.anchorHotspot,
              };
              // Precompute offset-invariant landing view
              const backlinkPosition = parseHotspotWorldPosition(
                backlinkDirectionCheck.anchorHotspot?.position,
              );
              if (backlinkPosition) {
                slideToPrecomputedLanding = resolveAutomaticSlideToLandingView({
                  currentView,
                  sourceHotspotPosition,
                  targetBacklinkPosition: backlinkPosition,
                });
              }
              } else {
              const cameraDirection = camera
                ? yawPitchToVector(currentView.yaw, currentView.pitch)
                : backlinkDirectionCheck.direction.clone();
              slideToDirection = {
                direction: cameraDirection.normalize(),
                anchorPosition: backlinkDirectionCheck.anchorPosition,
                anchorHotspot: backlinkDirectionCheck.anchorHotspot,
              };
              }
            }
          }

          if (!slideToDirection) {
            const fallbackDirection = yawPitchToVector(currentView.yaw, currentView.pitch).normalize();
            slideToDirection = {
              direction: fallbackDirection,
              anchorPosition: fallbackDirection.clone().multiplyScalar(-400),
            };
          }
          const slideToResolution = slideToResolutionRef.current;
          const dollyAnchorPosition = slideToDirection.direction.clone().multiplyScalar(-400);
          slideToDebugRef.current = {
            status: "active",
            viewMode,
            sourcePanoramaId,
            targetPanoramaId: targetPanorama.id,
            sourcePanoramaName,
            targetPanoramaName,
            sourceHotspotId: sourceHotspot?.id ?? null,
            sourceHotspotName: sourceHotspot?.name ?? null,
            anchorHotspotId: slideToDirection.anchorHotspot?.id ?? null,
            anchorHotspotName: slideToDirection.anchorHotspot?.name ?? null,
            direction: toVec3(slideToDirection.direction),
            anchorPosition: toVec3(dollyAnchorPosition),
            dollyTargetScale: SLIDE_TO_DOLLY_TARGET_SCALE,
            resolvedFrom:
              slideToResolution?.targetPanoramaId === targetPanorama.id
                ? slideToResolution.resolvedFrom
                : null,
            landingView:
              slideToResolution?.targetPanoramaId === targetPanorama.id
                ? slideToResolution.view
                : null,
            sourceExitYaw: slideToPrecomputedLanding?.debug.sourceExitYaw ?? null,
            targetEntryYaw: slideToPrecomputedLanding?.debug.targetEntryYaw ?? null,
            targetBaseYaw: slideToPrecomputedLanding?.debug.targetBaseYaw ?? null,
            relativeYawFromExit: slideToPrecomputedLanding?.debug.relativeYawFromExit ?? null,
            resolvedYaw: slideToPrecomputedLanding?.yaw ?? null,
            resolvedPitch: slideToPrecomputedLanding?.pitch ?? null,
          };
          crossfadeFovZoomRef.current = {
            startFov: 0, zoomedFov: 0, endFov: 0, // placeholder, set properly in applyTexture
            hotspotDirection: slideToDirection.direction,
            // Dolly anchor in the SOURCE hotspot direction (what the user clicked).
            // Use the negative to move the camera toward the hotspot in world space.
            anchorPosition: dollyAnchorPosition,
            recenterAtEnd: false,
          };
        } else {
          const sourceHotspotPosition = parseHotspotWorldPosition(options?.hotspotPosition);
          if (sourceHotspotPosition) {
            const hView = positionToYawPitch(sourceHotspotPosition);
            crossfadeFovZoomRef.current = {
              startFov: 0, zoomedFov: 0, endFov: 0, // placeholder, set properly in applyTexture
              hotspotDirection: yawPitchToVector(hView.yaw, hView.pitch),
              recenterAtEnd: false,
            };
          }
        }
      }

      // Reset state, then prime crossfade. The camera position is reset
      // inside resetPanoramaTransitionState, but we need to prime AFTER that.
      resetPanoramaTransitionState();
      isPanoramaTransitioningRef.current = true;
      primePanoramaCrossfade();
      pendingCrossfadeArrivalRef.current = {
        panoramaId: targetPanorama.id,
        incomingFov,
        restoreFov: startingFov,
        fadeDurationMs: options?.fadeDurationMs,
        fadeColor: options?.fadeColor,
        isFade: isFadeTransition,
        forceLandingView,
        // For automatic slide-to, always carry an explicit landing target.
        // If precompute is unavailable, fall back to the resolved navigation landing.
        isAutomaticSlideTo,
        slideToResolvedYaw: slideToPrecomputedLanding?.yaw ?? resolvedView?.yaw,
        slideToResolvedPitch: slideToPrecomputedLanding?.pitch ?? resolvedView?.pitch,
      };
      logCrossfadeDebug("crossfade:setPendingArrival", {
        panoramaId: targetPanorama.id,
        incomingFov,
        restoreFov: startingFov,
      });
      commitPanoramaNavigation(targetPanorama, { view: resolvedView });
    },
    [
      commitPanoramaNavigation,
      hotspots,
      panorama?.id,
      panorama?.name,
      primePanoramaCrossfade,
      resetPanoramaTransitionState,
      resolveNavigationView,
      resolveSlideToDollyDirection,
      triggerInvalidSlideToOutline,
    ],
  );

  const navigateToPanorama = useCallback(
    (targetPanorama: Panorama, options?: PanoramaNavigationOptions) => {
      const requestedTransitionType = options?.transitionType || "default";
      const transitionType = requestedTransitionType;
      const effectiveOptions =
        transitionType === requestedTransitionType
          ? options
          : {
              ...options,
              transitionType,
            };

      if (transitionType === "morph") {
        startMorph2Transition(targetPanorama, effectiveOptions);
        return;
      }
      if (
        transitionType === "crossfade" ||
        transitionType === "fade" ||
        transitionType === "fade-same-view" ||
        transitionType === "slide-to"
      ) {
        startCrossfadeTransition(targetPanorama, effectiveOptions);
        return;
      }

      resetPanoramaTransitionState();
      commitPanoramaNavigation(targetPanorama, {
        view: resolveNavigationView(targetPanorama, effectiveOptions),
      });
    },
    [
      commitPanoramaNavigation,
      resetPanoramaTransitionState,
      resolveNavigationView,
      startCrossfadeTransition,
      startMorph2Transition,
    ],
  );

  useEffect(() => {
    const handlePanoramaNavigationRequest = (event: Event) => {
      const detail = (event as CustomEvent<PanoramaNavigationRequestDetail>).detail;
      if (!detail?.panoramaId) return;
      logCrossfadeDebug("navigationRequest:event", {
        fromPanoramaId: panorama?.id || null,
        targetPanoramaId: detail.panoramaId,
        transitionType: detail.transitionType || "default",
        sourcePanoramaId: detail.sourcePanoramaId || null,
        hasLinkedPanoramaView: Boolean(detail.linkedPanoramaView),
        hotspotPosition: detail.hotspotPosition || null,
        hotspotId: detail.hotspotId || null,
      });

      const targetPanorama = panoramas.find((item) => item.id === detail.panoramaId);
      if (!targetPanorama) return;

      const requestedTransitionType = detail.transitionType || "default";
      const transitionType = requestedTransitionType;
      const linkedPanoramaView = detail.linkedPanoramaView;
      navigateToPanorama(targetPanorama, {
        transitionType,
        view:
          transitionType === "same-view" ||
          transitionType === "fade-same-view" ||
          transitionType === "specific-view" ||
          transitionType === "morph"
            ? linkedPanoramaView
            : undefined,
        hotspotPosition: detail.hotspotPosition,
        hotspotId: detail.hotspotId,
        sourcePanoramaId: detail.sourcePanoramaId || panorama?.id,
        fadeColor: detail.fadeColor,
        fadeDurationMs: detail.fadeDurationMs,
        transitionViewMode: transitionType === "slide-to" ? detail.transitionViewMode : undefined,
        transitionDirectionView:
          transitionType === "slide-to" ? detail.transitionDirectionView : undefined,
      });
    };

    window.addEventListener(
      "360crafter:navigate-panorama",
      handlePanoramaNavigationRequest as EventListener,
    );
    return () => {
      window.removeEventListener(
        "360crafter:navigate-panorama",
        handlePanoramaNavigationRequest as EventListener,
      );
    };
  }, [navigateToPanorama, panorama?.id, panoramas]);

  // Wrapper function for skin component panorama changes
  const handleSkinComponentPanoramaChange = (
    targetPanorama: Panorama,
    component?: { content?: Record<string, unknown> },
    action?: Record<string, unknown>,
  ) => {
    const transitionSource = action || component?.content || {};
    const linkedPanoramaView = transitionSource.linkedPanoramaView as PanoramaView | undefined;
    const normalizedTransitionType = normalizePanoramaTransitionType(
      transitionSource.linkTransitionType,
      Boolean(linkedPanoramaView),
    );
    const transitionViewMode = transitionSource.linkTransitionViewMode as
      | "automatic"
      | "manual"
      | undefined;
    const transitionDirectionView = transitionSource.linkTransitionDirectionView as
      | PanoramaView
      | undefined;
    const fadeColor = transitionSource.linkTransitionFadeColor as string | undefined;
    const fadeDurationSec = transitionSource.linkTransitionFadeDurationSec as number | undefined;
    const fadeDurationMs =
      typeof fadeDurationSec === "number" && Number.isFinite(fadeDurationSec)
        ? Math.max(0, fadeDurationSec) * 1000
        : undefined;

    navigateToPanorama(targetPanorama, {
      transitionType: normalizedTransitionType,
      view:
        normalizedTransitionType === "same-view" ||
        normalizedTransitionType === "fade-same-view"
          ? currentCameraView || undefined
          : normalizedTransitionType === "specific-view" || normalizedTransitionType === "morph"
            ? linkedPanoramaView
            : undefined,
      sourcePanoramaId: panorama?.id,
      fadeColor,
      fadeDurationMs,
      transitionViewMode,
      transitionDirectionView,
    });
  };

  const formatSlideToNumber = (value: number | null | undefined, digits = 2): string =>
    typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : "—";
  const formatSlideToVector = (
    value: { x: number; y: number; z: number } | null | undefined,
  ): string =>
    value
      ? `${value.x.toFixed(2)}, ${value.y.toFixed(2)}, ${value.z.toFixed(2)}`
      : "—";

  return (
    <div
      ref={mountRef}
      className={`h-full bg-gray-900 relative${isDraggingToAddHotspot ? " cursor-crosshair" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onContextMenu={handleContextMenu}
      role="application"
      aria-label="Panorama viewer"
    >
      <div
        className="pointer-events-none absolute inset-0 z-[1] transition-opacity duration-700"
        style={{
          opacity: isMorphTransitionActive ? 1 : 0,
          background:
            "radial-gradient(circle at center, rgba(255,255,255,0.10) 0%, rgba(96,165,250,0.08) 26%, rgba(17,24,39,0.20) 68%, rgba(2,6,23,0.34) 100%)",
        }}
      />
      {showDefaultViewOutline && (
        <div
          className="pointer-events-none absolute inset-0 z-[2] rounded-md border-2 border-emerald-400/70"
          style={{ animation: "default-view-outline-pulse 520ms ease-out" }}
        />
      )}
      {showInvalidSlideToOutline && (
        <div
          className="pointer-events-none absolute inset-0 z-[2] rounded-md border-2 border-red-500/70"
          style={{ animation: "invalid-slide-outline-pulse 1000ms ease-out" }}
        />
      )}
      {navDropMenu && (
        <div
          ref={navMenuRef}
          className="absolute z-[1600] bg-gray-900 border border-gray-700 rounded-lg shadow-lg w-64"
          style={{
            left: `${(navDropMenuPos ?? { x: navDropMenu.x + 8, y: navDropMenu.y + 12 }).x}px`,
            top: `${(navDropMenuPos ?? { x: navDropMenu.x + 8, y: navDropMenu.y + 12 }).y}px`,
          }}
        >
          <div className="px-3 py-1.5 border-b border-gray-700 text-xs text-gray-400">
            Navigation hotspot options
          </div>
          <div className="p-1.5 space-y-1">
            <Button
              type="button"
              variant="outline"
              className="w-full border-gray-700 text-gray-200 hover:bg-gray-800 h-7 text-xs"
              onClick={() => {
                void applySlideToViewForHotspot(navDropMenu.hotspotId);
                setNavDropMenu(null);
              }}
            >
              Slide To
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full border-gray-700 text-gray-200 hover:bg-gray-800 h-7 text-xs"
              onClick={() => {
                void applyDefaultViewForHotspot(navDropMenu.hotspotId);
                setNavDropMenu(null);
              }}
            >
              Open default view
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full border-gray-700 text-gray-200 hover:bg-gray-800 h-7 text-xs"
              onClick={() => {
                openSpecificViewDialog(
                  navDropMenu.hotspotId,
                  navDropMenu.targetPanoramaId,
                  "specific-view",
                );
                setNavDropMenu(null);
              }}
            >
              Open specific view
            </Button>
          </div>
        </div>
      )}

      {specificViewContext && panorama?.id === specificViewContext.targetPanoramaId && (
        <div
          ref={specificViewDialogRef}
          className="absolute top-4 left-1/2 z-40 -translate-x-1/2 rounded-lg border border-blue-700/60 bg-blue-950/90 px-4 py-3 text-sm text-blue-100 shadow-lg"
        >
          <div className="font-medium text-white">Set landing view for this hotspot</div>
          <div className="text-xs text-blue-200 mt-1">
            Adjust the camera, then click Save to store the landing view for this transition.
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeSpecificViewDialog}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSaveSpecificView()}>
              Save view
            </Button>
          </div>
        </div>
      )}

      <BacklinkPlacementModal
        open={Boolean(backlinkPlacement)}
        targetPanorama={
          backlinkPlacement
            ? panoramas.find((p) => p.id === backlinkPlacement.targetPanoramaId) ?? null
            : null
        }
        sourcePanoramaName={backlinkPlacement?.sourcePanoramaName ?? null}
        targetPanoramaName={backlinkPlacement?.targetPanoramaName ?? null}
        craftspaceId={projectCraftspaceId}
        onCancel={() => setBacklinkPlacement(null)}
        onPick={(position) => void createBacklinkHotspotAt(position)}
      />

      {/* Live Camera Statistics Overlay - Only show when debug=true */}
      {panorama && showDebug && (
        <div className="absolute top-4 right-4 bg-black/80 text-white px-4 py-3 rounded-lg font-mono text-sm z-20 pointer-events-none">
          <div className="space-y-1">
            <div>
              Yaw: <span className="text-yellow-300">{cameraStats.yaw.toFixed(1)}°</span>
            </div>
            <div>
              Pitch: <span className="text-green-300">{cameraStats.pitch.toFixed(1)}°</span>
            </div>
            <div>
              Roll: <span className="text-blue-300">{cameraStats.roll.toFixed(1)}°</span>
            </div>
            <div>
              FOV: <span className="text-purple-300">{cameraStats.fov.toFixed(1)}°</span>
            </div>
            <div>
              Camera:{" "}
              <span className="text-gray-300">
                {cameraRef.current
                  ? `${cameraRef.current.position.x.toFixed(2)}, ${cameraRef.current.position.y.toFixed(2)}, ${cameraRef.current.position.z.toFixed(2)}`
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Map Overlay - Top Right */}
      {showMapOverlay && panorama && (
        <div className="absolute top-4 right-4 w-80 h-64 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-30 pointer-events-auto">
          <div className="flex items-center justify-between p-2 border-b border-gray-700">
            <span className="text-white text-sm font-medium">{panorama.name}</span>
            <button
              onClick={() => setShowMapOverlay(false)}
              className="text-gray-400 hover:text-white transition-colors"
              title="Close map"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <title>Close map</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="relative w-full h-[calc(100%-41px)] rounded-b-lg overflow-hidden">
            <div ref={mapOverlayRef} className="w-full h-full rounded-b-lg" />
            {/* FOV indicator overlay - will be moved to Leaflet overlay pane */}
            <button
              ref={radarOverlayRef}
              className="absolute bg-transparent border-0 p-0"
              style={{
                width: "100px",
                height: "100px",
                left: "50%",
                top: "50%",
                marginLeft: "-50px",
                marginTop: "-50px",
                transformOrigin: "center center",
                transition: isDraggingRadar
                  ? "none"
                  : "transform 0.1s ease-out, left 0.2s ease-out, top 0.2s ease-out",
                cursor: isDraggingRadar ? "grabbing" : "grab",
                zIndex: 500,
              }}
              onMouseDown={handleRadarMouseDown}
              title={
                isDraggingRadar
                  ? "Release to save north orientation"
                  : "Drag to calibrate north orientation"
              }
              type="button"
              aria-label="Rotate north orientation"
            >
              {/* FOV arc indicator */}
              <svg width="100" height="100" viewBox="0 0 100 100" className="absolute inset-0">
                <title>Field of view indicator</title>
                {/* FOV arc - dynamically sized based on camera FOV */}
                <path
                  className="fov-arc"
                  fill="rgba(59, 130, 246, 0.3)"
                  stroke="rgba(59, 130, 246, 0.8)"
                  strokeWidth="2"
                  d="M 50 50 L 50 5 A 45 45 0 0 1 50 5 Z"
                />
                {/* Center dot */}
                <circle cx="50" cy="50" r="2" fill="#3B82F6" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Drag-over feedback for panorama and component drags */}
      {isDraggingOver && dragType !== "hotspot-add" && dragType !== "hotspot-relocate" && (
        <div className="absolute inset-0 bg-green-500/20 border-2 border-dashed border-green-400 flex items-start justify-center pt-8 z-50">
          <div className="bg-green-600 text-white px-4 py-2 rounded-lg text-lg font-medium flex items-center gap-2">
            <span className="text-2xl">→</span>
            {dragType === "panorama"
              ? "Drop panorama here to create navigation hotspot"
              : "Drop component here to add to skin"}
          </div>
        </div>
      )}

      {/* Pointer-drag feedback for hotspot-add (drag from Add Hotspot button) */}
      {isDraggingToAddHotspot && (
        <div className="absolute inset-0 border-4 border-blue-500 flex items-start justify-center pt-8 z-10 pointer-events-none cursor-crosshair">
          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg text-lg font-medium flex items-center gap-2">
            <span className="text-2xl">+</span>
            Release to place hotspot here
          </div>
        </div>
      )}

      {/* Hotspot Placement Mode Overlay */}
      {placementMode && !isPreviewMode && (
        <div className="absolute inset-0 border-4 border-blue-500 flex items-start justify-center pt-8 z-50">
          <div className="bg-blue-600 text-white px-4 py-2 rounded-lg text-lg font-medium flex items-center gap-2">
            <span className="text-2xl">+</span>
            Click or drag to place your hotspot
          </div>
        </div>
      )}

      {placementMode && placementRect && !isPreviewMode && (
        <div
          className="absolute pointer-events-none z-[60]"
          style={{
            left: placementRect.x,
            top: placementRect.y,
            width: placementRect.width,
            height: placementRect.height,
            backgroundColor: placementPreviewColor,
            borderRadius: placementType === "svg" ? "9999px" : "6px",
            border:
              placementType === "svg"
                ? "0px solid transparent"
                : "0px solid #ffffff",
            boxShadow: "none",
            opacity: 1,
          }}
        />
      )}

      {/* Right-click context menu */}
      {contextMenu && !isPreviewMode && (
        <>
          <div
            className="absolute inset-0 z-[2000]"
            onClick={() => setContextMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}
          />
          <div
            className="absolute z-[2001] bg-gray-800 border border-gray-600 rounded-md shadow-lg py-1 min-w-[140px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            {!contextMenu.hotspotId && (
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-700 flex items-center gap-2"
                onClick={handleContextMenuAddHotspot}
              >
                <span className="text-base leading-none">+</span>
                Add hotspot
              </button>
            )}
            <div className="border-t border-gray-700 my-1" />
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-700 flex items-center gap-2"
              onClick={handleSetDefaultView}
            >
              <Camera className="w-4 h-4" />
              Set Default View
            </button>
            {contextMenu.hotspotId && (
              <>
                <div className="border-t border-gray-700 my-1" />
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-red-300 hover:bg-gray-700 flex items-center gap-2"
                  onClick={handleContextMenuDeleteHotspot}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete hotspot
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* Contextual popover intentionally removed */}

      {/* No panorama selected message */}
      {!panorama && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-white text-lg">Select a panorama to view</div>
        </div>
      )}

      {/* Panorama loading indicator */}
      {isTextureLoading && panorama && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-gray-600 border-t-blue-400 rounded-full animate-spin" />
            <div className="text-gray-300 text-sm">Loading panorama…</div>
          </div>
        </div>
      )}
      {panoramaLoadFailed && panorama && !isTextureLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/75 z-[60]">
          <div className="flex flex-col items-center gap-3 text-center px-4">
            <div className="text-white text-sm font-medium">Failed to load high-resolution panorama</div>
            <div className="text-gray-300 text-xs">
              The preview was removed to avoid showing a blurry image.
            </div>
            <button
              type="button"
              className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs transition-colors"
              onClick={() => {
                setPanoramaLoadFailed(false);
                setPanoramaLoadAttempt((prev) => prev + 1);
              }}
            >
              Retry loading panorama
            </button>
          </div>
        </div>
      )}

      <div
        ref={selectionOverlayRef}
        className="absolute inset-0 pointer-events-none z-[40] overflow-visible"
        style={{ display: "none" }}
      >
        <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
          <title>Selected hotspot bounds</title>
          <polygon
            ref={selectionOutlineRef}
            fill="none"
            stroke="rgba(96, 165, 250, 0.95)"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {RESIZE_HANDLES.map((handle) => (
          <button
            key={handle}
            ref={setSelectionHandleRef(handle)}
            type="button"
            aria-label={`Resize ${handle}`}
            data-hotspot-resize-handle="true"
            className="absolute w-4 h-4 rounded-full border-2 border-blue-100 bg-blue-500 pointer-events-auto -translate-x-1/2 -translate-y-1/2 transition-all z-50"
            style={{
              display: "none",
              cursor: RESIZE_CURSOR_BY_HANDLE[handle],
              left: "0",
              top: "0",
            }}
            onPointerDown={(e) => handleResizePointerDown(handle, e)}
            onMouseMove={(e) => {
              // Show rotation cursor when Shift is held in 3D mode
              const mode = getHotspotFacingMode(selectedHotspotRef.current);
              if (mode === "3d" && e.shiftKey) {
                e.currentTarget.style.cursor = ROTATE_CURSOR;
                e.currentTarget.style.boxShadow = "0 0 12px rgba(96, 165, 250, 0.8)";
              } else {
                e.currentTarget.style.cursor = RESIZE_CURSOR_BY_HANDLE[handle];
                e.currentTarget.style.boxShadow = "none";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.cursor = RESIZE_CURSOR_BY_HANDLE[handle];
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        ))}
      </div>

      {/* Show skin components while editing skin or in global preview mode. */}
      {(currentRoute === "skin" || isPreviewMode) && skinComponents.length > 0 && (
        <ComponentRenderer
          components={skinComponents}
          selectedComponent={selectedSkinComponent}
          onComponentSelect={setSelectedSkinComponent}
          onUpdateComponents={setSkinComponents}
          onPanoramaChange={handleSkinComponentPanoramaChange}
          panoramas={panoramas}
          isPreviewMode={isPreviewMode}
          allowSelectionInPreview={currentRoute === "skin"}
          currentCMSRowData={currentCMSRowData}
        />
      )}
    </div>
  );
};

export default Viewer360;
